import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, Search, X, AlertCircle, AlertTriangle, Tag, Phone, CreditCard, DollarSign, Store, Truck, ChevronRight, ChevronDown, Trash2, UtensilsCrossed, ArrowUpDown, Shirt, Coffee, Nut, Sandwich, ChefHat, CakeSlice, Scissors, ShoppingBasket, Wine, Cookie, PackageSearch } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../ProductCard';
import CartContent from '../CartContent';
import OffersModal from '../OffersModal';
import PaymentModal from '../PaymentModal';
import OffersManager from '../OffersManager';
import { checkStoreAvailability } from '../checkStoreAvailability';
import { useTranslation } from 'react-i18next';
import CustomizationModal from '../CustomizationModal';
import ComboDetailsModal from '../ComboDetailsModal';
import WeightSelectionModal from '../WeightSelectionModal';
import AuthModal from '../AuthModal';
import { restaurantConfig, getRestaurantConfigSync } from '../../config/restaurantConfig';
import { getTemplate, generateCSSVariables } from '../../config/TemplateStyles';
import ApparelFilters from '../ApparelFilters';
import ApparelFilterDrawer from '../ApparelFilterDrawer';
import SortDrawer, { SORT_OPTIONS } from '../SortDrawer';
import FallbackImg from '../FallbackImg';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductDetailsModal from '../ProductDetailsModal';
import OrderConfirmationModal from '../OrderConfirmationModal';
import StoreClosedModal from '../StoreClosedModal';
import { useOrderFlow } from '../../context/OrderFlowContext';
import { CHECKOUT_FLOW_CONFIG } from '../../config/checkoutConfig';
import { calculateDistance } from '../../utils/storeUtils';
import settingsService from '../../config/settingsService';
import { getTenantId } from '../../config/tenantConfig';
import { apiCache } from '../../utils/apiCache';
import { formatLocalDateTime } from '../../utils/timeSlotGenerator';
import { cartManager } from '../../utils/cartManager';
import { getValidAuthSession, clearAuthSession } from '../../utils/authStorage';
import './index.css';

// Add to Cart action pending a login prompt (see addToCart /
// handleCustomizedProductAdd / handleWeightConfirm's login gates below).
// Kept in sessionStorage, not just React state — this app remounts
// RestaurantMenuApp at several points during the login flow (confirmed via
// its own renderCountRef resetting to 1 right after a successful login),
// which wipes any plain useState. sessionStorage survives that remount,
// the same reasoning behind this app's existing pending_category_id
// mechanism used for cross-navigation state elsewhere.
const PENDING_CART_ACTION_KEY = 'pending_cart_action';
const PENDING_CART_ACTION_MAX_AGE_MS = 5 * 60 * 1000; // ignore anything stale/abandoned

const savePendingCartAction = (type, product) => {
  try {
    sessionStorage.setItem(PENDING_CART_ACTION_KEY, JSON.stringify({ type, product, ts: Date.now() }));
  } catch {
    // sessionStorage unavailable (private mode, quota, etc.) — the in-memory
    // pendingProduct state still covers the common case where no remount
    // happens between the click and login.
  }
};

const readPendingCartAction = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_CART_ACTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - (parsed.ts || 0) > PENDING_CART_ACTION_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const clearPendingCartAction = () => {
  try {
    sessionStorage.removeItem(PENDING_CART_ACTION_KEY);
  } catch {
    // ignore
  }
};

// Empty-search-results copy, keyed by outlet_type — one icon, one title, one
// short description per business type, matching what the API's outlet_type
// field actually sends. Falls back to a generic set for anything unlisted.
const EMPTY_STATE_BY_OUTLET_TYPE = {
  restaurant: { icon: UtensilsCrossed, title: 'Hungry for something else?', description: "We couldn't find any dishes matching your search." },
  apparel: { icon: Shirt, title: 'No products found', description: "We couldn't find any products matching your search." },
  cafe_coffee_shop: { icon: Coffee, title: 'No items found', description: "We couldn't find any coffee or cafe items matching your search." },
  dry_fruit_shop: { icon: Nut, title: 'No dry fruits found', description: "We couldn't find any dry fruits matching your search." },
  fast_food_qsr: { icon: Sandwich, title: 'No items found', description: "We couldn't find any fast food items matching your search." },
  cloud_kitchen: { icon: ChefHat, title: 'No dishes found', description: "We couldn't find any dishes matching your search." },
  bakery: { icon: CakeSlice, title: 'No bakery items found', description: "We couldn't find any bakery items matching your search." },
  salon: { icon: Scissors, title: 'No services found', description: "We couldn't find any services matching your search." },
  grocery_store: { icon: ShoppingBasket, title: 'No products found', description: "We couldn't find any grocery products matching your search." },
  bar_restaurant: { icon: Wine, title: 'No items found', description: "We couldn't find any items matching your search." },
  sweet_shop: { icon: Cookie, title: 'No sweets found', description: "We couldn't find any sweets matching your search." }
};
const DEFAULT_EMPTY_STATE = { icon: PackageSearch, title: 'No items found', description: "We couldn't find anything matching your search." };

const CASHFREE_CONFIG = {
  sandbox: {
    mode: "sandbox"
  },
  production: {
    mode: "production"
  }
};

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;
const API_ENDPOINTS = {
  sendOtp: '/auth/customer-login/send-otp',
  verifyOtp: '/auth/customer-login/verify-otp',
  defaultAccess: '/customer/default-access',
  activeOffers: '/web/offers/active',
  availableOffers: '/web/offers/available',
  validateCoupon: '/web/offers/validate-coupon'
};

// Cookie and Storage Configuration
const CART_STORAGE_KEY = 'resto_cart_data';

const getAuthFromCookies = () => {
  const session = getValidAuthSession();
  console.log('📋 Reading auth cookies:', {
    tokenExists: !!session?.token,
    userId: session?.userId,
    phone: session?.phone
  });
  return session;
};

const getCartKey = (storeId, orderType) => {
  return `${CART_STORAGE_KEY}_${storeId}_${orderType}`;
};

const saveCartToStorage = (storeId, orderType, cartItems) => {
  try {
    const key = getCartKey(storeId, orderType);
    const cartData = {
      items: cartItems,
      timestamp: Date.now(),
      storeId,
      orderType
    };
    localStorage.setItem(key, JSON.stringify(cartData));

    // Notify global cart indicator that cart was updated
    window.dispatchEvent(new Event('cart_updated'));
  } catch (error) {
    console.error('Error saving cart to storage:', error);
  }
};

const loadCartFromStorage = (storeId, orderType) => {
  try {
    const key = getCartKey(storeId, orderType);
    const storedData = localStorage.getItem(key);

    if (storedData) {
      const cartData = JSON.parse(storedData);
      const hoursSinceLastUpdate = (Date.now() - cartData.timestamp) / (1000 * 60 * 60);

      if (hoursSinceLastUpdate < 24 && cartData.storeId === storeId && cartData.orderType === orderType) {
        return cartData.items;
      } else {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('Error loading cart from storage:', error);
  }
  return [];
};

const clearCartFromStorage = (storeId, orderType) => {
  try {
    const key = getCartKey(storeId, orderType);
    localStorage.removeItem(key);

    // Notify global cart indicator that cart was updated
    window.dispatchEvent(new Event('cart_updated'));
  } catch (error) {
    console.error('Error clearing cart from storage:', error);
  }
};


const RestaurantMenuApp = ({
  isOpen,
  onClose,
  selectedStore,
  orderDetails,
  initialCategorySlug,
  HeaderComponent,
  headerProps,
  onLocationChange,
  onAuthUpdate,
  authMethod,
  // ✅ Centralized Address Props
  addresses: propAddresses,
  selectedAddress: propAddress,
  onAddressSelect,
  onSetDefaultAddress,
  onCreateAddress,
  onUpdateAddress,
  onDeleteAddress,
  addressesLoading,
  outletType: propOutletType,
  menuFilterSettings: propMenuFilterSettings,
  templateType: propTemplateType
}) => {
  const navigate = useNavigate();
  const { isPreOrder: contextIsPreOrder, setPreOrder } = useOrderFlow();
  const isPreOrder = contextIsPreOrder || orderDetails?.isPreOrder || false;
  // ✅ DIAGNOSTIC: Track render count
  const renderCountRef = useRef(0);
  renderCountRef.current++;

  // Log every render to verify loop is fixed
  console.log(`🔁 [RestaurantMenuApp] Render count: ${renderCountRef.current}, isOpen: ${isOpen}`);

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [activeOffers, setActiveOffers] = useState([]);
  const [availableOffers, setAvailableOffers] = useState(null);
  const [showActiveOffersModal, setShowActiveOffersModal] = useState(false);
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userToken, setUserToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userPhone, setUserPhone] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [cookingInstruction, setCookingInstruction] = useState('');
  const [userName, setUserName] = useState(null);
  const [vegFilter, setVegFilter] = useState(false);
  const [nonVegFilter, setNonVegFilter] = useState(false);
  const [cartAnalysis, setCartAnalysis] = useState(null);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [analyzingCart, setAnalyzingCart] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showOrderConfirmationModal, setShowOrderConfirmationModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  // AuthModal calls onClose() right after onAuthSuccess() on every successful
  // login (see handleVerifyOTP/handleEmailLogin in AuthModal/index.js), not
  // just on cancel. Set synchronously inside handleAuthSuccess, before that
  // trailing onClose() call runs, so onClose can tell "closed after success"
  // apart from "user cancelled" and only clear the pending action on cancel.
  const authSucceededRef = useRef(false);
  // Holds the single in-flight/resolved /web/offers/active request made on
  // mount (see fetchInitialOffers below), so loadInitialData's combo-tab
  // check can await the SAME request instead of firing its own duplicate.
  const comboOffersPromiseRef = useRef(null);
  // ✅ NEW: Track offer state changes to trigger memo recalculation
  const [offerStateVersion, setOfferStateVersion] = useState(0);
  const [selectedCollectionTime, setSelectedCollectionTime] = useState('');
  // ✅ Mirrors CartContent's local ASAP/Schedule-for-Later choice (reported
  // via onScheduleModeChange below) — used only to decide whether the order
  // payload includes scheduled_at; does not affect any UI.
  const [scheduleMode, setScheduleMode] = useState('asap');
  // ✅ How many loyalty points the customer chose to redeem on this order
  // (reported up from CartContent's Redeem Points input). Used only to add
  // loyalty_points_used to the order payload; the discount itself is always
  // computed by the backend, never here.
  const [redeemPoints, setRedeemPoints] = useState(0);
  // Courier speed the customer picked in the cart's Delivery Fee row ('air' | 'surface', or null
  // = no pick yet, the server chooses its default). It is sent to /web/cart/analyze, which returns
  // the fee for that speed in the same shipping.rate the cart already uses — so no fee logic here.
  const [deliverySpeed, setDeliverySpeed] = useState(null);
  // ✅ The logged-in customer's available loyalty points, read from the same
  // /web/me cache Header already populates (apiCache) — no extra API call.
  // Refreshed on login and whenever 'profile_updated' fires (the same event
  // Header listens to), so it stays in sync after a redemption.
  const [loyaltyPoints, setLoyaltyPoints] = useState(null);

  // ✅ Keep loyaltyPoints in sync with the cached /web/me profile — read on
  // login/mount, and re-read whenever Header's existing 'profile_updated'
  // event fires (already dispatched elsewhere in this file after things
  // like an email update, and after a points redemption below).
  useEffect(() => {
    if (!userId) {
      setLoyaltyPoints(null);
      return;
    }

    const syncLoyaltyFromCache = () => {
      const cachedProfile = apiCache.getUserProfile();
      setLoyaltyPoints(cachedProfile?.loyalty || null);
    };

    syncLoyaltyFromCache();
    window.addEventListener('profile_updated', syncLoyaltyFromCache);
    return () => window.removeEventListener('profile_updated', syncLoyaltyFromCache);
    // Re-sync whenever the cart opens too, in case Header's own /web/me
    // fetch (which populates this same cache) finished after this ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, showCart]);

  // Use passed selectedAddress directly, or fallback to first address if it exists
  const selectedAddress = propAddress || (propAddresses && propAddresses.length > 0 ? propAddresses.find(a => a.is_default) || propAddresses[0] : null);
  const setSelectedAddress = onAddressSelect;

  const [storeIsOpen, setStoreIsOpen] = useState(true);
  const [showStoreClosedModal, setShowStoreClosedModal] = useState(false);
  const [storeClosureInfo, setStoreClosureInfo] = useState(null);
  const storeWasOpenRef = useRef(true); // Track previous open state
  const activeCategoryRef = useRef('all'); // ✅ Track active category to prevent race conditions
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [productToCustomize, setProductToCustomize] = useState(null);
  const [isEditingCartItem, setIsEditingCartItem] = useState(false);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showComboModal, setShowComboModal] = useState(false);
  const [comboToDisplay, setComboToDisplay] = useState(null);
  const [comboOffers, setComboOffers] = useState([]); // ✅ ADD THIS LINE
  const [categoryLoading, setCategoryLoading] = useState(false); // ✅ Category switch loading
  const [showClearCartDialog, setShowClearCartDialog] = useState(false); // ✅ Clear cart confirmation dialog
  const [menuToast, setMenuToast] = useState(null); // { type: 'success'|'error', message: string }
  const [showWeightModal, setShowWeightModal] = useState(false); // ✅ Weight-based item selection
  const [weightProduct, setWeightProduct] = useState(null); // ✅ Product to show weight picker for
  const [showEmailModal, setShowEmailModal] = useState(false); // ✅ Email prompt before order
  const [emailInput, setEmailInput] = useState(''); // ✅ Email input value
  const [emailSaving, setEmailSaving] = useState(false); // ✅ Email saving loading state
  const [emailError, setEmailError] = useState(''); // ✅ Email validation error
  const [showAuthModal, setShowAuthModal] = useState(false); // ✅ Auth modal for add-to-cart
  const locationData = useLocation();
  const [apparelFilters, setApparelFilters] = useState({
    categories: [],
    priceRange: null,
    brands: [],
    colors: [],
    sizes: [],
    gender: [],
    ageGroups: [],
    minRating: null
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  // Presentational sort of the already-filtered product list — no API calls,
  // no new business logic. Only options backed by real, already-used data
  // (price) are offered; there is no "newest"/"popularity" field anywhere in
  // the product data today, so those are intentionally not included.
  const [sortBy, setSortBy] = useState('relevance'); // 'relevance' | 'price_asc' | 'price_desc'
  const [showSortSheet, setShowSortSheet] = useState(false);
  // Desktop "Sort by" control (retail/apparel toolbar) — a custom dropdown
  // instead of a native <select> so the closed state can show the static
  // "Sort by" label rather than the currently selected option's text.
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const sortTriggerRef = useRef(null);
  // The open menu is portaled to document.body (see JSX) so it isn't
  // clipped by this row's overflow: hidden — but that also means it's not
  // a real DOM descendant of sortDropdownRef, so the outside-click check
  // below needs its own ref for the menu, or every click on an option
  // would register as "outside" and close the menu before the option's
  // own onClick had a chance to fire.
  const sortMenuRef = useRef(null);

  useEffect(() => {
    if (!isSortDropdownOpen) return undefined;
    const handleOutsideInteraction = (e) => {
      if (
        sortDropdownRef.current && !sortDropdownRef.current.contains(e.target) &&
        sortTriggerRef.current && !sortTriggerRef.current.contains(e.target) &&
        (!sortMenuRef.current || !sortMenuRef.current.contains(e.target))
      ) {
        setIsSortDropdownOpen(false);
      }
    };
    // Also close on scroll/resize rather than trying to re-track position —
    // the menu is portaled (position: fixed) so it would otherwise drift
    // away from the trigger button.
    const closeDropdown = () => setIsSortDropdownOpen(false);
    document.addEventListener('mousedown', handleOutsideInteraction);
    document.addEventListener('touchstart', handleOutsideInteraction);
    window.addEventListener('scroll', closeDropdown, true);
    window.addEventListener('resize', closeDropdown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInteraction);
      document.removeEventListener('touchstart', handleOutsideInteraction);
      window.removeEventListener('scroll', closeDropdown, true);
      window.removeEventListener('resize', closeDropdown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSortDropdownOpen]);

  // Lock body scroll while this overlay is open. .menu-app-container is
  // position:fixed/inset:0 (see index.css), so it visually covers the
  // whole screen, but a fixed element doesn't remove the page behind it
  // from the document's scroll — without this, the underlying page (e.g.
  // Home, which is taller than the viewport) stays scrollable and its own
  // scrollbar (styled thick/gray for the main browser window in App.css)
  // keeps rendering at the edge of the browser window, indistinguishable
  // at a glance from — and easy to mistake for — one of this overlay's own
  // internal scroll areas.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Custom Categories sidebar scrollbar: native scrollbar thumbs can't be
  // given a fixed/compact height via CSS on any engine — the browser always
  // sizes the thumb proportionally to (visible / total) content, with no
  // property to override that for the scroll-axis dimension. A short,
  // fixed-height thumb like the reference design needs a real custom
  // thumb, drawn on top of the (still fully functional, untouched) native
  // scroll container and kept in sync with it. Native scrolling itself —
  // wheel, trackpad, touch, keyboard — is completely unaffected; this only
  // adds a visual indicator plus lets that indicator itself be dragged.
  const sidebarScrollRef = useRef(null);
  const sidebarThumbRef = useRef(null);
  const sidebarTrackRef = useRef(null);
  const SIDEBAR_THUMB_HEIGHT = 48;

  useEffect(() => {
    const el = sidebarScrollRef.current;
    const thumb = sidebarThumbRef.current;
    const track = sidebarTrackRef.current;
    if (!el || !thumb || !track) return undefined;
    let hideTimer;

    const updateThumb = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop <= 1) {
        thumb.style.display = 'none';
        return;
      }
      thumb.style.display = 'block';
      // The thumb travels within the track's own height (the track starts
      // below the sticky title — see CSS — so it's shorter than the
      // scroll container's full clientHeight), not the scroll container's
      // clientHeight itself, otherwise the thumb overshoots the sidebar's
      // bottom edge by the title's height once scrolled to the end.
      const trackHeight = track.clientHeight;
      const thumbHeight = Math.min(SIDEBAR_THUMB_HEIGHT, trackHeight);
      const travel = Math.max(trackHeight - thumbHeight, 0);
      const thumbTop = (scrollTop / maxScrollTop) * travel;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    // Thumb stays hidden until scroll activity, then fades out again after
    // a short idle period — matches OS-style auto-hide scrollbars. Dragging
    // the thumb itself also fires native scroll events (see the pointerdown
    // handler below setting el.scrollTop), so this same listener keeps it
    // visible through a drag too, without needing separate logic there.
    const handleScroll = () => {
      updateThumb();
      thumb.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => thumb.classList.remove('is-visible'), 1000);
    };

    updateThumb();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateThumb);

    // Category list height can change after this effect's initial run
    // (images/skeleton -> real items) — a ResizeObserver catches that
    // without needing every state value that could affect it as a dep.
    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumb);
      resizeObserver.disconnect();
      clearTimeout(hideTimer);
    };
  }, [categories]);

  const handleSidebarThumbPointerDown = (e) => {
    const el = sidebarScrollRef.current;
    const track = sidebarTrackRef.current;
    if (!el || !track) return;
    e.preventDefault();
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.min(SIDEBAR_THUMB_HEIGHT, trackHeight);
    const travel = Math.max(trackHeight - thumbHeight, 0);

    const handlePointerMove = (moveEvent) => {
      if (travel <= 0) return;
      const deltaY = moveEvent.clientY - startY;
      const scrollDelta = (deltaY / travel) * maxScrollTop;
      el.scrollTop = Math.min(Math.max(startScrollTop + scrollDelta, 0), maxScrollTop);
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Custom scrollbar thumb for the middle column (.menu-app-main — search +
  // categories strip + product grid together, per the existing "scrolling
  // parent for this whole column" design). Same reasoning, values, and
  // structure as the Categories sidebar's custom scrollbar above: no
  // browser lets CSS give a native scrollbar thumb a fixed/short height
  // (confirmed by direct testing), so a short thumb needs a real custom
  // one. Native scrolling (wheel/trackpad/touch) is completely
  // unaffected — .menu-app-main-scroll below is still the real,
  // natively-scrolling element; only its default scrollbar chrome is
  // hidden in favor of this custom indicator.
  const mainScrollRef = useRef(null);
  const mainThumbRef = useRef(null);
  const mainTrackRef = useRef(null);
  const MAIN_THUMB_HEIGHT = 48;

  useEffect(() => {
    const el = mainScrollRef.current;
    const thumb = mainThumbRef.current;
    const track = mainTrackRef.current;
    if (!el || !thumb || !track) return undefined;
    let hideTimer;

    const updateThumb = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScrollTop = scrollHeight - clientHeight;
      if (maxScrollTop <= 1) {
        thumb.style.display = 'none';
        return;
      }
      thumb.style.display = 'block';
      const trackHeight = track.clientHeight;
      const thumbHeight = Math.min(MAIN_THUMB_HEIGHT, trackHeight);
      const travel = Math.max(trackHeight - thumbHeight, 0);
      const thumbTop = (scrollTop / maxScrollTop) * travel;
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    // Thumb stays hidden until scroll activity, then fades out again after
    // a short idle period — matches OS-style auto-hide scrollbars. Dragging
    // the thumb itself also fires native scroll events (see the pointerdown
    // handler below setting el.scrollTop), so this same listener keeps it
    // visible through a drag too, without needing separate logic there.
    const handleScroll = () => {
      updateThumb();
      thumb.classList.add('is-visible');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => thumb.classList.remove('is-visible'), 1000);
    };

    updateThumb();
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateThumb);

    // Product grid height changes constantly (category switch, search,
    // filters, sort) — a ResizeObserver catches all of that without
    // needing every state value that could affect it as a dep.
    const resizeObserver = new ResizeObserver(updateThumb);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThumb);
      resizeObserver.disconnect();
      clearTimeout(hideTimer);
    };
  }, []);

  const handleMainThumbPointerDown = (e) => {
    const el = mainScrollRef.current;
    const track = mainTrackRef.current;
    if (!el || !track) return;
    e.preventDefault();
    const startY = e.clientY;
    const startScrollTop = el.scrollTop;
    const maxScrollTop = el.scrollHeight - el.clientHeight;
    const trackHeight = track.clientHeight;
    const thumbHeight = Math.min(MAIN_THUMB_HEIGHT, trackHeight);
    const travel = Math.max(trackHeight - thumbHeight, 0);

    const handlePointerMove = (moveEvent) => {
      if (travel <= 0) return;
      const deltaY = moveEvent.clientY - startY;
      const scrollDelta = (deltaY / travel) * maxScrollTop;
      el.scrollTop = Math.min(Math.max(startScrollTop + scrollDelta, 0), maxScrollTop);
    };
    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Edge-fade affordance for the retail category chip strip (hints there's
  // more to scroll) — purely presentational scroll-position tracking, no
  // business logic. The strip scrolls normally via drag/swipe/wheel; there
  // are no arrow buttons driving it.
  const categoriesScrollRef = useRef(null);
  const [categoriesCanScrollLeft, setCategoriesCanScrollLeft] = useState(false);
  const [categoriesCanScrollRight, setCategoriesCanScrollRight] = useState(false);

  useEffect(() => {
    const el = categoriesScrollRef.current;
    if (!el) return;
    const updateCategoriesScrollState = () => {
      setCategoriesCanScrollLeft(el.scrollLeft > 4);
      setCategoriesCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    updateCategoriesScrollState();
    // Re-check shortly after mount too — category thumbnail images finish
    // loading after this effect's first run and can change scrollWidth,
    // which would otherwise leave a stale "can scroll" arrow/fade showing
    // even once everything actually fits.
    const recheckTimer = setTimeout(updateCategoriesScrollState, 400);
    el.addEventListener('scroll', updateCategoriesScrollState, { passive: true });
    window.addEventListener('resize', updateCategoriesScrollState);

    // Also react to the container's own box changing size — e.g. hiding an
    // arrow removes it from the flex row, which widens the scroll area and
    // can itself flip whether the other side still needs to scroll.
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateCategoriesScrollState);
      resizeObserver.observe(el);
    }

    return () => {
      clearTimeout(recheckTimer);
      el.removeEventListener('scroll', updateCategoriesScrollState);
      window.removeEventListener('resize', updateCategoriesScrollState);
      resizeObserver?.disconnect();
    };
  }, [categories]);

  // ✅ FIX 1: generateCartItemKey - MUST be defined early (no excessive logging)
  const generateCartItemKey = (product) => {
    const parts = [product.id];

    // Weight-based items are distinguished by their selected weight, the
    // same way WeightSelectionModal itself keys them (`${id}|w:${weight}`).
    // Without this, regenerating a key for an already-weight-selected cart
    // item (e.g. on cart restore from storage) silently drops the weight
    // suffix, so a later add of the same weight can no longer find this
    // entry to increment and creates a duplicate line instead.
    if (product.isWeightBased && product.selectedWeight !== undefined && product.selectedWeight !== null) {
      parts.push(`w:${product.selectedWeight}`);
    }

    // Add variation IDs (sorted for consistency)
    if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
      const variationIds = product.variations
        .map(v => v.variation_id || v.id)
        .filter(id => id !== undefined)
        .sort()
        .join('-');
      if (variationIds) parts.push(`v:${variationIds}`);
    }

    // Add extra IDs (sorted for consistency)
    if (product.extras && Array.isArray(product.extras) && product.extras.length > 0) {
      const extraIds = product.extras
        .map(e => e.extra_id || e.id)
        .filter(id => id !== undefined)
        .sort()
        .join('-');
      if (extraIds) parts.push(`e:${extraIds}`);
    }

    // Add addon IDs (sorted for consistency)
    if (product.addons && Array.isArray(product.addons) && product.addons.length > 0) {
      const addonIds = product.addons
        .map(a => a.addon_item_id || a.id)
        .filter(id => id !== undefined)
        .sort()
        .join('-');
      if (addonIds) parts.push(`a:${addonIds}`);
    }

    return parts.join('|');
  };

  // ✅ FIX 2: areCartItemsIdentical - MUST be defined early
  const areCartItemsIdentical = (item1, item2) => {
    // If both have keys, compare keys
    if (item1.cartItemKey && item2.cartItemKey) {
      return item1.cartItemKey === item2.cartItemKey;
    }

    // If one or both don't have keys, generate and compare
    const key1 = item1.cartItemKey || generateCartItemKey(item1);
    const key2 = item2.cartItemKey || generateCartItemKey(item2);

    return key1 === key2;
  };


  const { t } = useTranslation();

  // Removed local sync useEffect since we use props directly now

  // Auto-dismiss menu toast after 3 seconds
  useEffect(() => {
    if (menuToast) {
      const timer = setTimeout(() => setMenuToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [menuToast]);

  // ✅ Mobile logic: Handle back button for Cart
  useEffect(() => {
    if (!showCart) return;

    // Push state when opening
    window.history.pushState({ mobileCart: true }, '');

    const handlePopState = (event) => {
      // Only close if we're no longer in the mobileCart state
      // (parent navigation or going back further)
      // If we are still in mobileCart state (e.g. back from a child modal), do nothing
      if (event.state?.mobileCart) {
        return;
      }
      setShowCart(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Cleanup: if closing manually, remove the state we pushed
      // We check if current state is ours to avoid popping unrelated states
      if (window.history.state?.mobileCart) {
        window.history.back();
      }
    };
  }, [showCart]);

  // ✅ Mobile logic: Handle back button for Offers Modal
  useEffect(() => {
    if (!showOffersModal) return;

    window.history.pushState({ offersModal: true }, '');

    const handlePopState = (event) => {
      if (event.state?.offersModal) {
        return;
      }
      setShowOffersModal(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.offersModal) {
        window.history.back();
      }
    };
  }, [showOffersModal]);

  // ✅ Mobile logic: Handle back button for Payment Modal
  useEffect(() => {
    if (!showPaymentModal) return;

    window.history.pushState({ paymentModal: true }, '');

    const handlePopState = (event) => {
      if (event.state?.paymentModal) {
        return;
      }
      setShowPaymentModal(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.paymentModal) {
        window.history.back();
      }
    };
  }, [showPaymentModal]);

  // ✅ Mobile logic: Handle back button for Customization Modal
  useEffect(() => {
    if (!showCustomizeModal) return;

    window.history.pushState({ customizeModal: true }, '');

    const handlePopState = (event) => {
      if (event.state?.customizeModal) {
        return;
      }
      setShowCustomizeModal(false);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.customizeModal) {
        window.history.back();
      }
    };
  }, [showCustomizeModal]);

  // ✅ FIX: Memoize apiClient to prevent recreation on every render
  const apiClient = useMemo(() => {
    const client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key': X_API_KEY,
        'Accept': 'application/json',
      }
    });

    client.interceptors.request.use(
      (config) => {
        // Read fresh on every request rather than baking it into the static
        // headers above — this client is memoized on [userToken] only, so a
        // static value here would freeze whatever getTenantId() returned at
        // mount time (often null, since it's populated asynchronously by the
        // /ordering/init bootstrap call), sending a stale/missing X-Tenant on
        // every later request — including order placement.
        config.headers['X-Tenant'] = getTenantId();

        if (config.skipAuth) {
          // Don't attach Bearer token for public endpoints
          delete config.headers['Authorization'];
          return config;
        }

        if (userToken) {
          config.headers['Authorization'] = `Bearer ${userToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          console.error('API Error:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('Network Error:', error.request);
        } else {
          console.error('Error:', error.message);
        }
        return Promise.reject(error);
      }
    );

    return client;
  }, [userToken]); // Only recreate when userToken changes

  // ✅ Confirmed branch count for the "Change Store" button's visibility
  // below (a genuine single-branch tenant should never see it). Previously
  // that check read apiCache.getBranches() directly inline and treated a
  // null/cold cache (hard refresh, direct deep link to /restaurant/:slug,
  // cache TTL expiry) as "show the button" — showing it for single-branch
  // tenants until something else happened to warm the cache. Worse, since
  // apiCache is a plain object read inline (not React state), another
  // component's fetch populating it later wouldn't even trigger a
  // re-render here, so the button could stay wrong indefinitely. Loading
  // it into real state fixes both: hidden by default until confirmed, and
  // reactive once the count is known.
  const [branchesReady, setBranchesReady] = useState(false);
  const [totalBranchCount, setTotalBranchCount] = useState(null);

  useEffect(() => {
    const cachedBranches = apiCache.getBranches();
    if (cachedBranches) {
      setTotalBranchCount(cachedBranches.length);
      setBranchesReady(true);
      return undefined;
    }

    let cancelled = false;
    const fetchBranchesForChangeStore = async () => {
      try {
        const response = await apiClient.get('/web/branch');
        const data = response.data?.data || [];
        apiCache.setBranches(data);
        if (!cancelled) {
          setTotalBranchCount(data.length);
          setBranchesReady(true);
        }
      } catch (err) {
        console.error('Error fetching branches for Change Store visibility:', err);
        if (!cancelled) setBranchesReady(true);
      }
    };
    fetchBranchesForChangeStore();

    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const [offersManager] = useState(() => new OffersManager(apiClient));

  // ✅ FIX: Subscribe to offer changes with debouncing to prevent excessive re-renders
  useEffect(() => {
    let updateTimeout;

    const unsubscribe = offersManager.subscribe((state) => {
      // Clear any pending update
      clearTimeout(updateTimeout);

      console.log('🎁 [OFFER SUBSCRIPTION] Offers changed (debounced):', {
        appliedCount: state.appliedOffers.length,
        autoAppliedCount: state.autoAppliedOffers.length,
        availableCount: state.availableOffers.length,
        totalSavings: state.appliedOffers.reduce((sum, o) => sum + o.discount_amount, 0) +
          state.autoAppliedOffers.reduce((sum, o) => sum + o.discount_amount, 0)
      });

      // Debounce state update to 500ms to prevent rapid-fire re-renders
      updateTimeout = setTimeout(() => {
        setOfferStateVersion(v => v + 1);
      }, 500);
    });

    return () => {
      console.log('🧹 [OFFER SUBSCRIPTION] Unsubscribing from offer changes');
      clearTimeout(updateTimeout);
      unsubscribe();
    };
  }, [offersManager]); // Only depend on offersManager

  const handleShowAuthModalFromHeader = () => {
    console.log('📱 Auth modal triggered from Header');
    navigate('/login', { state: { from: locationData.pathname } });
  };

  // ✅ DEBUG: Track render cycles only when isOpen changes
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    if (isOpen !== isOpenRef.current) {
      console.log(`📊 [RestaurantMenuApp] isOpen changed to:`, isOpen);
      isOpenRef.current = isOpen;
    }
  }, [isOpen]);

  // ✅ SIMPLE DEBUG: Track prop changes using only primitive values
  const debugRef = useRef({
    storeId: selectedStore?.id,
    orderType: orderDetails?.type,
    userId: userId,
    isOpen: isOpen
  });

  useEffect(() => {
    const changes = [];

    if (debugRef.current.storeId !== selectedStore?.id) {
      changes.push(`store: ${debugRef.current.storeId} → ${selectedStore?.id}`);
      debugRef.current.storeId = selectedStore?.id;
    }
    if (debugRef.current.orderType !== orderDetails?.type) {
      changes.push(`orderType: ${debugRef.current.orderType} → ${orderDetails?.type}`);
      debugRef.current.orderType = orderDetails?.type;
    }
    if (debugRef.current.userId !== userId) {
      changes.push(`userId: ${debugRef.current.userId} → ${userId}`);
      debugRef.current.userId = userId;
    }
    if (debugRef.current.isOpen !== isOpen) {
      changes.push(`isOpen: ${debugRef.current.isOpen} → ${isOpen}`);
      debugRef.current.isOpen = isOpen;
    }

    if (changes.length > 0 && renderCountRef.current % 10 === 0) {
      console.log(`📊 [PROPS CHANGED] Render #${renderCountRef.current}:`, changes);
    }
  }, [selectedStore?.id, orderDetails?.type, userId, isOpen]);

  const [liveWeekSchedule, setLiveWeekSchedule] = useState(null);
  const activeSchedule = liveWeekSchedule || selectedStore?.week_schedule;

  // ✅ Live Store Schedule Polling & Tab Focus Re-verification
  // Periodically fetches fresh branch details from the backend (every 30 seconds)
  // and when the user switches back to the tab, so any schedule updates made in
  // the admin panel immediately update store availability on active menu screens.
  useEffect(() => {
    const branchId = selectedStore?.id;
    if (!branchId || !isOpen) return;

    const checkLiveStoreSchedule = async () => {
      try {
        const response = await apiClient.get(`/web/branch/${branchId}`, {
          params: { _t: Date.now() },
          skipAuth: true
        });
        const freshBranch = response.data?.data;
        if (freshBranch && freshBranch.week_schedule) {
          apiCache.setStoreDetails(branchId, freshBranch);
          setLiveWeekSchedule(freshBranch.week_schedule);

          const freshAvailability = checkStoreAvailability(freshBranch.week_schedule);
          setStoreIsOpen(freshAvailability.isOpen);

          if (freshAvailability.isOpen) {
            setShowStoreClosedModal(false);
          } else {
            const supportsPreOrder = freshBranch.allow_pre_order === true || freshBranch.pre_order_enabled === true;
            if (!isPreOrder && !supportsPreOrder) {
              setStoreClosureInfo({
                message: freshAvailability.message,
                nextOpenTime: freshAvailability.nextOpenTime
              });
              setShowStoreClosedModal(true);
              storeWasOpenRef.current = false;
            }
          }
        }
      } catch (err) {
        console.error('Error polling live store schedule:', err);
      }
    };

    // 1. Initial check when menu opens
    checkLiveStoreSchedule();

    // 2. Poll every 30 seconds
    const intervalId = setInterval(checkLiveStoreSchedule, 30000);

    // 3. Re-check when window/tab regains focus
    const handleFocusOrVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkLiveStoreSchedule();
      }
    };

    window.addEventListener('focus', handleFocusOrVisibility);
    document.addEventListener('visibilitychange', handleFocusOrVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocusOrVisibility);
      document.removeEventListener('visibilitychange', handleFocusOrVisibility);
    };
  }, [selectedStore?.id, isOpen, apiClient, isPreOrder]);

  // ✅ FIX: Reset store open state when store or pre-order changes (use IDs)
  useEffect(() => {
    const scheduleToUse = activeSchedule || selectedStore?.week_schedule;
    if (scheduleToUse) {
      const availability = checkStoreAvailability(scheduleToUse);
      setStoreIsOpen(availability.isOpen);
    }
  }, [selectedStore?.id, activeSchedule]);

  // ✅ OPTIMIZED: Calculate milliseconds until store closes
  const calculateTimeUntilClosure = (weekSchedule) => {
    if (!weekSchedule) return null;

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()].toLowerCase();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

    const todaySchedule = weekSchedule[currentDay];

    if (!todaySchedule) return null;

    // Handle schedules array (new format)
    if (todaySchedule.schedules && Array.isArray(todaySchedule.schedules)) {
      for (const schedule of todaySchedule.schedules) {
        const openTime = (schedule.open_time.split(':')[0] * 60) + parseInt(schedule.open_time.split(':')[1]);
        const closeTime = (schedule.close_time.split(':')[0] * 60) + parseInt(schedule.close_time.split(':')[1]);

        // If currently within this time slot
        if (currentTime >= openTime && currentTime < closeTime) {
          const closeHour = parseInt(schedule.close_time.split(':')[0]);
          const closeMinute = parseInt(schedule.close_time.split(':')[1]);

          const closureTime = new Date();
          closureTime.setHours(closeHour, closeMinute, 0, 0);

          const msUntilClosure = closureTime.getTime() - now.getTime();
          console.log(`⏱️ Store closes at ${schedule.close_time}, in ${Math.round(msUntilClosure / 1000 / 60)} minutes`);

          return Math.max(msUntilClosure, 0); // Ensure positive
        }
      }
    }

    // Handle old format
    if (todaySchedule.open_time && todaySchedule.close_time) {
      const openTime = (todaySchedule.open_time.split(':')[0] * 60) + parseInt(todaySchedule.open_time.split(':')[1]);
      const closeTime = (todaySchedule.close_time.split(':')[0] * 60) + parseInt(todaySchedule.close_time.split(':')[1]);

      if (currentTime >= openTime && currentTime < closeTime) {
        const closeHour = parseInt(todaySchedule.close_time.split(':')[0]);
        const closeMinute = parseInt(todaySchedule.close_time.split(':')[1]);

        const closureTime = new Date();
        closureTime.setHours(closeHour, closeMinute, 0, 0);

        const msUntilClosure = closureTime.getTime() - now.getTime();
        console.log(`⏱️ Store closes at ${todaySchedule.close_time}, in ${Math.round(msUntilClosure / 1000 / 60)} minutes`);

        return Math.max(msUntilClosure, 0);
      }
    }

    return null;
  };

  // ✅ OPTIMIZED: Handle store closure detection with calculated timeout
  useEffect(() => {
    const scheduleToUse = activeSchedule || selectedStore?.week_schedule;
    if (!scheduleToUse) return;

    const availability = checkStoreAvailability(scheduleToUse);

    // Only set timeout if store is currently open
    if (!availability.isOpen) {
      console.log('🚪 Store already closed on load');
      return;
    }

    const msUntilClosure = calculateTimeUntilClosure(scheduleToUse);

    if (!msUntilClosure || msUntilClosure <= 0) {
      console.log('❌ Could not calculate closure time');
      return;
    }

    console.log(`⏰ Setting timeout for store closure in ${Math.round(msUntilClosure / 1000 / 60)} minutes`);

    // Set timeout for EXACT closure moment (much more efficient!)
    const closureTimeout = setTimeout(() => {
      console.log('🚪 Store closure time reached! Showing modal...');

      const updatedAvailability = checkStoreAvailability(scheduleToUse);

      if (!updatedAvailability.isOpen) {
        setStoreClosureInfo({
          message: updatedAvailability.message,
          nextOpenTime: updatedAvailability.nextOpenTime
        });
        setShowStoreClosedModal(true);
        storeWasOpenRef.current = false;
      }
    }, msUntilClosure);

    // Cleanup on unmount or store change
    return () => clearTimeout(closureTimeout);
  }, [selectedStore?.id, activeSchedule]);

  const guardStoreOpenAction = (options = {}) => {
    const { allowIfPreOrderSupported = true } = options;
    const scheduleToUse = activeSchedule || selectedStore?.week_schedule;
    if (!scheduleToUse) return true; // no schedule data — nothing to gate on
    const availability = checkStoreAvailability(scheduleToUse);
    if (!availability.isOpen) {
      const supportsPreOrder = selectedStore?.allow_pre_order === true || selectedStore?.pre_order_enabled === true;
      if (isPreOrder || (allowIfPreOrderSupported && supportsPreOrder)) {
        if (!contextIsPreOrder && setPreOrder && supportsPreOrder) {
          setPreOrder(true);
        }
        return true;
      }
      setStoreClosureInfo({
        message: availability.message,
        nextOpenTime: availability.nextOpenTime
      });
      setShowStoreClosedModal(true);
      setStoreIsOpen(false);
      storeWasOpenRef.current = false;
      return false;
    }
    return true;
  };

  // ✅ NEW: Handle store closed modal pre-order action
  const handlePreOrderFromModal = () => {
    console.log('📅 Pre-order initiated from store closed modal');
    if (setPreOrder) {
      setPreOrder(true);
    }
    setShowStoreClosedModal(false);
  };

  useEffect(() => {
    console.log('🔐 Checking for stored authentication...');

    const authData = getAuthFromCookies();

    if (authData) {
      console.log('✅ Auth data found:', {
        token: authData.token ? 'Present' : 'Missing',
        userId: authData.userId,
        phone: authData.phone
      });

      // ✅ CRITICAL: Set state immediately
      setUserToken(authData.token);
      setUserId(authData.userId); // ← This should set userId to "300"
      setUserPhone(authData.phone);

      // Fetch additional user data
      getDefaultAccess(authData.token);

      // ✅ Verify state was set correctly
      setTimeout(() => {
        console.log('🔍 Verification after 100ms:', {
          userIdState: authData.userId,
          cookieValue: getValidAuthSession()?.userId
        });
      }, 100);
    } else {
      console.log('⚠️ No authentication found in cookies');
    }
  }, []);

  const getDefaultAccess = async (token) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.defaultAccess);
      if (response.status === 200 && response.data.data) {
        console.log('Default Access Data:', response.data.data);
        const userData = response.data.data;
        if (userData.email) setUserEmail(userData.email);
        if (userData.name) setUserName(userData.name);
      }
    } catch (error) {
      console.error('Error getting default access:', error);
    }
  };

  // ✅ FIX 5: Update cart restoration (only depend on IDs, not objects)
  useEffect(() => {
    if (isOpen && selectedStore?.id && orderDetails?.type) {
      const storeId = selectedStore.id;
      const orderType = orderDetails.type;

      // ✅ Task 1: Clear carts from OTHER stores and OTHER order types when entering
      cartManager.clearOtherCarts(storeId, orderType);

      const savedCart = loadCartFromStorage(storeId, orderType);

      if (savedCart.length > 0) {
        // ✅ Regenerate keys for loaded items to ensure consistency
        const restoredCart = savedCart.map(item => ({
          ...item,
          cartItemKey: generateCartItemKey(item)
        }));

        console.log('📦 Restored cart from storage:', restoredCart.length, 'items');
        setCart(restoredCart);
      }

      loadInitialData();

      // Save store details to sessionStorage for post-payment redirects.
      // tenant_id is stored alongside so a stale entry from a different
      // tenant's storefront (same browser origin) can be detected and
      // rejected on read, rather than blindly trusted by name-slug match alone.
      if (selectedStore) {
        try {
          sessionStorage.setItem('selected_store_details', JSON.stringify({
            id: selectedStore.id,
            name: selectedStore.name,
            tenant_id: getTenantId()
          }));
        } catch (e) {
          // ignore
        }
      }
    }
  }, [isOpen, selectedStore?.id, orderDetails?.type]);

  useEffect(() => {
    if (selectedStore?.id && orderDetails?.type) {
      const storeId = selectedStore.id;
      const orderType = orderDetails.type;

      if (cart.length > 0) {
        saveCartToStorage(storeId, orderType, cart);
      } else {
        // Clear storage when cart becomes empty
        clearCartFromStorage(storeId, orderType);
      }
    }
  }, [cart, selectedStore?.id, orderDetails?.type]);
  const getBranchId = () => {
    if (!selectedStore?.id) {
      console.error('❌ getBranchId: selectedStore is missing an id — refusing to guess a branch.', { selectedStore });
      throw new Error('Store information is missing. Please reload the page and select your restaurant again.');
    }
    return selectedStore.id;
  };

  const getCustomerId = () => {
    // Simplified getCustomerId for performance
    return userId || getValidAuthSession()?.userId || null;
  };

  // ✅ MEMOIZE: Calculate login status once at the top of render
  const currentCustomerId = getCustomerId();
  const isUserLoggedIn = !!userToken || !!currentCustomerId;

  const getOrderType = () => {
    if (!orderDetails?.type) return 5;
    return orderDetails.type === 'collection' ? 10 : 5;
  };

  // ✅ FIXED: Removed excessive logging to prevent console spam on every cart change
  const calculateItemPrice = (item) => {
    const variations = Array.isArray(item.variations) ? item.variations : [];
    const extras = Array.isArray(item.extras) ? item.extras : [];
    const addons = Array.isArray(item.addons) ? item.addons : [];

    // ✅ CRITICAL: Check flatPrice FIRST (customized items from CustomizationModal)
    // flatPrice is pre-calculated and includes base + variations + extras + addons
    if (item.flatPrice) {
      return Number(item.flatPrice);
    }

    const basePrice = Number(item.price || 0);
    let itemPrice = basePrice;

    // STEP 1: Base price + highest variation price delta (for non-customized items)
    if (variations.length > 0) {
      const prices = variations.map(v => Number(v.variation_price || v.price || 0));
      itemPrice = basePrice + Math.max(...prices);
    }

    // STEP 2: Add extras
    const extraTotal = extras.reduce((sum, e) => {
      const price = Number(e.extra_price || e.price || 0);
      return sum + price;
    }, 0);

    // STEP 3: Add addons
    const addonTotal = addons.reduce((sum, a) => {
      const price = Number(a.addon_item_price || a.price || 0);
      return sum + price;
    }, 0);

    return itemPrice + extraTotal + addonTotal;
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('appetizer') || name.includes('starter')) return '🍽️';
    if (name.includes('beverage') || name.includes('drink') || name.includes('juice')) return '🥤';
    if (name.includes('dessert') || name.includes('sweet')) return '🍰';
    if (name.includes('bread') || name.includes('roti') || name.includes('naan')) return '🫓';
    if (name.includes('curry') || name.includes('gravy')) return '🍛';
    if (name.includes('rice') || name.includes('biryani')) return '🍚';
    if (name.includes('chicken') || name.includes('meat') || name.includes('non-veg')) return '🍖';
    if (name.includes('veg') || name.includes('vegetable')) return '🥬';
    if (name.includes('salad')) return '🥗';
    if (name.includes('all')) return '🍽️';
    return '🍴';
  };

  const handleLogout = () => {
    console.log('🚪 User logging out - clearing cart and auth data');

    // A pending Add to Cart action waiting on login is meaningless once
    // the user logs out instead.
    setPendingProduct(null);
    clearPendingCartAction();

    // Clear auth cookies
    clearAuthSession();

    // Clear auth state
    setUserToken(null);
    setUserId(null);
    setUserPhone(null);
    setUserEmail(null);
    setUserName(null);

    // Clear cart from state
    setCart([]);
    setCartAnalysis(null);
    setCouponCode('');
    setSelectedCollectionTime('');
    setSelectedAddress(null);
    offersManager.clearOffers();

    // Clear cart from localStorage for all stores/order types
    try {
      // Get all localStorage keys
      const keys = Object.keys(localStorage);

      // Filter and remove all cart-related keys
      keys.forEach(key => {
        if (key.startsWith(CART_STORAGE_KEY)) {
          localStorage.removeItem(key);
          console.log('🗑️ Cleared cart:', key);
        }
      });

      console.log('✅ All cart data cleared from localStorage');
    } catch (error) {
      console.error('❌ Error clearing cart from localStorage:', error);
    }

    // Notify parent component if callback exists
    if (onAuthUpdate) {
      onAuthUpdate(null, null, null);
    }

    console.log('✅ Logout complete');

    // Call the global logout function if available, so the global toast shows as we navigate away
    if (headerProps && headerProps.onLogout) {
      headerProps.onLogout();
    } else {
      setMenuToast({ type: 'error', message: 'Logged Out Successfully.' });
    }
  };

  const handleAuthSuccess = (token, id, phone) => {
    // Must be set before returning — AuthModal calls onClose() right after
    // this, synchronously, and that handler reads this ref to know not to
    // clear the pending Add to Cart action (see authSucceededRef above).
    authSucceededRef.current = true;
    setUserToken(token);
    setUserId(id);
    setUserPhone(phone);
    getDefaultAccess(token);
    setShowAuthModal(false); // Close modal on success
    setMenuToast({ type: 'success', message: 'Logged In Successfully!' });

    if (onAuthUpdate) {
      onAuthUpdate(token, id, phone);
    }

    // ✅ Resuming the pending Add to Cart action is handled by the
    // pendingProduct effect below, NOT here. apiClient is memoized on
    // [userToken] (see its useMemo above) — a call made synchronously in
    // this same tick, right after setUserToken(token), would still close
    // over the PREVIOUS apiClient instance (built before login, with no
    // Authorization header) since React hasn't re-rendered yet. Any API
    // call inside the resumed add (e.g. the inventory check) would then
    // silently run unauthenticated. Deferring to an effect guarantees the
    // resume only runs after the component has actually re-rendered with
    // the fresh, correctly-authenticated apiClient.
  };

  // ✅ Resume the exact pending Add to Cart action once login has actually
  // taken effect (isUserLoggedIn recomputes fresh every render from
  // userToken/userId, so this only fires on the render after
  // handleAuthSuccess's state updates have committed — the same render
  // where apiClient's own [userToken] useMemo has already produced a fresh,
  // correctly-authenticated client). Re-enters the SAME function the
  // original click would have used, so inventory checks, pricing, and
  // combo/weight shaping stay identical to any other logged-in add.
  //
  // Falls back to sessionStorage (readPendingCartAction) when pendingProduct
  // itself is empty — confirmed via live testing that this app remounts
  // RestaurantMenuApp during the login flow (its own renderCountRef resets
  // to 1 right after a successful login), which wipes plain useState before
  // this effect ever gets to run on the pre-remount instance. This same
  // effect on the FRESH post-remount instance still fires on mount with
  // isUserLoggedIn already true, and recovers the pending item from
  // sessionStorage instead.
  useEffect(() => {
    if (!isUserLoggedIn) return;
    let pending = pendingProduct;
    if (!pending) {
      pending = readPendingCartAction();
    }
    if (!pending) return;
    setPendingProduct(null);
    clearPendingCartAction();
    const { type, product } = pending;
    if (type === 'weighted') {
      handleWeightConfirm(product);
    } else if (type === 'customized') {
      handleCustomizedProductAdd(product, false);
    } else {
      addToCart(product);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingProduct, isUserLoggedIn]);

  // 2️⃣ FILTER HANDLER FUNCTIONS
  const handleVegFilterClick = () => {
    if (vegFilter) {
      // If veg is already active, deactivate it
      setVegFilter(false);
    } else {
      // Activate veg and deactivate non-veg
      setVegFilter(true);
      setNonVegFilter(false);
    }
  };

  const handleNonVegFilterClick = () => {
    if (nonVegFilter) {
      // If non-veg is already active, deactivate it
      setNonVegFilter(false);
    } else {
      // Activate non-veg and deactivate veg
      setNonVegFilter(true);
      setVegFilter(false);
    }
  };


  // addToCartAfterAuth removed — handleAuthSuccess now resumes through the
  // same addToCart / handleCustomizedProductAdd / handleWeightConfirm
  // functions the original click used (see handleAuthSuccess above),
  // instead of this separate, simplified duplicate that skipped the
  // inventory check and combo/weight price shaping those do.


  // const analyzeCart = async (cartItems) => {
  //   if (!cartItems || cartItems.length === 0) {
  //     setCartAnalysis(null);
  //     return;
  //   }
  //  const customerId = getCustomerId();
  //   if (!customerId) {
  //     console.log('⚠️ Skipping cart analysis - user not logged in');
  //     setCartAnalysis(null);
  //     return;
  //   }
  //   setAnalyzingCart(true);

  //   try {
  //     const items = prepareCartAnalysisPayload(cartItems);

  //     const cartData = {
  //       branch_id: Number(getBranchId()),
  //       order_type: Number(getOrderType()),
  //       delivery_charge: orderDetails?.type === 'collection' ? 0 : 50,
  //       items: items
  //     };

  //     if (couponCode) {
  //       cartData.coupon_code = couponCode;
  //     }

  //     console.log('📤 Cart Analysis Request:', JSON.stringify(cartData, null, 2));

  //     const response = await apiClient.post('/web/cart/analyze', cartData);
  //     const analysisData = response.data?.data || response.data;

  //     console.log('📥 Cart Analysis Response:', analysisData);

  //     setCartAnalysis(analysisData || null);
  //   } catch (error) {
  //     console.error('❌ Cart analysis error:', error);
  //     setCartAnalysis(null);
  //   } finally {
  //     setAnalyzingCart(false);
  //   }
  // };


  // const analyzeCartWithCoupon = async (appliedCouponCode) => {
  //   if (!cart || cart.length === 0) {
  //     setCartAnalysis(null);
  //     return;
  //   }

  //   setAnalyzingCart(true);

  //   try {
  //     const cartData = {
  //       branch_id: Number(getBranchId()),
  //       order_type: Number(getOrderType()),
  //       delivery_charge: orderDetails?.type === 'collection' ? 0 : 50,
  //       coupon_code: appliedCouponCode, // Include coupon code
  //       items: cart.map(item => {
  //         const variations = Array.isArray(item.variations)
  //           ? item.variations
  //           : item.variations
  //           ? [item.variations]
  //           : [];

  //         const extras = Array.isArray(item.extras)
  //           ? item.extras
  //           : item.extras
  //           ? [item.extras]
  //           : [];

  //         const basePrice = Number(item.flatPrice || item.price || 0);

  //         const variationTotal = variations.reduce(
  //           (sum, v) => sum + Number(v.variation_price || v.price || 0),
  //           0
  //         );

  //         const extraTotal = extras.reduce(
  //           (sum, e) => sum + Number(e.extra_price || e.price || 0),
  //           0
  //         );

  //         const totalPrice =
  //           (basePrice + variationTotal + extraTotal) *
  //           Number(item.quantity);

  //         return {
  //           item_id: Number(item.id),
  //           quantity: Number(item.quantity),
  //           total_price: Number(totalPrice.toFixed(2)),
  //           item_variations: variations.map(v => ({
  //             variation_id: Number(v.variation_id || v.id),
  //             variation_name: v.variation_name || v.name || '',
  //             variation_price: Number(v.variation_price || v.price || 0)
  //           })),
  //           item_extras: extras.map(e => ({
  //             extra_id: Number(e.extra_id || e.id),
  //             extra_name: e.extra_name || e.name || '',
  //             extra_price: Number(e.extra_price || e.price || 0)
  //           }))
  //         };
  //       })
  //     };

  //     console.log('🔄 Re-analyzing cart with coupon:', cartData);

  //     const response = await apiClient.post('/web/cart/analyze', cartData);
  //     const analysisData = response.data?.data || response.data;

  //     console.log('✅ Cart analysis with coupon:', analysisData);

  //     setCartAnalysis(analysisData || null);
  //   } catch (error) {
  //     console.error('Error analyzing cart with coupon:', error);
  //     setCartAnalysis(null);
  //   } finally {
  //     setAnalyzingCart(false);
  //   }
  // };


  const cartAnalysisInProgressRef = useRef(false);
  const lastAnalyzedCartSignatureRef = useRef('');
  // ✅ FIX: A newer cart/address signature that arrives while a previous
  // /web/cart/analyze call is still in flight used to be silently dropped —
  // the in-flight request would finish and mark itself "up to date" even
  // though the world had moved on, freezing delivery fee/shipping on stale
  // data. This flag records that a newer signature showed up mid-flight so
  // the in-flight request's completion handler can re-run analysis for the
  // latest state immediately, instead of dropping it.
  const pendingReanalysisRef = useRef(false);

  console.log('------------------------------------------');
  // ✅ Memoize cart serialization to prevent unnecessary effect triggers
  const cartSignature = useMemo(() => {
    if (cart.length === 0) return '';

    // Create a stable signature of cart contents
    // CRITICAL FIX: Include price and customizations in signature!
    // Previously only tracked id-qty-key, which ignored customization edits
    const itemsSig = cart.map(item => {
      const price = item.flatPrice || item.price || 0;
      const vars = (item.variations || []).map(v => v.id || v.variation_id).join(',');
      const exts = (item.extras || []).map(e => e.id || e.extra_id).join(',');
      const adds = (item.addons || []).map(a => a.id || a.addon_item_id).join(',');

      return `${item.id}-${item.quantity}-${item.cartItemKey}-${price}-v[${vars}]-e[${exts}]-a[${adds}]`;
    }).join('|');

    // Include address in signature only for delivery orders when ShipRocket is configured,
    // so re-analysis fires when address changes (to recalculate shipping rate)
    const currentOrderType = orderDetails?.type;
    const shiprocketActive = settingsService.isShiprocketEnabled();
    const addressSig = shiprocketActive && (currentOrderType === 'delivery' || currentOrderType === 5)
      ? (selectedAddress?.id || selectedAddress?.zip_code || selectedAddress?.post_code || '')
      : '';

    // ✅ Include redeemed loyalty points so changing the Redeem Points input
    // re-triggers /web/cart/analyze and the backend re-computes the total.
    const base = addressSig ? `${itemsSig}@addr:${addressSig}` : itemsSig;
    // A different Air/Surface pick means a different delivery fee, so it re-triggers analysis too.
    const withSpeed = addressSig && deliverySpeed ? `${base}@sp:${deliverySpeed}` : base;
    return redeemPoints > 0 ? `${withSpeed}@lp:${redeemPoints}` : withSpeed;
  }, [cart, selectedAddress, orderDetails?.type, redeemPoints, deliverySpeed]);

  // ✅ FIX: Extracted so it can be re-invoked from the finally-block below
  // when a newer signature arrived while this request was running.
  // runCartAnalysisRef always points at the CURRENT render's version of this
  // function, so whenever it actually executes it closes over the latest
  // cart/selectedAddress/orderDetails/couponCode/userId — never a stale
  // closure from whenever the request was originally scheduled.
  const runCartAnalysisRef = useRef(null);
  runCartAnalysisRef.current = async () => {
    // Re-check right before doing any work — the signature may already
    // have been satisfied by another run while this one was queued.
    if (cartSignature === lastAnalyzedCartSignatureRef.current) {
      return;
    }

    cartAnalysisInProgressRef.current = true;
    pendingReanalysisRef.current = false;
    setAnalyzingCart(true);
    console.log('🚀 Starting cart analysis for signature:', cartSignature);

    try {
      const branchId = getBranchId();
      const customerId = getCustomerId();
      const regularItems = cart.filter(item => !item.isCombo && !item.isComboOffer);

      // ✅ DEBUG: Log EXACT cart items being sent to analyze
      console.group('📦 CART ITEMS BEING SENT TO ANALYZE:');
      cart.forEach((item, idx) => {
        console.log(`Item ${idx + 1}:`, {
          name: item.name,
          id: item.id,
          price: item.price,
          flatPrice: item.flatPrice,
          variations: item.variations?.map(v => ({ name: v.variation_name, price: v.variation_price })) || [],
          quantity: item.quantity
        });
      });
      console.groupEnd();

      console.log('🔄 🚀 ANALYZING CART NOW:', {
        items: cart.length,
        regularItems: regularItems.length,
        branchId,
        customerId,
        signature: cartSignature
      });

      // Build address options for API analysis
      const orderType = getOrderType();
      const addressOptions = {
        order_type: orderType
      };

      // ✅ Redeem Points — pass the entered amount so the analysis response
      // includes the backend-computed loyalty discount and reduced total.
      if (redeemPoints > 0) {
        addressOptions.loyalty_points_used = redeemPoints;
      }

      // Only send address/pincode for delivery orders
      if (orderType === 5) {
        if (selectedAddress?.id) {
          addressOptions.address_id = selectedAddress.id;
        } else {
          // Fallback 1: Pincode from selected address (if it's a temp/manual address)
          const addrPincode = selectedAddress?.zip_code || selectedAddress?.post_code;

          // Fallback 2: Pincode from detected location in Header
          const detectedPincode = localStorage.getItem('detected_pincode');

          const fallbackPincode = addrPincode || detectedPincode;
          if (fallbackPincode) {
            addressOptions.customer_pincode = String(fallbackPincode);
          }
        }

        // The customer's Air/Surface pick — the server returns that speed's fee when it is offered.
        if (deliverySpeed) {
          addressOptions.delivery_speed = deliverySpeed;
        }
      }

      // ✅ FIX: Bundle/combo-only carts now go through this exact same real
      // /web/cart/analyze call as normal and mixed carts. Previously a
      // combo-only cart skipped this call entirely and fabricated a local
      // cartAnalysis with no shipping/delivery data, so delivery fee was
      // never actually calculated for bundle-only orders. OffersManager's
      // _prepareCartItems already knows how to serialize combo items
      // (offer_id + is_combo: true) alongside regular items — unchanged —
      // so the existing bundle payload/backend contract is preserved.
      const analysis = await offersManager.analyzeCart(
        cart,
        branchId,
        customerId,
        addressOptions
      );

      console.log('✅ 📊 Cart analysis RESPONSE:', analysis ? 'HAS DATA' : 'NULL');
      setCartAnalysis(analysis);
      lastAnalyzedCartSignatureRef.current = cartSignature;
      console.log('✅ Cart analysis complete - cartAnalysis UPDATED');
    } catch (error) {
      console.error('❌ Cart analysis FAILED:', error.message);
      setCartAnalysis(null);
    } finally {
      cartAnalysisInProgressRef.current = false;
      setAnalyzingCart(false);

      // ✅ FIX: If cart/address changed again while this request was in
      // flight, re-run analysis immediately for the latest state instead of
      // leaving cartAnalysis stale until some unrelated change happens to
      // fire a fresh signature.
      if (pendingReanalysisRef.current) {
        pendingReanalysisRef.current = false;
        runCartAnalysisRef.current();
      }
    }
  };

  // ✅ FIXED: Cart analysis with proper dependencies
  useEffect(() => {
    console.log('🔍 [CART ANALYSIS EFFECT] Running - cart.length:', cart.length, 'cartSignature:', cartSignature);

    // Skip if cart is empty
    if (cart.length === 0) {
      console.log('📊 ❌ BLOCKED: Cart empty');
      setCartAnalysis(null);
      offersManager.clearOffers();
      // ✅ CRITICAL: Reset ref when cart is empty so next add won't match old signature
      lastAnalyzedCartSignatureRef.current = '';
      pendingReanalysisRef.current = false;
      console.log('🔄 Reset cartSignature ref (cart now empty)');
      return;
    }

    console.log('✅ PASSED: Cart has items:', cart.length);

    // ✅ NEW: Skip if user is not logged in
    const customerId = getCustomerId();
    console.log('👤 getCustomerId returned:', customerId);

    if (!customerId) {
      console.log('⚠️ ❌ BLOCKED: User not logged in');
      setCartAnalysis(null);
      lastAnalyzedCartSignatureRef.current = '';
      return;
    }

    console.log('✅ PASSED: User logged in');

    // ✅ FIX: For delivery orders, wait for the user's saved addresses to
    // finish loading before analyzing. Logging in while already inside
    // this component (as opposed to logging in beforehand) forces a
    // remount (see this component's key in App.js, which includes userId)
    // at the same moment a *separate*, async address fetch is kicked off
    // by that same userId change (useAddressManager). Without this guard,
    // the freshly-remounted instance's very first analysis call could fire
    // before that fetch resolves, going out with no address/pincode and
    // coming back with no serviceable delivery rate — this only defers
    // that first call until addresses have actually settled; it changes
    // nothing about the analysis logic, request, or response handling
    // itself, and has no effect once addresses are already loaded (the
    // normal case in every other flow).
    if (getOrderType() === 5 && addressesLoading) {
      console.log('⏳ ❌ BLOCKED: Addresses still loading for a delivery order');
      return;
    }

    const lastSig = lastAnalyzedCartSignatureRef.current;
    console.log('📝 Current sig:', cartSignature);
    console.log('📝 Last sig:', lastSig);
    console.log('📝 Signatures match?:', cartSignature === lastSig);

    // ✅ Skip if signature hasn't changed
    if (cartSignature === lastAnalyzedCartSignatureRef.current) {
      console.log('⏭️ ❌ BLOCKED: Cart signature unchanged');
      return;
    }

    console.log('✅ PASSED: New signature');

    // ✅ FIX: Instead of dropping this newer signature when a previous
    // request is still in flight, flag it so the in-flight request's own
    // completion handler picks it up and re-analyzes immediately.
    if (cartAnalysisInProgressRef.current) {
      console.log('⏳ Analysis already in progress — queued for re-run on completion');
      pendingReanalysisRef.current = true;
      return;
    }

    console.log('✅ PASSED: No analysis in progress');
    console.log('⏲️ Starting 400ms debounce timer...');

    // Debounce to prevent rapid-fire API calls
    const debounceTimer = setTimeout(() => {
      runCartAnalysisRef.current();
    }, 400);

    return () => {
      console.log('🧹 Clearing debounce timer');
      clearTimeout(debounceTimer);
    };
  }, [cartSignature, couponCode, userId, addressesLoading]); // ✅ Dependencies



  const checkInventoryAvailability = async (itemId, quantity, knownAvailableStock) => {
    try {
      if (!settingsService.isInventoryCheck()) {
        console.log('⏩ Skipping inventory check (disabled in settings)');
        return { canAdd: true };
      }

      console.log('🔍 Checking inventory for item:', itemId, 'quantity:', quantity, 'knownStock:', knownAvailableStock);

      const payload = {
        branch_id: getBranchId(),
        customer_id: getCustomerId(),
        item_id: Number(itemId),
        quantity: Number(quantity)
      };

      console.log('📦 Inventory check payload:', payload);

      const { data } = await apiClient.post('/web/cart/check-item', payload);

      console.log('✅ Inventory check response:', data);

      // Handle successful response
      if (data.status && data.data) {
        return {
          canAdd: data.data.can_add,
          // Use the /web/item available_stock as the real remaining quantity
          available_quantity: knownAvailableStock !== undefined ? knownAvailableStock : data.data.available_quantity
        };
      }

      // Handle when status is false (out of stock)
      return {
        canAdd: false,
        available_quantity: knownAvailableStock !== undefined ? knownAvailableStock : 0,
        message: data.message || 'Item unavailable'
      };

    } catch (error) {
      console.error('❌ Inventory check error:', error);

      // Handle API error responses (e.g. 400 Insufficient inventory)
      if (error.response?.data) {
        const errorData = error.response.data;
        console.log('📦 Error response data:', errorData);

        return {
          canAdd: false,
          // check-item returns available_quantity: 0 on rejection,
          // so use the REAL stock from /web/item instead
          available_quantity: knownAvailableStock !== undefined ? knownAvailableStock : (errorData.data?.available_quantity || 0),
          message: errorData.message || 'Insufficient inventory'
        };
      }

      // Handle network or other errors - allow adding if network fails
      return {
        canAdd: true,
        available_quantity: knownAvailableStock || 'unlimited',
        message: 'Network error. Proceeding without inventory check.'
      };
    }
  };

  // Add this function around line 780 (after checkInventoryAvailability)
  const checkAllProductsStock = async (products) => {
    if (!products || products.length === 0) return products;

    const stockChecks = await Promise.all(
      products.map(async (product) => {
        try {
          const check = await checkInventoryAvailability(product.id, 1);

          return {
            ...product,
            inventory_checked: true,
            is_available: check.available,
            is_tracked: check.is_tracked,
            available_stock: check.is_tracked ? parseFloat(check.available_quantity) : null
          };
        } catch (error) {
          // If check fails, assume available (fail-safe)
          return {
            ...product,
            inventory_checked: false,
            is_available: true,
            is_tracked: false,
            available_stock: null
          };
        }
      })
    );

    return stockChecks;
  };






  const fetchBranchDetails = async (branchId) => {
    try {
      console.log('Fetching branch details for ID:', branchId);
      const { data } = await apiClient.get(`/web/branch/${branchId}`);
      return data.data || null;
    } catch (error) {
      console.error('Error fetching branch details:', error);
      return null;
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await apiClient.get('/web/item-category', {
        params: { branch_id: getBranchId() },
        skipAuth: true,  // ← No Bearer token
      });
      return data.data || [];
    } catch (error) {
      return [];
    }
  };

  const fetchProducts = async (categoryId = null) => {
    try {
      const params = { branch_id: getBranchId() };
      if (categoryId && categoryId !== 'all') {
        params.item_category_id = categoryId;
      }

      const { data } = await apiClient.get('/web/item', {
        params,
        skipAuth: true,  // ← No Bearer token
      });
      return data.data || [];
    } catch (error) {
      return [];
    }
  };

  // ============================================
  // FIXED placeNewOrder function
  // Replace in RestaurantMenuApp/index.js around line 1046
  // ============================================

  const placeNewOrder = async (orderPayload) => {
    try {
      // Build final payload with items as JSON string
      const finalPayload = {
        branch_id: orderPayload.branch_id,
        order_type: orderPayload.order_type,
        items: JSON.stringify(orderPayload.items), // ✅ Convert to JSON string
        payment_method: orderPayload.payment_method,
        delivery_charge: orderPayload.delivery_charge,
        subtotal: orderPayload.subtotal,
        total: orderPayload.total,
        is_advance_order: orderPayload.is_advance_order,
        source: orderPayload.source,
        cooking_instruction: orderPayload.cooking_instruction || ''
      };



      // ✅ Add discount if present
      const totalDiscount = parseFloat(getTotalSavings || 0);
      if (totalDiscount > 0) {
        finalPayload.discount = totalDiscount;
        console.log('✅ Adding discount to payload:', totalDiscount);
      }

      // Add customer info based on login status
      if (orderPayload.customer_id) {
        finalPayload.customer_id = orderPayload.customer_id;
        finalPayload.customer = ""; // ✅ Empty string when using customer_id
      } else if (orderPayload.customer) {
        finalPayload.customer = orderPayload.customer;
      }


      // ✅ FIXED: Handle both address_id and address object
      if (orderPayload.address_id) {
        finalPayload.address_id = orderPayload.address_id;
        console.log('✅ Sending address_id:', finalPayload.address_id);
      } else if (orderPayload.address) {
        finalPayload.address = orderPayload.address;
        console.log('⚠️ Sending full address object (guest mode)');
      }
      const offersData = offersManager.getOrderPayloadData();
      // ✅ NEW: Add offers data properly
      if (orderPayload.applied_offer_ids && orderPayload.applied_offer_ids.length > 0) {
        finalPayload.applied_offer_ids = orderPayload.applied_offer_ids;
        console.log('✅ Adding applied_offer_ids:', finalPayload.applied_offer_ids);
      }

      if (orderPayload.total_offer_discount) {
        finalPayload.total_offer_discount = orderPayload.total_offer_discount;
        console.log('✅ Adding total_offer_discount:', finalPayload.total_offer_discount);
      }

      if (orderPayload.applied_offers && orderPayload.applied_offers.length > 0) {
        finalPayload.applied_offers = orderPayload.applied_offers.map(offer => ({
          offer_id: Number(offer.offer_id || offer.id),
          discount_amount: Number(offer.discount_amount || 0),
          offer_type: offer.offer_type || 'unknown'
        }));
        console.log('✅ Adding applied_offers:', finalPayload.applied_offers);
      }

      // Add optional fields
      if (orderPayload.coupon_code) {
        finalPayload.coupon_code = orderPayload.coupon_code;
      }
      if (orderPayload.delivery_time) {
        finalPayload.delivery_time = orderPayload.delivery_time;
      }
      if (orderPayload.scheduled_at) {
        finalPayload.scheduled_at = orderPayload.scheduled_at;
      }
      if (orderPayload.loyalty_points_used) {
        finalPayload.loyalty_points_used = orderPayload.loyalty_points_used;
      }
      if (orderPayload.payment_gateway) {
        finalPayload.payment_gateway = orderPayload.payment_gateway;
      }
      if (orderPayload.special_instructions) {
        finalPayload.special_instructions = orderPayload.special_instructions;
      }
      // Courier speed ('air' | 'surface') the delivery fee in this order was priced for, so staff
      // booking the shipment start from what the customer paid for.
      if (orderPayload.delivery_speed) {
        finalPayload.delivery_speed = orderPayload.delivery_speed;
      }

      console.log('📦 Final order payload:', finalPayload);

      const { data } = await apiClient.post('/web/order', finalPayload);
      console.log('✅ Order placed successfully:', data);

      return data.data;
    } catch (error) {
      console.error('❌ Order placement error:', error.response?.data || error);
      throw new Error(error.response?.data?.message || 'Failed to place order');
    }
  };

  // ✅ REMOVED: This effect was causing a race condition — it re-fired handleCategoryChange
  // every time selectedCategory changed, conflicting with loadInitialData's setProducts.

  // Fetch active + combo offers on mount — single call (see fetchInitialOffers),
  // stashed in a ref so loadInitialData can await this exact request instead
  // of firing its own duplicate.
  useEffect(() => {
    if (isOpen && selectedStore?.id) {
      if (!comboOffersPromiseRef.current) {
        comboOffersPromiseRef.current = fetchInitialOffers();
      }
    }
  }, [isOpen, selectedStore?.id]);


  // Add this button temporarily in your JSX (in the header area):

  // ✅ REMOVED: Cart verification effect - was causing excessive console logging
  // This was running on every cart change and creating infinite loop
  // Only enable if specifically needed for debugging

  // ✅ DEBOUNCED: Fetch available offers when cart changes
  useEffect(() => {
    if (cart.length === 0) {
      setAvailableOffers(null);
      return;
    }

    // ✅ Skip if user not logged in
    const customerId = getCustomerId();
    if (!customerId) {
      console.log('⚠️ Skipping available offers fetch - user not logged in');
      setAvailableOffers(null);
      return;
    }

    // Debounce to prevent excessive API calls
    const timer = setTimeout(() => {
      fetchAvailableOffers();
    }, 600);

    return () => clearTimeout(timer);
  }, [cart]);

  // ✅ NEW: Set global auth modal trigger for child components
  useEffect(() => {
    window.showAuthModal = () => {
      console.log('🌐 Global auth modal triggered');
      setShowAuthModal(true);
    };

    const handleOpenCart = () => {
      if (!guardStoreOpenAction()) return;
      setShowCart(true);
    };
    window.addEventListener('open_cart', handleOpenCart);

    return () => {
      delete window.showAuthModal;
      window.removeEventListener('open_cart', handleOpenCart);
    };
  }, []);

  // Add this useEffect in RestaurantMenuApp component
  // Place it after the existing useEffects (around line 300)
  // ============================================
  // EFFECT: Handle Payment Retry from Failure Page
  // ============================================
  useEffect(() => {
    // Only run if component is open
    if (!isOpen) return;

    // Check if we're coming from payment failure
    const urlParams = new URLSearchParams(locationData.search);
    const retryPayment = urlParams.get('retryPayment');
    const fromFailure = locationData.state?.fromPaymentFailure;

    if (retryPayment === 'true' || fromFailure) {
      console.log('🔄 Payment retry detected');
      console.log('📊 Current cart status:', {
        cartLength: cart.length,
        items: cart.map(i => i.name)
      });

      // Wait longer for cart restoration (storage might be loading)
      const retryTimer = setTimeout(() => {
        // Try to restore cart from storage one more time
        if (cart.length === 0 && selectedStore && orderDetails) {
          console.log('⚠️ Cart empty, attempting manual restoration...');
          const storeId = selectedStore.id;
          const orderType = orderDetails.type;
          const savedCart = loadCartFromStorage(storeId, orderType);

          if (savedCart.length > 0) {
            console.log('✅ Restored cart from storage:', savedCart.length, 'items');
            setCart(savedCart);

            // Wait for state update before opening modal
            setTimeout(() => {
              setShowPaymentModal(true);
              // ✅ Directly Auto-trigger online payment (Skip manual selection)
              // Comment the line below to show the manual payment selection modal again
              handlePaymentMethod('card');
              console.log('✅ Payment modal opened with restored cart and auto-triggered online payment');
            }, 100);
          } else {
            console.error('❌ No cart found in storage');

          }
        } else if (cart.length > 0) {
          console.log('✅ Cart already loaded, opening payment modal and auto-triggering online payment');
          setShowPaymentModal(true);
          handlePaymentMethod('card');
        } else {
          console.error('❌ Cannot retry - no store or order details');

        }
      }, 500); // Increased wait time

      // Clean up URL and state
      const cleanUrl = locationData.pathname + locationData.search.replace(/[?&]retryPayment=true/g, '');
      window.history.replaceState({}, '', cleanUrl);

      return () => clearTimeout(retryTimer);
    }
  }, [isOpen, cart.length, selectedStore?.id, orderDetails?.type]);
  // ============================================
  // ALSO FIX: handleCategoryChange function
  // Replace around line 1178
  // ============================================

  const handleCategoryChange = async (categoryId) => {
    try {
      console.log('📂 Category changed to:', categoryId);
      // ✅ Update ref immediately so async guards use the latest category
      activeCategoryRef.current = categoryId;
      setSelectedCategory(categoryId);
      setCategoryLoading(true); // ✅ Show skeleton loader

      if (categoryId === 'all') {
        console.log('📋 Showing all products:', allProducts.length);
        // 'all' is instant (cached) — tiny artificial delay for smooth UX
        setProducts(allProducts);

      } else if (categoryId === 'combo-offers') {
        console.log('🎁 Loading combo offers category');

        // ✅ Clear products immediately so old category items don't linger
        setProducts([]);

        // Use cached combos or fetch fresh
        let currentCombos = comboOffers;
        if (currentCombos.length === 0) {
          console.log('⚠️ No combo offers cached, fetching...');
          currentCombos = await fetchComboOffers(); // ✅ Get returned data directly
        }

        // ✅ Guard: only update products if user is still on combo-offers
        if (activeCategoryRef.current !== 'combo-offers') return;

        if (currentCombos.length === 0) {
          console.log('⚠️ No combo offers available — showing empty state');
          setProducts([]); // Empty state will show
          return;
        }

        // Transform combo offers into products
        const comboProducts = currentCombos.map(combo => ({
          id: `combo-${combo.id}`,
          name: combo.name || `Combo Offer ${combo.id}`,
          category: 'combo-offers',
          price: parseFloat(combo.combo_price || 0),
          flatPrice: parseFloat(combo.combo_price || 0),
          image: combo.thumb || combo.image_url || '',
          coverImage: combo.cover || combo.image_url || '',
          isVeg: true,
          description: combo.description || `Save with this combo deal!`,
          isCombo: true,
          comboData: combo,
          comboItems: combo.combo_items_details || combo.combo_items || [],
          status: 1,
          is_tracked: false,
          is_available: true,
          variations: [],
          extras: [],
          addons: []
        }));

        console.log('✅ Showing combo products:', comboProducts.length);
        setProducts(comboProducts);
      }
      else {
        // Regular category
        console.log('📂 Loading regular category:', categoryId);
        const productsData = await fetchProducts(categoryId);

        const processedProducts = productsData.map(product => {
          const isTracked = product.track_inventory || false;
          const availableStock = product.available_stock !== undefined ? product.available_stock : null;
          const isAvailable = !isTracked || (isTracked && availableStock > 0);

          const onlinePrice = (product.online_price && product.online_price !== '0.00') ? product.online_price : null;
          const storePrice = product.price;

          const processedItem = {
            id: product.id.toString(),
            name: product.name,
            category: product.item_category_id ? product.item_category_id.toString() : '0',
            price: onlinePrice || storePrice,
            flatPrice: onlinePrice || storePrice,
            storePrice: storePrice,
            onlinePrice: onlinePrice,
            image: product.image || product.preview || product.cover || product.thumb || '',
            coverImage: product.cover || product.preview || product.image || product.thumb || '',
            isVeg: product.item_type === 5,
            spiceLevel: product.spice_level || 0, // 0-5; 0 shows nothing
            genderCompatibility: product.gender_compatibility || null,
            ageGroup: product.age_group || null,
            brand: product.brand || null,
            color: product.color || null,
            description: product.description || '',
            variations: product.variations || {},
            extras: product.extras || [],
            addons: product.addons || [],

            // ✅ Combo Resolution: Use only the latest configuration if both exist
            ...(() => {
              const hasComponents = Array.isArray(product.combo_components) && product.combo_components.length > 0;
              const hasGroups = Array.isArray(product.combo_groups) && product.combo_groups.length > 0;

              let finalGroups = product.combo_groups || [];
              let finalComponents = product.combo_components || [];

              if (hasComponents && hasGroups) {
                const maxCompId = Math.max(...product.combo_components.map(c => parseInt(c.id || 0)));
                const maxGroupId = Math.max(...product.combo_groups.map(g => parseInt(g.id || 0)));

                if (maxGroupId >= maxCompId) {
                  finalComponents = [];
                } else {
                  finalGroups = [];
                }
              }

              return {
                _resolvedComboGroups: finalGroups,
                _resolvedComboComponents: finalComponents
              };
            })(),

            modifierGroups: (
              (product.modifierGroups && product.modifierGroups.length > 0) ? product.modifierGroups :
                (product.modifier_groups && product.modifier_groups.length > 0) ? product.modifier_groups :
                  ((Array.isArray(product.combo_groups) && product.combo_groups.length > 0 &&
                    (Array.isArray(product.combo_components) && product.combo_components.length > 0 ?
                      Math.max(...product.combo_groups.map(g => parseInt(g.id || 0))) >= Math.max(...product.combo_components.map(c => parseInt(c.id || 0))) : true))
                    ? product.combo_groups : [])
            ).map(group => {
              const mappedGroup = {
                ...group,
                min_selections: parseInt(group.min_selections || group.min_select || 0),
                max_selections: parseInt(group.max_selections || group.max_select || 0),
                modifiers: (group.modifiers || group.options || []).map(opt => ({
                  ...opt,
                  id: opt.id,
                  name: opt.name || opt.item_name || opt.display_label || 'Option',
                  price: parseFloat(opt.price || opt.extra_price || 0)
                }))
              };
              return mappedGroup;
            }),
            itemAttributes: product.itemAttributes || [],
            isCombo: !!product.is_combo,
            comboComponents: (Array.isArray(product.combo_components) && product.combo_components.length > 0 &&
              (Array.isArray(product.combo_groups) && product.combo_groups.length > 0 ?
                Math.max(...product.combo_components.map(c => parseInt(c.id || 0))) > Math.max(...product.combo_groups.map(g => parseInt(g.id || 0))) : true))
              ? product.combo_components : [],
            comboGroups: (Array.isArray(product.combo_groups) && product.combo_groups.length > 0 &&
              (Array.isArray(product.combo_components) && product.combo_components.length > 0 ?
                Math.max(...product.combo_groups.map(g => parseInt(g.id || 0))) >= Math.max(...product.combo_components.map(c => parseInt(c.id || 0))) : true))
              ? product.combo_groups : [],
            offer: product.offer || [],
            status: product.status,
            is_tracked: isTracked,
            available_stock: availableStock,
            is_available: isAvailable,
            inventory_checked: true,
            caution: product.caution || '',
            // ✅ Preserve weight-based item data
            unit_type: product.unit_type,
            unit: product.unit || (product.unit_type === 2 ? 'kg' : 'pc'),
            // Unit to append after the price on the card (e.g. "₹450.00/kg",
            // "₹40.00/pc") — only when the API actually gives a unit or the
            // item is weight-based; dish-priced items (no unit) show nothing.
            priceUnit: product.unit || (product.unit_type === 2 ? 'kg' : null),
            weight_config: product.weight_config || null,
            isWeightBased: product.unit_type === 2 || product.weight_config?.is_weight_based === true
          };

          // ✅ DEBUG: Log combo item processing
          if (processedItem.isCombo || (processedItem.modifierGroups && processedItem.modifierGroups.length > 0)) {
            console.log(`🎁 [Category Change] Normalized Combo: ${processedItem.name}`, {
              id: processedItem.id,
              isCombo: processedItem.isCombo,
              modifierGroups: processedItem.modifierGroups.length
            });
          }

          return processedItem;
        });

        console.log('✅ Loaded regular products:', processedProducts.length);
        setProducts(processedProducts);
      }

    } catch (error) {
      console.error('❌ Error changing category:', error);
      setProducts([]);
    } finally {
      setCategoryLoading(false); // ✅ Always hide loader when done
    }
  };

  // Applies a category clicked from a homepage category tile. Navigation
  // into this page can pass through the store-selector first, so the click
  // is carried via a one-shot sessionStorage flag rather than a prop —
  // purely additive, a no-op whenever nothing was clicked.
  const appliedPendingCategoryRef = useRef(false);
  useEffect(() => {
    if (appliedPendingCategoryRef.current) return;
    if (!categories || categories.length === 0) return;
    const pendingId = sessionStorage.getItem('pending_category_id');
    if (!pendingId) return;
    appliedPendingCategoryRef.current = true;
    sessionStorage.removeItem('pending_category_id');
    const match = categories.find(c => String(c.id) === String(pendingId));
    if (match) {
      handleCategoryChange(match.id);
      // sessionStorage is one-shot and already cleared above — sync the
      // real category into the URL so a refresh restores it from there.
      updateCategoryInUrl(match);
    }
  }, [categories]);

  // URL-driven category selection/persistence: reflects the selected
  // category in the route (/restaurant/:storeSlug/:categorySlug) so page
  // refresh, direct links, and browser back/forward all restore the same
  // filtered view. Runs once categories are loaded; "all" stays on the
  // base /restaurant/:storeSlug route with no extra segment.
  const appliedInitialUrlCategoryRef = useRef(false);
  useEffect(() => {
    if (appliedInitialUrlCategoryRef.current) return;
    if (!categories || categories.length === 0) return;
    appliedInitialUrlCategoryRef.current = true;
    if (!initialCategorySlug) return;
    const match = categories.find(c => c.slug === initialCategorySlug);
    if (match && match.id !== 'all') {
      handleCategoryChange(match.id);
    }
  }, [categories, initialCategorySlug]);

  const updateCategoryInUrl = (category) => {
    const pathStoreSlug = locationData.pathname.split('/restaurant/')[1]?.split('/')[0];
    if (!pathStoreSlug) return;
    const slug = category && category.id !== 'all' ? category.slug : null;
    const newPath = slug ? `/restaurant/${pathStoreSlug}/${slug}` : `/restaurant/${pathStoreSlug}`;
    if (newPath !== locationData.pathname) {
      navigate(newPath, { state: locationData.state, replace: false });
    }
  };

  // ✅ OPTIMIZED: categories and products are now fetched in parallel — products
  // no longer wait for categories or the offers API to resolve before their
  // own request is even sent (see productsPromise, kicked off immediately
  // below). Combo-offers is awaited from the single shared mount-effect
  // request (comboOffersPromiseRef) instead of firing its own duplicate call.
  const loadInitialData = async () => {
    try {
      setLoading(false); // remove full-screen loading flag; we will use categoryLoading instead
      setCategoryLoading(true); // show skeleton immediately so we don't flash "No menu items"

      const branchId = selectedStore?.id;
      console.log('Loading menu for store:', selectedStore?.name, 'Branch ID:', branchId);

      if (!branchId) {
        console.error('No branch ID available');
        return;
      }

      // ✅ OPTIMIZED: Use store data from props (already fetched by parent)
      setRestaurantInfo({
        name: selectedStore.name,
        branchName: selectedStore.city && selectedStore.state
          ? `${selectedStore.city}, ${selectedStore.state}`
          : selectedStore.location || ''
      });

      // ✅ PERF: Kick off the products fetch immediately (cache-checked, same
      // pattern as categories below) so it runs in parallel with categories
      // and offers instead of only starting after they resolve.
      const cachedProducts = apiCache.getMenuData(branchId);
      const productsPromise = cachedProducts
        ? Promise.resolve(cachedProducts)
        : fetchProducts().then(data => {
          apiCache.setMenuData(branchId, data);
          return data;
        });
      if (cachedProducts) {
        console.log('⚡ Using cached products:', cachedProducts.length);
      }

      // 1) Check cache for categories
      const cachedCategories = apiCache.getCategories();
      let categoriesData;

      if (cachedCategories) {
        categoriesData = cachedCategories;
        console.log('⚡ Using cached categories:', categoriesData.length);
      } else {
        categoriesData = await fetchCategories();
        apiCache.setCategories(categoriesData);
      }

      const filteredCategories = categoriesData.filter(cat =>
        !cat.name.toLowerCase().includes('all') && cat.name.toLowerCase() !== 'all items'
      );

      // Combo offers — await the single shared request from the mount effect
      // (lazily start it here too, in case this runs before that effect has).
      let currentCombos = comboOffers;
      if (currentCombos.length === 0) {
        if (!comboOffersPromiseRef.current) {
          comboOffersPromiseRef.current = fetchInitialOffers();
        }
        currentCombos = await comboOffersPromiseRef.current;
      }

      // ✅ NEW: Add Combo Offers category conditionally
      const processedCategories = [
        { id: 'all', name: 'All Items', slug: 'all', image: null, icon: getCategoryIcon('all') }
      ];

      if (currentCombos && currentCombos.length > 0) {
        processedCategories.push({ id: 'combo-offers', name: 'Combo Offers', slug: 'combo-offers', image: null, icon: '🎁' });
      }

      processedCategories.push(...filteredCategories.map(cat => ({
        ...cat,
        id: cat.id.toString(),
        name: cat.name,
        slug: cat.slug,
        image: cat.image,
        icon: getCategoryIcon(cat.name)
      })));

      setCategories(processedCategories);

      // 2) Products fetch was already kicked off above, in parallel with
      // categories/offers — just wait for it here.
      setCategoryLoading(true);
      const productsData = await productsPromise;

      console.log('📦 API returned products:', productsData.length);
      console.log('📦 Products with offers:',
        productsData.filter(p => p.offer && p.offer.length > 0).length
      );

      // Process products with stock data
      const processedProducts = productsData.map(product => {
        const isTracked = product.track_inventory || false;
        const availableStock = product.available_stock !== undefined ? product.available_stock : null;
        const isAvailable = !isTracked || (isTracked && availableStock > 0);

        const onlinePrice = (product.online_price && product.online_price !== '0.00') ? product.online_price : null;
        const storePrice = product.price;

        const processedItem = {
          ...product,
          id: product.id.toString(),
          name: product.name,
          category: product.item_category_id ? product.item_category_id.toString() : '0',
          price: onlinePrice || storePrice,
          flatPrice: onlinePrice || storePrice,
          storePrice: storePrice,
          onlinePrice: onlinePrice,
          coverImage: product.cover || product.preview || product.image || product.thumb || '',
          image: product.image || product.preview || product.cover || product.thumb || '',
          isVeg: product.item_type === 5,
          spiceLevel: product.spice_level || 0, // 0-5; 0 shows nothing
          genderCompatibility: product.gender_compatibility || null,
          ageGroup: product.age_group || null,
          brand: product.brand || null,
          color: product.color || null,
          description: product.description || '',
          variations: product.variations || {},
          extras: product.extras || [],
          addons: product.addons || [],

          // ✅ Combo Resolution: Use only the latest configuration if both exist
          ...(() => {
            const hasComponents = Array.isArray(product.combo_components) && product.combo_components.length > 0;
            const hasGroups = Array.isArray(product.combo_groups) && product.combo_groups.length > 0;

            let finalGroups = product.combo_groups || [];
            let finalComponents = product.combo_components || [];

            if (hasComponents && hasGroups) {
              const maxCompId = Math.max(...product.combo_components.map(c => parseInt(c.id || 0)));
              const maxGroupId = Math.max(...product.combo_groups.map(g => parseInt(g.id || 0)));

              if (maxGroupId >= maxCompId) {
                finalComponents = [];
              } else {
                finalGroups = [];
              }
            }

            return {
              _resolvedComboGroups: finalGroups,
              _resolvedComboComponents: finalComponents
            };
          })(),

          modifierGroups: (
            (product.modifierGroups && product.modifierGroups.length > 0) ? product.modifierGroups :
              (product.modifier_groups && product.modifier_groups.length > 0) ? product.modifier_groups :
                ((Array.isArray(product.combo_groups) && product.combo_groups.length > 0 &&
                  (Array.isArray(product.combo_components) && product.combo_components.length > 0 ?
                    Math.max(...product.combo_groups.map(g => parseInt(g.id || 0))) >= Math.max(...product.combo_components.map(c => parseInt(c.id || 0))) : true))
                  ? product.combo_groups : [])
          ).map(group => {
            const mappedGroup = {
              ...group,
              min_selections: parseInt(group.min_selections || group.min_select || 0),
              max_selections: parseInt(group.max_selections || group.max_select || 0),
              modifiers: (group.modifiers || group.options || []).map(opt => ({
                ...opt,
                id: opt.id,
                name: opt.name || opt.item_name || opt.display_label || 'Option',
                price: parseFloat(opt.price || opt.extra_price || 0)
              }))
            };
            return mappedGroup;
          }),
          itemAttributes: product.itemAttributes || [],
          isCombo: !!product.is_combo,
          comboComponents: (Array.isArray(product.combo_components) && product.combo_components.length > 0 &&
            (Array.isArray(product.combo_groups) && product.combo_groups.length > 0 ?
              Math.max(...product.combo_components.map(c => parseInt(c.id || 0))) > Math.max(...product.combo_groups.map(g => parseInt(g.id || 0))) : true))
            ? product.combo_components : [],
          comboGroups: (Array.isArray(product.combo_groups) && product.combo_groups.length > 0 &&
            (Array.isArray(product.combo_components) && product.combo_components.length > 0 ?
              Math.max(...product.combo_groups.map(g => parseInt(g.id || 0))) >= Math.max(...product.combo_components.map(c => parseInt(c.id || 0))) : true))
            ? product.combo_groups : [],
          offer: product.offer || [],
          status: product.status,
          is_tracked: isTracked,
          available_stock: availableStock,
          is_available: isAvailable,
          inventory_checked: true,
          caution: product.caution || '',
          // ✅ Preserve weight-based item data
          unit_type: product.unit_type,
          unit: product.unit || (product.unit_type === 2 ? 'kg' : 'pc'),
          // Unit to append after the price on the card (e.g. "₹450.00/kg",
          // "₹40.00/pc") — only when the API actually gives a unit or the
          // item is weight-based; dish-priced items (no unit) show nothing.
          priceUnit: product.unit || (product.unit_type === 2 ? 'kg' : null),
          weight_config: product.weight_config || null,
          isWeightBased: product.unit_type === 2 || product.weight_config?.is_weight_based === true
        };

        // ✅ DEBUG: Log combo item processing
        if (processedItem.isCombo || (processedItem.modifierGroups && processedItem.modifierGroups.length > 0)) {
          console.log(`🎁 Normalized Combo/Complex Item: ${processedItem.name}`, {
            id: processedItem.id,
            isCombo: processedItem.isCombo,
            modifierGroups: processedItem.modifierGroups.length,
            comboGroups: processedItem.comboGroups?.length || 0
          });
        }

        return processedItem;
      });
      // ✅ DEBUG: Check processed products
      console.log('✅ Processed products:', processedProducts.length);
      console.log('✅ Processed products with offers:',
        processedProducts.filter(p => p.offer && p.offer.length > 0).length
      );
      console.log('✅ Products with combo offers:',
        processedProducts.filter(p =>
          p.offer &&
          p.offer.some(o => o.name?.toLowerCase().includes('combo'))
        ).map(p => ({ id: p.id, name: p.name, offers: p.offer }))
      );

      // Hide categories with no item for THIS branch. /web/item-category is
      // tenant-wide (it ignores branch_id) while /web/item is scoped to the
      // branch's assigned outlet menu (or the master menu if it has none), so a
      // branch with a curated menu would otherwise show empty category tabs.
      // 'all' and 'combo-offers' are synthetic and always kept. Skipped when no
      // products came back, so a failed/empty fetch can't wipe every tab.
      if (processedProducts.length > 0) {
        const branchCategoryIds = new Set(processedProducts.map(p => String(p.category)));
        setCategories(prev => prev.filter(cat =>
          cat.id === 'all' || cat.id === 'combo-offers' || branchCategoryIds.has(String(cat.id))
        ));
      }

      setAllProducts(processedProducts);
      // ✅ Only set products to all-items if the user hasn't switched to another category
      if (activeCategoryRef.current === 'all') {
        setProducts(processedProducts);
      }
      setCategoryLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setCategoryLoading(false);
    }
  };


  // Handle editing customization from cart
  const handleEditCustomization = (cartItem) => {
    console.log('✏️ Editing cart item:', cartItem);

    // Find the original product from allProducts
    const originalProduct = allProducts.find(p => p.id === cartItem.id);

    if (!originalProduct) {

      return;
    }

    setProductToCustomize(originalProduct);
    setEditingCartItem(cartItem);
    setIsEditingCartItem(true);
    setShowCustomizeModal(true);
  };

  // ✅ FIX 4: Update handleCustomizedProductAdd function (around line 1080)
  const handleCustomizedProductAdd = (customizedProduct, isEditing = false) => {
    // ✅ CRITICAL: Reset the signature ref FIRST to force re-analysis
    lastAnalyzedCartSignatureRef.current = '';

    // ✅ STEP 1: Immediately generate the customization fingerprint (cartItemKey)
    // This is CRITICAL so that weight modal knows about these customizations
    const customizationKey = customizedProduct.cartItemKey || generateCartItemKey(customizedProduct);
    const preparedProduct = {
      ...customizedProduct,
      cartItemKey: customizationKey,
      isCustomized: true
    };

    console.log('🔄 handleCustomizedProductAdd - Prepared item:', preparedProduct.name, customizationKey);

    // ✅ NEW CASE: If the customized product is ALSO weight-based, we show the weight modal next...
    // EXCEPT if it already has variations (Variations take priority as per user request)
    const hasAnyVariations = Array.isArray(preparedProduct.variations) && preparedProduct.variations.length > 0;

    if (preparedProduct.isWeightBased && !preparedProduct.selectedWeight && !hasAnyVariations) {
      console.log('⚖️ Customizable + Weight-based item - showing weight picker next');

      // ✅ Close customization modal immediately to prevent double-popups
      setShowCustomizeModal(false);
      setProductToCustomize(null);

      // Delay weight modal slightly for smooth transition
      setTimeout(() => {
        setWeightProduct(preparedProduct);
        setShowWeightModal(true);
      }, 300);

      return;
    }

    // ✅ Check authentication for new items (not when editing) — moved here
    // so it only gates the final cart write, after customization (and any
    // weight chaining above) is already resolved, matching
    // useSpecialtyCart's pattern. Preserve the fully-prepared item so
    // handleAuthSuccess can resume this exact add once login succeeds.
    if (!isEditing) {
      const customerId = getCustomerId();
      if (!customerId) {
        console.log('🔒 User not logged in - showing auth modal');
        setPendingProduct({ type: 'customized', product: preparedProduct });
        savePendingCartAction('customized', preparedProduct);
        setShowAuthModal(true);
        return;
      }
    }

    if (isEditing) {
      // EDIT MODE: Replace the existing item
      setCart(prevCart =>
        prevCart.map(item =>
          item.cartItemKey === customizedProduct.cartItemKey
            ? customizedProduct
            : item
        )
      );
      console.log('✅ Updated cart item');
    } else {
      // ADD MODE: Check if same customization exists
      const existingItem = cart.find(item =>
        areCartItemsIdentical(item, customizedProduct)
      );

      if (existingItem) {
        // Same customization - increase quantity
        setCart(prevCart =>
          prevCart.map(item =>
            areCartItemsIdentical(item, customizedProduct)
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        );
        console.log('✅ Increased quantity of existing customized item');
      } else {
        // New customization - add as new item
        const newItem = {
          ...customizedProduct,
          cartItemKey: customizedProduct.cartItemKey || generateCartItemKey(customizedProduct)
        };

        // ✅ DEBUG: Verify flatPrice is preserved in cart item
        console.group('🛒 ADDING CUSTOMIZED ITEM TO CART:');
        console.log('Item being added:', {
          name: newItem.name,
          price: newItem.price,
          flatPrice: newItem.flatPrice,
          variations: newItem.variations?.length || 0,
          extras: newItem.extras?.length || 0,
          addons: newItem.addons?.length || 0,
          cartItemKey: newItem.cartItemKey
        });
        console.groupEnd();

        setCart(prevCart => [...prevCart, newItem]);
        console.log('✅ Added new customized item');
      }
    }

    // ✅ CRITICAL FIX: Force cartAnalysis to trigger by incrementing version
    // This ensures offers button shows after customized product is added
    setOfferStateVersion(v => v + 1);

    setShowCustomizeModal(false);
    setProductToCustomize(null);
    setIsEditingCartItem(false);
    setEditingCartItem(null);
  };
  // ✅ PERF: fetchActiveOffers used to make its own call to /web/offers/active
  // on mount, and fetchComboOffers (below) made a SECOND call to the exact
  // same endpoint right alongside it (plus a THIRD, from loadInitialData) —
  // three concurrent requests for identical data. This single fetch replaces
  // the mount-time pair; activeOffers and comboOffers are both derived from
  // the one response. fetchComboOffers itself is left as-is below since it's
  // still used on demand when the user switches to the Combo Offers tab.
  const fetchInitialOffers = async () => {
    try {
      const { data } = await apiClient.get(API_ENDPOINTS.activeOffers, {
        params: { branch_id: getBranchId() }
      });

      const offers = (data.status && data.data) ? data.data : [];
      setActiveOffers(offers);

      // ✅ Store active offers in OffersManager for stackability reference
      offersManager.setActiveOffers(offers);

      const combos = offers.filter(offer => offer.offer_type === 'combo');
      setComboOffers(combos);

      console.log('✅ Active offers loaded:', offers.length, '(combos:', combos.length, ')');
      return combos;
    } catch (error) {
      console.error('❌ Error fetching offers:', error);
      setActiveOffers([]);
      setComboOffers([]);
      return [];
    }
  };
  const fetchComboOffers = async () => {
    try {
      console.log('🎁 Fetching combo offers for branch:', getBranchId());

      const { data } = await apiClient.get(API_ENDPOINTS.activeOffers, {
        params: { branch_id: getBranchId() }
      });

      console.log('📊 Combo API Response:', data);

      if (data.status && data.data) {
        const combos = data.data.filter(offer => offer.offer_type === 'combo');
        console.log('✅ Combo offers found:', combos.length);
        setComboOffers(combos);
        // ✅ Return combos so callers can decide what to do with the data
        return combos;
      }
      setComboOffers([]);
      return [];
    } catch (error) {
      console.error('❌ Error fetching combo offers:', error);
      setComboOffers([]);
      return [];
    }
  };



  // Fetch available offers based on current cart
  const fetchAvailableOffers = async () => {
    if (cart.length === 0) {
      setAvailableOffers(null);
      return;
    }
    // ✅ Skip if user not logged in
    const customerId = getCustomerId();
    if (!customerId) {
      console.log('⚠️ Skipping available offers - user not logged in');
      setAvailableOffers(null);
      return;
    }
    try {
      const cartSubtotal = cart.reduce((sum, item) =>
        sum + (parseFloat(item.flatPrice || item.price) * item.quantity), 0
      );

      // ✅ BUILD PAYLOAD WITH subtotal (not cart_subtotal)
      const payload = {
        branch_id: getBranchId(),
        subtotal: parseFloat(cartSubtotal.toFixed(2)), // ✅ Changed from cart_subtotal to subtotal
        items: cart
          .filter(item => !item.isCombo && !item.isComboOffer) // ✅ Exclude combo items
          .map(item => {
            const variations = Array.isArray(item.variations) ? item.variations : [];
            const extras = Array.isArray(item.extras) ? item.extras : [];

            const basePrice = Number(item.flatPrice || item.price || 0);

            const variationTotal = variations.reduce(
              (sum, v) => sum + Number(v.variation_price || v.price || 0),
              0
            );

            const extraTotal = extras.reduce(
              (sum, e) => sum + Number(e.extra_price || e.price || 0),
              0
            );

            const totalPrice = (basePrice + variationTotal + extraTotal) * Number(item.quantity);

            return {
              item_id: parseInt(item.id),
              quantity: parseInt(item.quantity),
              total_price: Number(totalPrice.toFixed(2))
            };
          })
      };

      console.log('📊 Fetching available offers with payload:', payload);

      const { data } = await apiClient.post(API_ENDPOINTS.availableOffers, payload);

      if (data.status && data.data) {
        setAvailableOffers(data.data);
        console.log('✅ Available offers loaded:', data.data);
      }
    } catch (error) {
      console.error('❌ Error fetching available offers:', error);
      setAvailableOffers(null);
    }
  };

  // Replace around line 1400 (after all helper functions are defined)
  // ============================================
  // COMPLETE FIXED addToCart FUNCTION
  // Instructions:
  // 1. Search your file for "const addToCart = async (product)"
  // 2. Delete the ENTIRE old function (everything until the closing brace)
  // 3. Replace it with THIS complete function below
  // ============================================

  // ============================================
  // REPLACE YOUR addToCart FUNCTION (around line 1400)
  // ============================================

  // ✅ Handle confirmed weight selection from WeightSelectionModal
  const handleWeightConfirm = (weightedProduct) => {
    console.log('⚖️ Weight confirmed:', {
      name: weightedProduct.name,
      weight: weightedProduct.selectedWeight,
      unit: weightedProduct.weightUnit,
      flatPrice: weightedProduct.flatPrice,
      cartItemKey: weightedProduct.cartItemKey
    });

    // Close modal
    setShowWeightModal(false);
    setWeightProduct(null);

    // ✅ Weight selection can now be made before login (see the addToCart /
    // handleCustomizedProductAdd reordering above) — this is the terminal
    // commit point for every weight-based item regardless of how it got
    // here (direct weight item, or customization chained into weight), so
    // it needs its own login gate, preserving the selected weight so login
    // can resume this exact add.
    const customerId = getCustomerId();
    if (!customerId) {
      console.log('🔒 User not logged in - showing auth modal');
      setPendingProduct({ type: 'weighted', product: weightedProduct });
      savePendingCartAction('weighted', weightedProduct);
      setShowAuthModal(true);
      return;
    }

    // ✅ CRITICAL: Reset the signature ref to force re-analysis
    lastAnalyzedCartSignatureRef.current = '';

    // Add the weighted product directly to the cart
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.cartItemKey === weightedProduct.cartItemKey);

      if (existingItem) {
        // Same weight exists - increase quantity
        console.log(`✅ Increasing quantity of weight-based item: ${weightedProduct.weightLabel}`);
        return prevCart.map(item =>
          item.cartItemKey === weightedProduct.cartItemKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // New weight selection - add as new item
        console.log(`✅ Adding weight-based item: ${weightedProduct.weightLabel}`);
        return [...prevCart, weightedProduct];
      }
    });

    console.log('✅ Weight-based item added to cart successfully');
  };

  const addToCart = async (product) => {
    // ✅ Step 1.4: Block entirely if the store is currently closed — checked
    // first, before customization/combo/weight picker, so a closed store
    // blocks the whole add flow rather than letting the user pick options
    // for an item that can't actually be added.
    if (!guardStoreOpenAction()) {
      return;
    }

    // ✅ Step 1.5: Check for customization FIRST if both are needed
    // This allows Customization -> Weight chained flow
    const hasModifierGroups = Array.isArray(product.modifierGroups) && product.modifierGroups.length > 0;
    const hasComboGroups = Array.isArray(product.comboGroups) && product.comboGroups.length > 0;

    const needsCustomization = (product.variations && Object.keys(product.variations).length > 0) ||
      (product.extras && product.extras.length > 0) ||
      (product.addons && product.addons.length > 0) ||
      hasModifierGroups || hasComboGroups;

    const isAlreadyCustomized = product.isCustomized || !!product.cartItemKey;

    console.log('🎨 addToCart customization check:', {
      name: product.name,
      needsCustomization,
      isAlreadyCustomized,
      hasModifierGroups,
      hasComboGroups
    });

    if (needsCustomization && !isAlreadyCustomized) {
      console.log('🎨 Product needs customization first:', product.name);
      setProductToCustomize(product);
      setShowCustomizeModal(true);
      return;
    }

    // ✅ Step 1.6: Check for Combo with fixed components (ONLY if no customization needed)
    if (product.isCombo && !isAlreadyCustomized && product.comboComponents && product.comboComponents.length > 0) {
      console.log('🎁 Fixed Combo detected - showing details modal:', product.name);
      setComboToDisplay(product);
      setShowComboModal(true);
      return;
    }

    // ✅ Step 1.7: Check for weight
    // Prioritize variations: if it has variations defined, customization modal already handled it (or will)
    // and we should NOT show the weight picker if it's already customized with variations.
    const hasSelectedVariations = Array.isArray(product.variations) && product.variations.length > 0;

    if (product.isWeightBased && !product.selectedWeight && !hasSelectedVariations) {
      console.log('⚖️ Weight-based item detected - showing weight picker:', product.name);
      // WeightSelectionModal builds its cart key as `${product.cartItemKey ||
      // product.id}|w:${weight}` — for this plain (non-customized) weight
      // item that base key must always be product.id, so a second add of
      // the same weight matches and increments instead of duplicating.
      // Pass a clean object rather than trusting product.cartItemKey to
      // already be empty here.
      setWeightProduct({ ...product, cartItemKey: undefined });
      setShowWeightModal(true);
      return;
    }

    // ✅ Step 1.8: Only now — once any required customization/combo/weight
    // selection above is already resolved — gate on login. Mirrors
    // useSpecialtyCart's working pattern (selection happens regardless of
    // login state; login only blocks the final cart write) instead of the
    // old position at the very top of this function, which blocked the
    // customization/combo/weight modals from ever opening for a logged-out
    // user and silently dropped the product. Preserving it here lets
    // handleAuthSuccess resume this exact add once login succeeds.
    const customerId = getCustomerId();
    if (!customerId) {
      console.log('🔒 User not logged in - showing auth modal');
      setPendingProduct({ type: 'simple', product });
      savePendingCartAction('simple', product);
      setShowAuthModal(true);
      return;
    }

    console.log('🛒 addToCart called:', {
      name: product.name,
      isCombo: product.isCombo,
      hasKey: !!product.cartItemKey,
      customerId: customerId
    });

    // ✅ Step 2: Handle combo products differently
    if (product.isCombo && product.comboData) {
      console.log('🎁 Adding combo as single item:', product.name);

      const comboCartItem = {
        ...product,
        quantity: 1,
        isCombo: true,
        comboItems: product.comboData.combo_items_details || [],
        flatPrice: parseFloat(product.comboData.combo_price || product.price),
        cartItemKey: `combo-${product.id}`,
        comboOfferId: product.comboData.id,
        comboOfferName: product.comboData.name,
        isComboOffer: true
      };

      setCart(prevCart => {
        const existing = prevCart.find(item => item.cartItemKey === comboCartItem.cartItemKey);

        if (existing) {
          return prevCart.map(item =>
            item.cartItemKey === comboCartItem.cartItemKey
              ? { ...item, quantity: item.quantity + 1 }
              : item
          );
        } else {
          return [...prevCart, comboCartItem];
        }
      });

      console.log('✅ Combo added to cart successfully');
      return;
    }

    // ✅ Step 3: Normalize regular product
    const normalizedProduct = {
      ...product,
      variations: Array.isArray(product.variations) ? product.variations : [],
      extras: Array.isArray(product.extras) ? product.extras : [],
      addons: Array.isArray(product.addons) ? product.addons : [],
      quantity: product.quantity || 1
    };

    if (!normalizedProduct.cartItemKey) {
      normalizedProduct.cartItemKey = generateCartItemKey(normalizedProduct);
    }

    console.log('📦 Normalized product:', {
      name: normalizedProduct.name,
      key: normalizedProduct.cartItemKey,
      quantity: normalizedProduct.quantity
    });

    // ✅ Step 4: Check static stock status
    if (settingsService.isInventoryCheck() && normalizedProduct.is_tracked && !normalizedProduct.is_available) {
      alert(`❌ ${normalizedProduct.name} is currently out of stock`);
      return;
    }

    // ✅ Step 5: Check inventory BEFORE updating cart
    try {
      // Get the current cart state
      let currentCart = [];
      setCart(prev => {
        currentCart = prev;
        return prev;
      });
      const existingItem = await new Promise(resolve => {
        setCart(prevCart => {
          const found = prevCart.find(item =>
            item.cartItemKey === normalizedProduct.cartItemKey
          );
          resolve(found);
          return prevCart;
        });
      });
      const currentQtyInCart = existingItem ? existingItem.quantity : 0;
      const quantityToAdd = normalizedProduct.quantity;
      const newTotalQuantity = currentQtyInCart + quantityToAdd;

      console.log(`📞 Checking inventory: ${normalizedProduct.name}`, {
        current: currentQtyInCart,
        adding: quantityToAdd,
        total: newTotalQuantity
      });

      const availabilityCheck = await checkInventoryAvailability(
        normalizedProduct.id,
        newTotalQuantity,
        normalizedProduct.available_stock  // Pass the real stock from /web/item
      );

      console.log('📊 Inventory check result:', availabilityCheck);

      if (availabilityCheck.canAdd === false) {
        // Use the real available_stock from /web/item, not check-item's available_quantity
        const availableQty = availabilityCheck.available_quantity ?? normalizedProduct.available_stock ?? 0;
        alert(`❌ Only ${availableQty} of ${normalizedProduct.name} available. You have ${currentQtyInCart} in your cart.`);
        return;
      }

      // ✅ Step 6: Inventory OK - Now update cart
      setCart(prevCart => {
        const existingItem = prevCart.find(item =>
          item.cartItemKey === normalizedProduct.cartItemKey
        );

        if (existingItem) {
          console.log(`✅ Updating quantity: ${existingItem.name}, ${existingItem.quantity} → ${newTotalQuantity}`);

          return prevCart.map(item =>
            item.cartItemKey === normalizedProduct.cartItemKey
              ? { ...item, quantity: newTotalQuantity }
              : item
          );
        } else {
          console.log(`✅ Adding new item: ${normalizedProduct.name}, qty: ${quantityToAdd}`);

          const newItem = {
            ...normalizedProduct,
            quantity: quantityToAdd,
            customizations: {},
            // ✅ CRITICAL FIX: Preserve pre-calculated flatPrice from customizations
            // Only use base price if flatPrice wasn't already calculated
            flatPrice: normalizedProduct.flatPrice && parseFloat(normalizedProduct.flatPrice) > parseFloat(normalizedProduct.price || 0)
              ? parseFloat(normalizedProduct.flatPrice)
              : parseFloat(normalizedProduct.price || normalizedProduct.flatPrice || 0)
          };

          console.log('📦 Final item being added to cart:', {
            name: newItem.name,
            flatPrice: newItem.flatPrice,
            hasVariations: Array.isArray(newItem.variations) && newItem.variations.length > 0,
            hasExtras: Array.isArray(newItem.extras) && newItem.extras.length > 0,
            hasAddons: Array.isArray(newItem.addons) && newItem.addons.length > 0
          });

          return [...prevCart, newItem];
        }
      });

      console.log('✅ Item successfully added to cart');

    } catch (error) {
      console.error('❌ Error during add to cart:', error);
      alert('Unable to verify stock availability. Please try again.');
    }
  };

  // ✅ STEP 1: Normalize the product (ensure all arrays exist)


  // ✅ STEP 5: Async inventory check (after state update)
  // This runs after the cart is updated


  // ============================================
  // UPDATED updateCartQuantity FUNCTION
  // Replace your existing updateCartQuantity function around line 775-785
  // ============================================

  const updateCartQuantity = async (cartItemKey, delta) => {
    console.log('🔄 Updating cart quantity:', cartItemKey, delta);

    // ✅ Block quantity changes (both directions) while the store is closed
    // and doesn't support pre-order — same guard used by Add to Cart/Cart-open.
    if (!guardStoreOpenAction({ allowIfPreOrderSupported: true })) {
      return;
    }

    // ✅ If decreasing quantity or removing, no need to check inventory
    if (delta < 0) {
      setCart(prevCart => prevCart.map(cartItem => {
        if (cartItem.cartItemKey === cartItemKey) {
          const newQuantity = cartItem.quantity + delta;
          const updated = newQuantity > 0
            ? { ...cartItem, quantity: newQuantity }
            : null;
          return updated;
        }
        return cartItem;
      }).filter(Boolean));
      return;
    }

    // ✅ If increasing quantity, use Optimistic Updates for instant UI response
    if (delta > 0) {
      const currentItem = cart.find(i => i.cartItemKey === cartItemKey);
      if (!currentItem) return;

      const newQuantity = currentItem.quantity + delta;

      // 🚀 Optimistically update the UI instantly so clicks don't feel laggy
      setCart(prevCart => prevCart.map(cartItem =>
        cartItem.cartItemKey === cartItemKey
          ? { ...cartItem, quantity: cartItem.quantity + delta }
          : cartItem
      ));

      if (currentItem.isCombo || currentItem.isComboOffer) {
        console.log('🎁 Combo item - skipping inventory check (optimistic update finished)');
        return;
      }

      console.log(`📞 Checking inventory asynchronously for ${currentItem.name} - new qty: ${newQuantity}`);

      // Look up the real available_stock from the products list (/web/item data)
      const productData = products.find(p => p.id === currentItem.id || p.id === currentItem.id?.toString());
      const knownStock = productData?.available_stock ?? currentItem.available_stock;

      try {
        const availabilityCheck = await checkInventoryAvailability(
          currentItem.id,
          newQuantity,
          knownStock  // Pass real stock from /web/item
        );
        console.log('📊 Quantity update availability check:', availabilityCheck);

        if (availabilityCheck.canAdd === false) {
          // 🔙 Revert the optimistic update if inventory checking says NO
          setCart(prevCart => prevCart.map(cartItem =>
            cartItem.cartItemKey === cartItemKey
              ? { ...cartItem, quantity: cartItem.quantity - delta }
              : cartItem
          ));

          // Use the real stock from /web/item, not check-item's available_quantity: 0
          const availableQty = availabilityCheck.available_quantity ?? knownStock ?? 0;
          alert(`❌ Only ${availableQty} of ${currentItem.name} available.`);
        }
      } catch (error) {
        console.error('❌ Error during async inventory check:', error);
        // 🔙 Revert on network error
        setCart(prevCart => prevCart.map(cartItem =>
          cartItem.cartItemKey === cartItemKey
            ? { ...cartItem, quantity: cartItem.quantity - delta }
            : cartItem
        ));
      }
    }
  };

  const checkCartInventory = async (cartItems) => {
    console.log('🔍 Checking inventory for entire cart...');

    // ✅ Filter out combo items before checking inventory
    const regularItems = cartItems.filter(item => !item.isCombo && !item.isComboOffer);

    if (regularItems.length === 0) {
      console.log('✅ Cart contains only combo items - no inventory check needed');
      return true;
    }

    const checks = await Promise.all(
      regularItems.map(item =>
        checkInventoryAvailability(item.id, item.quantity)
      )
    );
    const unavailableItems = checks
      .map((check, index) => ({
        check,
        item: regularItems[index]
      }))
      .filter(({ check }) => check.canAdd === false);

    if (unavailableItems.length > 0) {
      const messages = unavailableItems.map(({ check, item }) =>
        `• ${item.name}: ${check.available_quantity !== undefined ? check.available_quantity : 0} available (you have ${item.quantity})`
      );

      alert(`The following items in your cart are low on stock or out of stock:\n\n${messages.join('\n')}\n\nPlease adjust your quantities to proceed.`);
      return false;
    }

    console.log('✅ All items available');
    return true;
  };

  const removeFromCart = (cartItemKey) => {
    setCart(cart.filter(item => item.cartItemKey !== cartItemKey));
  };
  const clearCart = () => {
    setCart([]);
    setCartAnalysis(null);
    setCouponCode('');
    setSelectedCollectionTime('');
    setSelectedAddress(null);

    // Clear offers
    offersManager.clearOffers();

    // Clear storage
    if (selectedStore && orderDetails) {
      const storeId = selectedStore.id;
      const orderType = orderDetails.type;
      clearCartFromStorage(storeId, orderType);
    }
  };
  // ✅ 2. FIX: getTotalAmount function (around line 926)
  // ✅ MEMOIZED - Only recalculates when cart changes
  // ✅ Depends on offers
  const analyzedCartStatus = useMemo(() => {
    return cartAnalysis?.universal_response?.cart_status || null;
  }, [cartAnalysis]);



  const analyzedDeliveryCharge = useMemo(() => {
    // ShipRocket rate takes priority when serviceable
    if (cartAnalysis?.shipping?.serviceable === true &&
      cartAnalysis?.shipping?.rate != null) {
      return Number(cartAnalysis.shipping.rate);
    }

    const candidates = [
      analyzedCartStatus?.delivery_charge,
      analyzedCartStatus?.delivery_fee,
      analyzedCartStatus?.shipping_charge,
      cartAnalysis?.delivery_charge,
      cartAnalysis?.delivery_fee,
      cartAnalysis?.shipping_charge
    ];

    for (const value of candidates) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return null;
  }, [analyzedCartStatus, cartAnalysis]);

  // ✅ FIXED: Manual distance calculation is commented out.
  // Since we rely entirely on the API (Shiprocket or backend defaults),
  // calculating distance is inefficient and irrelevant for national shipping.
  /*
  const distanceBasedDeliveryCharge = useMemo(() => {
    const orderType = orderDetails?.type || 'delivery';
    if (orderType === 'collection') return 0;

    const basicCharge = settingsService.getBasicDeliveryCharge();
    const targetAddress = selectedAddress;

    if (selectedStore?.latitude && selectedStore?.longitude &&
        targetAddress?.latitude && targetAddress?.longitude) {
      const dist = calculateDistance(
        parseFloat(selectedStore.latitude),
        parseFloat(selectedStore.longitude),
        parseFloat(targetAddress.latitude),
        parseFloat(targetAddress.longitude)
      );
      const freeKm = settingsService.getFreeDeliveryKilometer();
      if (dist <= freeKm) return 0;
      const perKm = settingsService.getChargePerKilo();
      return basicCharge + ((dist - freeKm) * perKm);
    }
    return basicCharge;
  }, [orderDetails?.type, selectedStore, selectedAddress]);
  */

  // If the API returns a rate (even 0), use it. Otherwise, assume 0.
  const effectiveDeliveryCharge = Number.isFinite(analyzedDeliveryCharge)
    ? analyzedDeliveryCharge
    : 0; // Fallback to 0 instead of manual calculation

  const rawCartSubtotal = useMemo(() => {
    if (cart.length === 0) return 0;

    return cart.reduce((sum, item) => {
      const itemPrice = calculateItemPrice(item);
      const itemTotal = itemPrice * Number(item.quantity);
      return sum + itemTotal;
    }, 0);
  }, [cart]);

  const getTotalAmount = useMemo(() => {
    const analyzedSubtotal = Number(analyzedCartStatus?.subtotal);
    if (Number.isFinite(analyzedSubtotal)) {
      return analyzedSubtotal.toFixed(2);
    }

    return rawCartSubtotal.toFixed(2);
  }, [analyzedCartStatus, rawCartSubtotal]);

  const getTotalSavings = useMemo(() => {
    const savings = offersManager.getTotalSavings();
    return savings.toFixed(2);
  }, [offersManager, offerStateVersion]);

  const getFinalTotal = useMemo(() => {
    const analyzedTaxTotal = Number(analyzedCartStatus?.tax_total);
    const analyzedGrandTotal = Number(analyzedCartStatus?.grand_total);
    const subtotal = Number(parseFloat(getTotalAmount) || 0);
    const taxTotal = Number.isFinite(analyzedTaxTotal) ? analyzedTaxTotal : 0;
    const deliveryCharge = orderDetails?.type === 'collection' || CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW ? 0 : effectiveDeliveryCharge;
    const discount = Number(parseFloat(getTotalSavings) || 0);
    const freeItemsValue = Number(offersManager.getFreeItemsValue() || 0);
    const localOfferSavings = Math.max(0, discount + freeItemsValue);

    if (Number.isFinite(analyzedGrandTotal)) {
      const analyzedTotalWithDelivery = Math.max(0, analyzedGrandTotal + deliveryCharge);
      const rawNoDiscountTotal = rawCartSubtotal + taxTotal + deliveryCharge;
      const savingsAlreadyAppliedByApi = Math.max(0, rawNoDiscountTotal - analyzedTotalWithDelivery);
      const remainingSavingsToApply = Math.max(0, localOfferSavings - savingsAlreadyAppliedByApi);

      return Math.max(0, analyzedTotalWithDelivery - remainingSavingsToApply).toFixed(2);
    }

    const rowTotalBeforeSavings = subtotal + taxTotal + deliveryCharge;
    return Math.max(0, rowTotalBeforeSavings - localOfferSavings).toFixed(2);
  }, [analyzedCartStatus, getTotalAmount, getTotalSavings, orderDetails?.type, effectiveDeliveryCharge, offersManager, offerStateVersion, rawCartSubtotal]);

  const getFreeItemsValue = useMemo(() => {
    const freeValue = offersManager.getFreeItemsValue();
    return freeValue.toFixed(2);
  }, [offersManager]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      console.warn('Please enter a coupon code');
      return;
    }

    try {
      const branchId = getBranchId();
      const customerId = getCustomerId();

      console.log('🎟️ Applying coupon:', couponCode.trim());

      const result = await offersManager.validateAndApplyCoupon(
        couponCode.trim(),
        cart,
        branchId,
        customerId
      );

      if (result.success) {
        console.log('✅ Coupon applied successfully');

        // Show success message
        alert(`✅ ${result.message}\n\n${result.offer.discount_amount > 0
          ? `💰 Discount: ₹${result.offer.discount_amount.toFixed(2)}\n`
          : ''
          }${result.offer.free_items?.length > 0
            ? `🎁 Free Items: ${result.offer.free_items.length} item(s)`
            : ''
          }`);

        // Clear coupon input and close modal
        setCouponCode('');
        setShowOffersModal(false);
      } else {
        console.warn('⚠️ Coupon validation failed:', result.message);

      }
    } catch (error) {
      console.error('❌ Coupon application error:', error);

    }
  };

  // ✅ 1. STRIPE PAYMENT HANDLER - Don't clear cart before redirect

  const handleStripePayment = async (checkout, orderId) => {
    try {

      const checkoutUrl = checkout.checkout_url || checkout.url || checkout.data?.checkout_url;

      if (!checkoutUrl) {
        console.error('❌ Stripe checkout URL not found in response:', checkout);
        alert('Payment Error: Stripe checkout URL not returned by server.');
        throw new Error('Stripe checkout URL not provided by the server');
      }



      // Instead of closing modals first (which might unmount and cause issues),
      // we navigate right away.
      window.location.href = checkoutUrl;

      // Give browser time to navigate before we start cleaning up UI
      setTimeout(() => {
        setShowPaymentModal(false);
        setShowCart(false);
      }, 1000);

    } catch (error) {
      console.error('❌ Error inside handleStripePayment:', error);
      alert('Failed to redirect to Stripe: ' + error.message);
      throw error;
    }
  };

  // ✅ 2. RAZORPAY PAYMENT HANDLER - Don't clear cart before opening modal
  const handleRazorpayPayment = async (checkout, orderId) => {
    const orderSerial = checkout.order_serial_no || orderId;

    if (typeof window.Razorpay === 'undefined') {
      await loadRazorpayScript();
    }

    if (typeof window.Razorpay === 'undefined') {
      alert('Payment gateway failed to load. Please check your internet connection and try again.');
      setProcessingPayment(false);
      return;
    }

    const options = {
      key: checkout.razorpay_key,
      amount: checkout.amount,
      currency: checkout.currency || 'INR',
      name: restaurantInfo?.name || 'Restaurant',
      description: `Order #${orderSerial}`,
      order_id: checkout.session_id,
      prefill: {
        name: checkout.customer_name,
        email: checkout.customer_email,
        contact: checkout.customer_phone
      },
      theme: {
        color: 'var(--color-primary, #ff6b35)'
      },
      handler: async function (response) {
        // ✅ Payment completed. Verify immediately via a signature-checked
        // call instead of waiting on Razorpay's async webhook — this is
        // cryptographically equivalent to webhook verification (same HMAC
        // signature, same account secret), just synchronous, so it doesn't
        // depend on the webhook's own delivery/configuration. Best-effort:
        // if this call fails for any reason, PaymentSuccess's own polling
        // (backed by the webhook, once that lands) is still the fallback —
        // we still redirect either way.
        console.log('✅ Razorpay payment success:', response.razorpay_payment_id);
        try {
          await apiClient.post('/web/order/verify-razorpay-payment', {
            order_serial_no: orderSerial,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          }, { skipAuth: true });
        } catch (verifyError) {
          console.warn('⚠️ Immediate payment verification failed — falling back to webhook/polling:', verifyError);
        }
        const successUrl = `/payment/success?gateway=razorpay&order=${orderSerial}&payment_id=${response.razorpay_payment_id}`;
        window.location.href = successUrl;
      },
      modal: {
        ondismiss: function () {
          // ✅ User closed Razorpay modal without completing payment
          // Redirect to cancel page so user gets clear feedback
          console.log('⚠️ Razorpay modal dismissed by user');
          setProcessingPayment(false);
          window.location.href = `/payment/cancel?gateway=razorpay&order=${orderSerial}&reason=dismissed`;
        }
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        console.error('❌ Razorpay payment failed:', response.error?.description);
        setProcessingPayment(false);
        window.location.href = `/payment/cancel?gateway=razorpay&order=${orderSerial}&reason=${encodeURIComponent(response.error?.description || 'payment_failed')}`;
      });
      razorpay.open();
      setShowPaymentModal(false);
    } catch (error) {
      console.error('❌ Error opening Razorpay:', error);
      alert('Failed to open payment gateway. Please try again.');
      setProcessingPayment(false);
    }
  };

  // ============================================
  // ALSO ADD: PaymentSuccess page should clear cart on mount
  // Create or update your PaymentSuccess component
  // ============================================




  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.Razorpay) {
        console.log('✅ Razorpay already loaded');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        console.log('✅ Razorpay SDK loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('❌ Failed to load Razorpay SDK');
        reject(new Error('Failed to load Razorpay SDK'));
      };
      document.body.appendChild(script);
    });
  };

  const loadCashfreeSDK = async () => {
    return new Promise((resolve, reject) => {
      if (window.Cashfree) {
        console.log('✅ Cashfree SDK already loaded');
        resolve();
        return;
      }

      console.log('📦 Loading Cashfree SDK...');

      const script = document.createElement('script');
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      script.async = true;

      script.onload = () => {
        console.log('✅ Cashfree SDK script loaded');

        // Wait for Cashfree object to be available
        const checkSDK = setInterval(() => {
          if (window.Cashfree) {
            clearInterval(checkSDK);
            console.log('✅ Cashfree SDK ready');
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkSDK);
          if (!window.Cashfree) {
            reject(new Error('Cashfree SDK failed to initialize'));
          }
        }, 10000);
      };

      script.onerror = () => {
        console.error('❌ Failed to load Cashfree SDK');
        reject(new Error('Failed to load Cashfree SDK'));
      };

      document.body.appendChild(script);
    });
  };


  // ✅ 3. CASHFREE PAYMENT HANDLER - Don't clear cart before redirect
  const handleCashfreePayment = async (checkout, orderId) => {
    const orderSerial = checkout.order_serial_no || checkout.session_id || orderId;

    try {
      // ✅ Use checkout_url directly (simplest approach)
      if (checkout.checkout_url) {

        const checkoutUrl = checkout.checkout_url;

        // Validate it's a Cashfree URL
        if (!checkoutUrl.includes('cashfree.com')) {
          throw new Error('Invalid Cashfree checkout URL: ' + checkoutUrl);
        }

        // Use a unified /payment/result handler instead of /payment/success directly.
        // Cashfree's old SDK sends ALL outcomes (success, fail, cancelled) to the same
        // returnUrl with txStatus appended. /payment/result reads txStatus and routes
        // to /payment/success or /payment/cancel accordingly.
        // Use orderId (integer DB id) so the backend cancel API can find the order.
        const returnUrl = `${window.location.origin}/payment/result?gateway=cashfree&order=${orderId}`;
        const separator = checkoutUrl.includes('?') ? '&' : '?';
        const finalUrl = `${checkoutUrl}${separator}return_url=${encodeURIComponent(returnUrl)}`;

        console.log(`🔗 [Cashfree] Redirecting to checkout for order: ${orderSerial}`);

        // ❌ REMOVED: Cart clearing before redirect
        // Cart will be cleared by PaymentSuccess page after successful payment

        // ❌ REMOVED: setShowPaymentModal(false) - closing modal here triggers
        // window.history.back() from useEffect cleanup, which cancels the redirect!

        // Redirect to Cashfree
        window.location.href = finalUrl;
        return;
      }

      // FALLBACK: Use SDK if checkout_url not available

      await loadCashfreeSDK();

      let paymentSessionId = null;

      if (checkout.payment_session_id) {
        paymentSessionId = checkout.payment_session_id;
      } else if (checkout.data?.payment_session_id) {
        paymentSessionId = checkout.data.payment_session_id;
      }

      if (!paymentSessionId) {
        throw new Error('Neither checkout_url nor payment_session_id provided by backend');
      }

      const sessionIdStr = String(paymentSessionId).trim();
      if (!sessionIdStr.startsWith('session_')) {
        throw new Error(`Invalid session ID format: ${sessionIdStr}`);
      }

      const environment = checkout.environment?.toLowerCase() ||
        process.env.REACT_APP_CASHFREE_ENVIRONMENT?.toLowerCase() ||
        'sandbox';

      const cashfree = await window.Cashfree({ mode: environment });

      // Same unified handler for the SDK fallback path
      const returnUrl = `${window.location.origin}/payment/result?gateway=cashfree&order=${orderId}`;
      const checkoutOptions = {
        paymentSessionId: sessionIdStr,
        returnUrl: returnUrl,
        redirectTarget: "_self"
      };

      console.log(`🔗 [Cashfree SDK] Launching checkout for order: ${orderSerial}`);

      // ❌ REMOVED: Cart clearing before SDK launch
      // ❌ REMOVED: setShowPaymentModal(false) - it cancels the redirect

      // Launch checkout
      cashfree.checkout(checkoutOptions).then((result) => {
        if (result.error) {
          console.error('❌ Cashfree payment error:', result.error?.message);
          setProcessingPayment(false);
          // Redirect to cancel page with error reason
          window.location.href = `/payment/cancel?gateway=cashfree&order=${orderSerial}&reason=${encodeURIComponent(result.error?.message || 'payment_error')}`;
        }
      }).catch((error) => {
        console.error('❌ Cashfree checkout error:', error?.message);
        setProcessingPayment(false);
        window.location.href = `/payment/cancel?gateway=cashfree&order=${orderSerial}&reason=${encodeURIComponent(error?.message || 'checkout_error')}`;
      });

    } catch (error) {
      console.error('❌ Cashfree init failed:', error.message);
      alert('Payment initialization failed: ' + error.message);
      setProcessingPayment(false);
    }
  };

  const formatDeliveryTime = (dateTime) => {
    if (!dateTime) return null;

    // If it's already in the correct format (YYYY-MM-DD HH:MM:SS), return as is
    if (dateTime.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      return dateTime;
    }

    // Legacy format handling (just time like "12:00 PM")
    const timeMatch = dateTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/);

    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const period = timeMatch[3];

      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;

      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');

      return `${year}-${month}-${day} ${formattedHours}:${formattedMinutes}:00`;
    }

    return null;
  };

  // Updated handlePaymentMethod function - with item_price and total_price
  // ============================================
  // FIXED handlePaymentMethod function
  // Replace your existing handlePaymentMethod in RestaurantMenuApp
  // ============================================

  const handlePaymentMethod = async (method) => {
    if (processingPayment) {
      console.warn('Payment already processing, ignoring click');
      return;
    }

    if (method === 'cash') {
      await placeOrderWithPaymentMethod('cash');
      return;
    }

    if (method === 'card') {
      setProcessingPayment(true);

      try {


        // Check inventory before placing order
        const isInventoryOk = await checkCartInventory(cart);
        if (!isInventoryOk) {
          setProcessingPayment(false);
          return;
        }

        // Build items list
        const itemsList = buildOrderItemsList(cart);

        const orderType = getOrderType();
        const isLoggedIn = !!userId;

        // Online payment requires real customer details for payment gateway compliance
        // Payment gateways (Razorpay/Cashfree/Stripe) can suspend accounts that send fake details
        if (!isLoggedIn || !userPhone) {
          alert('Please log in to proceed with online payment. Your phone number is required by the payment gateway.');
          setProcessingPayment(false);
          setShowPaymentModal(false);
          navigate('/login', { state: { from: locationData.pathname } });
          return;
        }

        const customerName = userName || userPhone;
        const customerEmail = userEmail || '';
        const customerPhone = userPhone;

        // Build order payload
        const orderPayload = {
          branch_id: Number(getBranchId()),
          order_type: Number(orderType),
          items: itemsList,
          payment_method: 'online',
          delivery_charge: orderType === 5 ? parseFloat(effectiveDeliveryCharge) : 0,
          // The speed the analysis actually priced this fee for (server's answer, not the raw pick)
          ...(orderType === 5 && cartAnalysis?.shipping?.selected_speed
            ? { delivery_speed: cartAnalysis.shipping.selected_speed }
            : {}),
          subtotal: Number(parseFloat(getTotalAmount)),
          total: Number(parseFloat(getFinalTotal)),
          is_advance_order: orderDetails?.isPreOrder ? 1 : 0,
          source: 5,
          cooking_instruction: cookingInstruction || '',

        };

        // Add offers data
        const offersData = offersManager.getOrderPayloadData();

        if (offersData && offersData.applied_offer_ids && offersData.applied_offer_ids.length > 0) {
          orderPayload.applied_offer_ids = offersData.applied_offer_ids;
          orderPayload.total_offer_discount = offersData.total_offer_discount;

          if (offersData.applied_offers && offersData.applied_offers.length > 0) {
            orderPayload.applied_offers = offersData.applied_offers.map(offer => ({
              offer_id: Number(offer.offer_id || offer.id),
              discount_amount: Number(offer.discount_amount || 0),
              offer_type: offer.offer_type || 'unknown'
            }));
          }
        }

        // ✅ Handle combo offers separately
        const hasComboInCart = cart.some(item => item.isCombo || item.isComboOffer);

        if (hasComboInCart) {
          const comboOfferIds = cart
            .filter(item => item.isCombo || item.isComboOffer)
            .map(item => {
              if (item.comboOfferId) return Number(item.comboOfferId);
              if (typeof item.id === 'string' && item.id.startsWith('combo-')) {
                return Number(item.id.replace('combo-', ''));
              }
              return Number(item.id);
            })
            .filter(id => !isNaN(id) && id > 0);

          if (comboOfferIds.length > 0) {
            if (!orderPayload.applied_offer_ids || orderPayload.applied_offer_ids.length === 0) {
              orderPayload.applied_offer_ids = comboOfferIds;
              orderPayload.total_offer_discount = 0;
              orderPayload.applied_offers = comboOfferIds.map(offerId => ({
                offer_id: offerId,
                discount_amount: 0,
                offer_type: 'combo'
              }));


            } else {
              // Merge with existing offers
              comboOfferIds.forEach(comboId => {
                if (!orderPayload.applied_offer_ids.includes(comboId)) {
                  orderPayload.applied_offer_ids.push(comboId);
                  orderPayload.applied_offers.push({
                    offer_id: comboId,
                    discount_amount: 0,
                    offer_type: 'combo'
                  });
                }
              });


            }
          }
        }



        if (couponCode) {
          orderPayload.coupon_code = couponCode;
        }

        // Add customer info
        if (isLoggedIn) {
          orderPayload.customer_id = Number(userId);
          // ✅ Redeem Points — only sent when the customer actually entered
          // a positive value; the backend computes the discount and total,
          // this frontend only ever sends the point count (see CartContent's
          // Redeem Points row / onRedeemPointsChange).
          if (redeemPoints > 0) {
            orderPayload.loyalty_points_used = redeemPoints;
          }
        } else {
          orderPayload.customer = {
            name: customerName,
            email: customerEmail,
            phone: customerPhone
          };
        }

        if (orderType === 5) {
          if (!selectedAddress) {
            alert('Please select a delivery address');
            setProcessingPayment(false);
            return;
          }

          // ✅ FIXED: Send address_id for registered users
          if (isLoggedIn && selectedAddress.id) {
            orderPayload.address_id = Number(selectedAddress.id);
          } else {
            // Guest users (fallback - shouldn't happen in your app)
            orderPayload.address = {
              address: selectedAddress.address,
              apartment: selectedAddress.apartment || '',
              // No fabricated fallback — a real "no location captured" must stay
              // null so server-side delivery-fee calculation can detect it,
              // rather than silently computing against a fake coordinate.
              latitude: selectedAddress.latitude || null,
              longitude: selectedAddress.longitude || null,
              city: selectedAddress.city || 'Hyderabad',
              state: selectedAddress.state || 'Telangana',
              zip_code: selectedAddress.zip_code || '500001',
              recipient_name: selectedAddress.recipient_name || '',
              recipient_phone: selectedAddress.phone || ''
            };
          }

          if (selectedCollectionTime) {
            const formattedTime = formatDeliveryTime(selectedCollectionTime);
            if (formattedTime) {
              orderPayload.delivery_time = formattedTime;
              // ✅ scheduled_at only for a Pre-Order that's actually
              // Schedule-for-Later — normal orders and Pre-Order ASAP
              // (which also populates selectedCollectionTime, via the
              // computed ASAP time) must not send this field at all.
              if (orderPayload.is_advance_order === 1 && scheduleMode === 'later') {
                orderPayload.scheduled_at = formattedTime;
              }
            }
          } else {
            const deliveryTime = new Date();
            deliveryTime.setMinutes(deliveryTime.getMinutes() + 30);
            // Local time — see formatLocalDateTime; toISOString() (UTC) here
            // shifted delivery_time back by the UTC offset.
            orderPayload.delivery_time = formatLocalDateTime(deliveryTime);
          }
        } else {
          if (!selectedCollectionTime) {
            setProcessingPayment(false);
            return;
          }

          const formattedTime = formatDeliveryTime(selectedCollectionTime);
          if (formattedTime) {
            orderPayload.delivery_time = formattedTime;
            // ✅ scheduled_at only for a Pre-Order that's actually
            // Schedule-for-Later — see the matching comment above.
            if (orderPayload.is_advance_order === 1 && scheduleMode === 'later') {
              orderPayload.scheduled_at = formattedTime;
            }
          }
        }

        if (couponCode) {
          orderPayload.coupon_code = couponCode;
        }

        if (cartAnalysis?.discount) {
          orderPayload.discount = Number(parseFloat(cartAnalysis.discount));
        }

        // ✅ FIXED: Read payment gateway from branch config instead of hardcoding
        const branchGateways = selectedStore?.payment_gateways || [];
        const onlineGateway = branchGateways.find(gw =>
          ['razorpay', 'cashfree', 'stripe'].includes(gw.slug?.toLowerCase())
        );
        orderPayload.payment_gateway = onlineGateway?.slug?.toLowerCase() || 'cashfree';

        if (cart.some(item => item.instruction)) {
          const instructions = cart
            .filter(item => item.instruction)
            .map(item => `${item.name}: ${item.instruction}`)
            .join('; ');
          orderPayload.special_instructions = instructions;
        }



        const orderResponse = await placeNewOrder(orderPayload);
        offersManager.clearOffers();
        setCouponCode('');

        // ✅ Refresh cached loyalty points after a successful redemption so
        // the header strip / Loyalty page / this cart's own row all show the
        // reduced "available" figure — same cache-invalidate + event pattern
        // already used elsewhere in this file (e.g. after an email update).
        if (orderPayload.loyalty_points_used > 0) {
          apiCache.invalidateUserProfile();
          window.dispatchEvent(new Event('profile_updated'));
          setRedeemPoints(0);
        }

        const createdOrder = orderResponse;


        if (!createdOrder || !createdOrder.id) {
          throw new Error('Order creation failed - no order ID received');
        }



        const checkoutPayload = {
          order_id: createdOrder.id,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone,
          customer_country_code: '+91',
          source_id: 5,


        };

        // Call checkout API
        const checkoutResponse = await apiClient.post('/payment/checkout', checkoutPayload);

        const checkoutData = checkoutResponse.data;

        if (!checkoutData) {
          throw new Error('Empty response from checkout API');
        }

        if (!checkoutData.success) {
          const errorMsg = checkoutData.message || 'Payment initialization failed';
          throw new Error(errorMsg);
        }

        // Extract checkout data - handle multiple response formats
        let checkout = null;

        if (checkoutData.data) {
          checkout = checkoutData.data;
        } else if (checkoutData.checkout) {
          checkout = checkoutData.checkout;
        } else {
          checkout = checkoutData;
        }



        // Detect gateway
        let detectedGateway = 'unknown';

        // Priority 1: Check gateway fields
        if (checkout.gateway) {
          detectedGateway = String(checkout.gateway).toLowerCase();
        } else if (checkout.payment_gateway) {
          detectedGateway = String(checkout.payment_gateway).toLowerCase();
        } else if (checkout.data?.gateway) {
          detectedGateway = String(checkout.data.gateway).toLowerCase();
        }

        // Priority 2: Detect from response structure
        if (detectedGateway === 'unknown') {
          if (checkout.checkout_url?.includes('stripe') || (checkout.session_id && String(checkout.session_id).startsWith('cs_'))) {
            detectedGateway = 'stripe';
          } else if (checkout.payment_session_id || checkout.session_id) {
            detectedGateway = 'cashfree';
          } else if (checkout.razorpay_key) {
            detectedGateway = 'razorpay';
          }
        }



        if (!detectedGateway || detectedGateway === 'unknown') {
          throw new Error('Payment gateway not detected. Please check server configuration.');
        }

        // Route to handler
        switch (detectedGateway) {
          case 'cashfree':

            await handleCashfreePayment(checkout, createdOrder.id);
            break;

          case 'razorpay':

            await handleRazorpayPayment(checkout, createdOrder.id);
            break;

          case 'stripe':

            await handleStripePayment(checkout, createdOrder.id);
            break;

          default:
            throw new Error(`Unsupported payment gateway: ${detectedGateway}`);
        }

      } catch (error) {
        console.error('Payment error:', error.message);

        const errorMessage = error.response?.data?.message || error.message || 'Payment failed';
        alert(`Payment Initialization Error: ${errorMessage}\n\nPlease try again or contact support.`);

        setProcessingPayment(false);
      }
    }
  };

  // ============================================
  // REPLACE buildOrderItemsList FUNCTION (around line 1600)
  // ============================================
  const buildOrderItemsList = (cartItems) => {
    console.group('📦 BUILDING ORDER ITEMS');

    const regularItems = cartItems.filter(item => !item.isCombo && !item.isComboOffer);
    const comboItems = cartItems.filter(item => item.isCombo || item.isComboOffer);

    console.log(`Regular items: ${regularItems.length}, Combo offers: ${comboItems.length}`);

    // Process regular items normally
    const items = regularItems.map((item, index) => {
      console.log(`\nItem ${index + 1}: ${item.name}`);

      const variations = Array.isArray(item.variations) ? item.variations : [];
      const extras = Array.isArray(item.extras) ? item.extras : [];
      const addons = Array.isArray(item.addons) ? item.addons : [];

      let itemPrice = 0;

      // ✅ CRITICAL: Check flatPrice FIRST (items from CustomizationModal are pre-calculated)
      if (item.flatPrice) {
        itemPrice = Number(item.flatPrice);
        console.log(`✅ Using pre-calculated flatPrice: ₹${itemPrice}`);
      } else {
        // Regular item from menu - calculate based on variations/extras/addons
        const basePrice = Number(item.price || 0);
        itemPrice = basePrice;

        if (variations.length > 0) {
          // Variations are price DELTAS, add to base
          const variationPrice = Math.max(...variations.map(v => Number(v.variation_price || v.price || 0)));
          itemPrice = basePrice + variationPrice;
          console.log(`Base ₹${basePrice} + Variation ₹${variationPrice} = ₹${itemPrice}`);
        }

        const extraTotal = extras.reduce((sum, e) => sum + Number(e.extra_price || e.price || 0), 0);
        if (extraTotal > 0) {
          itemPrice += extraTotal;
          console.log(`+ Extras ₹${extraTotal} = ₹${itemPrice}`);
        }

        const addonTotal = addons.reduce((sum, a) => sum + Number(a.addon_item_price || a.price || 0), 0);
        if (addonTotal > 0) {
          itemPrice += addonTotal;
          console.log(`+ Addons ₹${addonTotal} = ₹${itemPrice}`);
        }
      }

      const totalPrice = itemPrice * Number(item.quantity);

      //console.log(`📦 Item: ${item.name}, Base Price: ₹${Number(item.price || 0)}, Final Price: ₹${itemPrice}, Total: ₹${totalPrice}, Customized: ${isCustomized}`);
      console.log("=========================");
      console.log(variations);
      console.log("=========================");
      let apiItemPrice = Number(itemPrice.toFixed(2));
      let apiQuantity = Number(item.quantity);
      let apiVariationId = null;
      let apiVariations = variations;
      let weightField = null;
      let unitField = null;
      let weightInfoField = null;

      // ✅ Weight-based item handling (Type A, B, C)
      if (item.isWeightBased) {
        const weightConfig = item.weight_config || {};

        if (weightConfig.has_fixed_variants && item.selectedVariant) {
          // ═══════════════════════════════════════════
          // TYPE B — Fixed variant sizes (250g / 500g / 1kg)
          // item_price = selected variant's price
          // quantity = number of portions ordered
          // ═══════════════════════════════════════════
          const variant = item.selectedVariant;
          apiItemPrice = Number(variant.price || 0);
          apiQuantity = Number(item.quantity);
          apiVariationId = Number(variant.variation_id || null);
          apiVariations = [{
            id: Number(variant.variation_id || 0),
            name: String(variant.variation_name || ''),
            price: Number(variant.price || 0)
          }];
          weightField = Number(variant.weight_quantity || 0);
          unitField = 'kg';
          weightInfoField = String(variant.weight_display || item.weightLabel || '');
          console.log(`⚖️ Type B (fixed variant): ${variant.variation_name} @ ₹${apiItemPrice}`);

        } else if (weightConfig.has_fixed_quantity) {
          // ═══════════════════════════════════════════
          // TYPE C — Fixed quantity portions (e.g. 150g per serving)
          // item_price = price per portion (price_per_kg × fixed_quantity)
          // quantity = number of portions ordered
          // ═══════════════════════════════════════════
          const fixedQty = Number(weightConfig.fixed_quantity || 0);
          const pricePerKg = Number(item.price || 0);
          apiItemPrice = Number((pricePerKg * fixedQty).toFixed(2));
          apiQuantity = Number(item.quantity);
          weightInfoField = String(weightConfig.fixed_quantity_display || item.weightLabel || '');
          console.log(`⚖️ Type C (fixed quantity): ${fixedQty}kg portion @ ₹${apiItemPrice}`);

        } else {
          // ═══════════════════════════════════════════
          // TYPE A — Manual weight input (default)
          // Matches POS behavior:
          //   item_price = weight-adjusted price per portion (pricePerKg × weightKg)
          //   quantity  = total weight in kg (weightKg × cartCount)
          // ═══════════════════════════════════════════
          const weightKg = Number(item.selectedWeight || 0);
          const pricePerKg = Number(item.price || 0);
          const cartQty = Number(item.quantity || 1);
          const totalWeightKg = Number((weightKg * cartQty).toFixed(3));

          apiItemPrice = Number((pricePerKg * weightKg).toFixed(2));
          // ✅ POS sends quantity as total weight in kg (e.g., 0.5 × 3 = 1.5)
          apiQuantity = totalWeightKg;

          weightField = weightKg;
          unitField = 'kg';

          // ✅ Build weight_info matching POS format: "1.5 kg (0.5 × 3)"
          if (cartQty > 1) {
            weightInfoField = `${totalWeightKg} kg (${weightKg} × ${cartQty})`;
          } else {
            weightInfoField = item.weightLabel || (weightKg < 1
              ? `${Math.round(weightKg * 1000)} g`
              : `${weightKg} kg`);
          }
          console.log(`⚖️ Type A (manual weight): ${weightKg}kg × ${cartQty} = ${totalWeightKg}kg @ ₹${pricePerKg}/kg, item_price=₹${apiItemPrice}`);
        }
      }

      // total_price: use pre-calculated flatPrice × cartQuantity for weight items
      // (NOT apiItemPrice × apiQuantity since POS quantity = total weight, not count)
      const finalTotalPrice = Number(totalPrice.toFixed(2));

      const orderItemPayload = {
        item_id: Number(item.id),
        item_price: apiItemPrice,
        quantity: apiQuantity,
        total_price: finalTotalPrice,
        discount: 0,
        item_variation_id: apiVariationId,

        item_variations: apiVariations.map(v => ({
          item_id: Number(v.variation_id || v.id || 0),
          variation_name: String(v.variation_name || ''),
          name: String(v.name || ''),
          variation_price: Number(v.variation_price || v.price || 0)
        })),

        item_extras: extras.map(e => ({
          item_id: Number(e.extra_id || e.id || 0),
          name: String(e.extra_name || e.name || ''),
          extra_price: Number(e.extra_price || e.price || 0)
        })),

        item_addons: addons.map(a => ({
          addon_id: Number(a.addon_id || 0),
          addon_item_id: Number(a.addon_item_id || 0),
          addon_item_name: String(a.addon_item_name || ''),
          addon_item_price: Number(a.addon_item_price || a.price || 0)
        })),

        item_variation_total: 0,
        item_extra_total: 0,

        instruction: cookingInstruction || item.instruction || ''
      };

      // ✅ Add weight fields only for weight-based items (float, not string)
      if (item.isWeightBased) {
        if (weightField !== null) {
          orderItemPayload.weight = weightField;       // float (e.g. 0.350)
          orderItemPayload.unit = unitField;            // string "kg"
        }
        if (weightInfoField) {
          orderItemPayload.weight_info = weightInfoField; // display string (e.g. "350 g")
        }
      }

      return orderItemPayload;
    });

    // ✅ NEW: Process combo items - add their sub-items to the order
    if (comboItems.length > 0) {
      console.log('\n🎁 PROCESSING COMBO OFFERS:');

      comboItems.forEach(combo => {
        const offerId = combo.comboOfferId ||
          (typeof combo.id === 'string' && combo.id.startsWith('combo-')
            ? Number(combo.id.replace('combo-', ''))
            : Number(combo.id));

        const comboPricePerUnit = Number(combo.flatPrice || combo.price || 0);
        const comboTotalPrice = comboPricePerUnit * Number(combo.quantity);

        console.log(`  - ${combo.name} (Offer ID: ${offerId}, Combo Price: ₹${comboPricePerUnit} × ${combo.quantity} = ₹${comboTotalPrice})`);

        // ✅ FIX: Fixed bundle products (added via ComboDetailsModal's
        // comboComponents path, e.g. "pizza 220") are ordinary catalog
        // items with their own real item_id — they never receive a
        // comboOfferId (that's only set for real Offer-based combos, see
        // the product.comboData path in addToCart). Sending these as a
        // single order line using that real item_id — same shape as any
        // normal item — keeps the bundle as ONE order line, so My Orders /
        // Order Details show "pizza 220 x1 — ₹299" instead of splitting
        // into "Pizza — ₹100" + "Karvepaku Kodi Pulav — ₹199" with no way
        // to tell they were ever one bundle (the backend's order_items
        // response has no combo-grouping field at all, so once split like
        // that the grouping is unrecoverable at display time). Real offer
        // combos are untouched below — they still expand into their real
        // components exactly as before, since that path is tied to
        // applied_offer_ids/discount tracking that already works.
        const isRealOfferCombo = Boolean(combo.comboOfferId) || combo.isComboOffer === true;

        if (!isRealOfferCombo) {
          const parsedBundleId = Number(combo.id);
          items.push({
            item_id: isNaN(parsedBundleId) ? 0 : parsedBundleId,
            item_price: Number(comboPricePerUnit.toFixed(2)),
            quantity: Number(combo.quantity || 1),
            total_price: Number(comboTotalPrice.toFixed(2)),
            discount: 0,
            item_variation_id: null,
            item_variations: [],
            item_extras: [],
            item_addons: [],
            item_variation_total: 0,
            item_extra_total: 0,
            instruction: cookingInstruction || combo.instruction || ''
          });
          console.log(`    ✅ Sent as single bundle line (item_id: ${parsedBundleId}), not expanded into sub-items`);
          return; // Continue to next combo — do not expand into sub-items
        }

        // Get the sub-items from this combo
        const comboSubItems = combo.comboItems || combo.comboData?.combo_items_details || combo.comboComponents || combo.combo_components || combo.items || [];

        console.log(`    Contains ${comboSubItems.length} sub-items:`);

        if (comboSubItems.length === 0) {
          // If no sub-items are found, push the combo itself as an item
          const parsedId = Number(offerId || (typeof combo.id === 'string' ? combo.id.replace('combo-', '') : combo.id) || combo.item_id);
          items.push({
            item_id: isNaN(parsedId) ? 0 : parsedId,
            item_price: Number(comboPricePerUnit.toFixed(2)),
            quantity: Number(combo.quantity || 1),
            total_price: Number(comboTotalPrice.toFixed(2)),
            discount: 0,
            item_variation_id: null,
            item_variations: [],
            item_extras: [],
            item_addons: [],
            item_variation_total: 0,
            item_extra_total: 0,
            instruction: cookingInstruction || combo.instruction || ''
          });
          return; // Continue to next combo
        }

        // ✅ FIXED: Use actual item prices instead of distributing combo price
        // Add each sub-item from the combo to the items array
        comboSubItems.forEach(subItem => {
          const subItemQuantity = Number(subItem.quantity || 1) * Number(combo.quantity);

          const extractedItemId = subItem.component_item_id || subItem.item_id || subItem.product_id || subItem.id;
          const parsedSubItemId = Number(extractedItemId);
          const finalSubItemId = isNaN(parsedSubItemId) ? 0 : parsedSubItemId;

          // If item_price is not available, try to find the product in allProducts
          let itemPrice = 0;

          if (subItem.item_price !== undefined) {
            // Use item_price from combo sub-item data
            itemPrice = Number(subItem.item_price);
            console.log(`      • ${subItem.component_item_name || subItem.item_name || subItem.name} (ID: ${extractedItemId}) @ ₹${itemPrice} (from item_price)`);
          } else if (subItem.price !== undefined) {
            // Fallback to price property
            itemPrice = Number(subItem.price);
            console.log(`      • ${subItem.component_item_name || subItem.item_name || subItem.name} (ID: ${extractedItemId}) @ ₹${itemPrice} (from price)`);
          } else {
            // Try to find product in allProducts to get actual price
            const productFromMenu = allProducts.find(p => Number(p.id) === finalSubItemId);
            if (productFromMenu) {
              itemPrice = Number(productFromMenu.price);
              console.log(`      • ${subItem.component_item_name || subItem.item_name || subItem.name} (ID: ${extractedItemId}) @ ₹${itemPrice} (from menu)`);
            } else {
              console.log(`      • ${subItem.component_item_name || subItem.item_name || subItem.name} (ID: ${extractedItemId}) @ ₹0 (NO PRICE FOUND)`);
            }
          }

          const subItemTotalPrice = itemPrice * subItemQuantity;

          // ✅ CRITICAL FIX: Include actual item prices (not distributed combo price)
          const orderItem = {
            item_id: finalSubItemId,
            item_price: Number(itemPrice.toFixed(2)), // ✅ Actual item price
            quantity: subItemQuantity,
            total_price: Number(subItemTotalPrice.toFixed(2)), // ✅ Total for this sub-item
            discount: 0,
            item_variation_id: subItem.component_variation_id || null,
            item_variations: [],
            item_extras: [],
            item_addons: [],
            item_variation_total: 0,
            item_extra_total: 0,
            instruction: cookingInstruction || combo.instruction || ''
          };

          // If sub-item has a variation, include it
          if (subItem.variation_id) {
            orderItem.item_variations = [{
              variation_id: Number(subItem.variation_id),
              variation_name: String(subItem.variation_name || ''),
              variation_price: 0 // No additional price for variation in combo
            }];
          }

          items.push(orderItem);
        });
      });
    }

    console.log('\n✅ ORDER ITEMS BUILD COMPLETE');
    console.log('Total Items (including combo sub-items):', items.length);
    console.groupEnd();

    return items;
  };


  // ✅ 4. CASH PAYMENT HANDLER - Clear cart after successful order
  const placeOrderWithPaymentMethod = async (paymentMethod) => {
    try {
      setProcessingPayment(true);

      const itemsList = buildOrderItemsList(cart);
      const orderType = getOrderType();
      const isLoggedIn = !!userId;

      console.log('💰 Creating order with payment method:', paymentMethod);

      const orderPayload = {
        branch_id: Number(getBranchId()),
        order_type: Number(orderType),
        items: itemsList,
        payment_method: paymentMethod === 'cash' ? 'cash' : 'online',
        delivery_charge: orderType === 5 ? parseFloat(effectiveDeliveryCharge) : 0,
        // The speed the analysis actually priced this fee for (server's answer, not the raw pick)
        ...(orderType === 5 && cartAnalysis?.shipping?.selected_speed
          ? { delivery_speed: cartAnalysis.shipping.selected_speed }
          : {}),
        subtotal: Number(parseFloat(getTotalAmount)),
        total: Number(parseFloat(getFinalTotal)),
        is_advance_order: orderDetails?.isPreOrder ? 1 : 0,
        source: 5,
        cooking_instruction: cookingInstruction || '',

      };

      // Add customer info
      if (isLoggedIn) {
        orderPayload.customer_id = Number(userId);
        // ✅ Redeem Points — see the matching comment in the online-payment
        // payload construction above.
        if (redeemPoints > 0) {
          orderPayload.loyalty_points_used = redeemPoints;
        }
      } else {
        // For cash orders, use whatever real details we have
        // Never fabricate fake email/phone - backend should handle missing fields
        if (!userPhone) {
          alert('Please log in to place an order. Your phone number is required.');
          setProcessingPayment(false);
          setShowPaymentModal(false);
          navigate('/login', { state: { from: locationData.pathname } });
          return;
        }
        orderPayload.customer = {
          name: userName || userPhone,
          email: userEmail || '',
          phone: userPhone
        };
      }

      // Add address for delivery
      if (orderType === 5) {
        if (!selectedAddress) {
          alert('Please select a delivery address');
          setProcessingPayment(false);
          return;
        }

        // ✅ FIXED: Send address_id for registered users
        if (isLoggedIn && selectedAddress.id) {
          orderPayload.address_id = Number(selectedAddress.id);
          console.log('✅ Cash order using address_id:', orderPayload.address_id);
        } else {
          // Guest users (fallback - shouldn't happen in your app)
          orderPayload.address = {
            address: selectedAddress.address,
            apartment: selectedAddress.apartment || '',
            // No fabricated fallback — a real "no location captured" must stay
            // null so server-side delivery-fee calculation can detect it,
            // rather than silently computing against a fake coordinate.
            latitude: selectedAddress.latitude || null,
            longitude: selectedAddress.longitude || null,
            city: selectedAddress.city || 'Hyderabad',
            state: selectedAddress.state || 'Telangana',
            zip_code: selectedAddress.zip_code || '500001',
            recipient_name: selectedAddress.recipient_name || '',
            recipient_phone: selectedAddress.phone || ''
          };
          console.log('⚠️ Cash order using full address (guest user)');
        }

        if (selectedCollectionTime) {
          const formattedTime = formatDeliveryTime(selectedCollectionTime);
          if (formattedTime) {
            orderPayload.delivery_time = formattedTime;
            // ✅ scheduled_at only for a Pre-Order that's actually
            // Schedule-for-Later — normal orders and Pre-Order ASAP
            // (which also populates selectedCollectionTime, via the
            // computed ASAP time) must not send this field at all.
            if (orderPayload.is_advance_order === 1 && scheduleMode === 'later') {
              orderPayload.scheduled_at = formattedTime;
            }
          }
        } else {
          const deliveryTime = new Date();
          deliveryTime.setMinutes(deliveryTime.getMinutes() + 30);
          // Local time — see formatLocalDateTime; toISOString() (UTC) here
          // shifted delivery_time back by the UTC offset.
          orderPayload.delivery_time = formatLocalDateTime(deliveryTime);
        }
      } else {
        if (!selectedCollectionTime) {

          setProcessingPayment(false);
          return;
        }

        const formattedTime = formatDeliveryTime(selectedCollectionTime);
        if (formattedTime) {
          orderPayload.delivery_time = formattedTime;
          // ✅ scheduled_at only for a Pre-Order that's actually
          // Schedule-for-Later — see the matching comment above.
          if (orderPayload.is_advance_order === 1 && scheduleMode === 'later') {
            orderPayload.scheduled_at = formattedTime;
          }
        }
      }

      // Add offers
      // Add offers data - FIXED VERSION
      const offersData = offersManager.getOrderPayloadData();
      console.log('🎁 Offers Data:', offersData);

      if (offersData && offersData.applied_offer_ids && offersData.applied_offer_ids.length > 0) {
        orderPayload.applied_offer_ids = offersData.applied_offer_ids;
        orderPayload.total_offer_discount = offersData.total_offer_discount;

        if (offersData.applied_offers && offersData.applied_offers.length > 0) {
          orderPayload.applied_offers = offersData.applied_offers.map(offer => ({
            offer_id: Number(offer.offer_id || offer.id),
            discount_amount: Number(offer.discount_amount || 0),
            offer_type: offer.offer_type || 'unknown'
          }));
        }
      }

      // ✅ Handle combo offers for cash orders too
      const hasComboInCart = cart.some(item => item.isCombo || item.isComboOffer);

      if (hasComboInCart) {
        const comboOfferIds = cart
          .filter(item => item.isCombo || item.isComboOffer)
          .map(item => {
            if (item.comboOfferId) return Number(item.comboOfferId);
            if (typeof item.id === 'string' && item.id.startsWith('combo-')) {
              return Number(item.id.replace('combo-', ''));
            }
            return Number(item.id);
          })
          .filter(id => !isNaN(id) && id > 0);

        if (comboOfferIds.length > 0) {
          if (!orderPayload.applied_offer_ids || orderPayload.applied_offer_ids.length === 0) {
            orderPayload.applied_offer_ids = comboOfferIds;
            orderPayload.total_offer_discount = 0;
            orderPayload.applied_offers = comboOfferIds.map(offerId => ({
              offer_id: offerId,
              discount_amount: 0,
              offer_type: 'combo'
            }));

            console.log('✅ Added combo offers to cash payment:', comboOfferIds);
          } else {
            comboOfferIds.forEach(comboId => {
              if (!orderPayload.applied_offer_ids.includes(comboId)) {
                orderPayload.applied_offer_ids.push(comboId);
                orderPayload.applied_offers.push({
                  offer_id: comboId,
                  discount_amount: 0,
                  offer_type: 'combo'
                });
              }
            });

            console.log('✅ Merged combo with existing offers');
          }
        }
      }

      console.log('📋 Final offers in cash payment:', {
        offer_ids: orderPayload.applied_offer_ids,
        total_discount: orderPayload.total_offer_discount,
        applied_offers: orderPayload.applied_offers
      });
      if (couponCode) {
        orderPayload.coupon_code = couponCode;
      }

      if (cartAnalysis?.discount) {
        orderPayload.discount = Number(parseFloat(cartAnalysis.discount));
      }

      if (cart.some(item => item.instruction)) {
        const instructions = cart
          .filter(item => item.instruction)
          .map(item => `${item.name}: ${item.instruction}`)
          .join('; ');
        orderPayload.special_instructions = instructions;
      }

      console.log('📦 Final Order Payload:', orderPayload);


      const orderResponse = await placeNewOrder(orderPayload);

      // ✅ Refresh cached loyalty points after a successful redemption — see
      // the matching comment in the online-payment flow above.
      if (orderPayload.loyalty_points_used > 0) {
        apiCache.invalidateUserProfile();
        window.dispatchEvent(new Event('profile_updated'));
        setRedeemPoints(0);
      }

      console.log('✅ Order placed successfully:', orderResponse);

      if (orderResponse && orderResponse.id) {
        // Close modals and clear processing state
        setProcessingPayment(false);
        setShowPaymentModal(false);
        setShowCart(false);

        // Redirect to dedicated cash order success page
        const successUrl = `/order/cash-success?order=${orderResponse.order_serial_no}`;
        console.log('✅ Navigating to success page:', successUrl);

        // Use navigate for internal routing, fallback to href if needed
        try {
          navigate(successUrl);
        } catch (e) {
          window.location.href = successUrl;
        }

        // Return early - navigation handled
        return;
      }



      // ✅ CRITICAL: Clear cart ONLY after successful order
      try {
        console.log('🗑️ Clearing cart after successful order...');
        clearCartFromStorage(selectedStore?.id, orderDetails?.type);

        if (selectedStore && orderDetails) {
          const key = `resto_cart_data_${selectedStore.id}_${orderDetails.type}`;
          localStorage.removeItem(key);
          console.log('✅ Cart cleared from storage:', key);
        }
      } catch (error) {
        console.error('❌ Error clearing cart:', error);
      }

      // Clear state
      setCart([]);
      setShowCart(false);
      setShowPaymentModal(false);
      setCartAnalysis(null);
      setSelectedCollectionTime('');
      setSelectedAddress(null);
      offersManager.clearOffers();
      setCouponCode('');
      setProcessingPayment(false);

    } catch (error) {
      console.error('❌ Order error:', error);
      console.error('Order failed:', error.response?.data?.message || error.message);

      setProcessingPayment(false);
      alert(error.response?.data?.message || error.message || 'Order failed. Please try again.');
      throw error;
    }
  };

  const handlePlaceOrder = async () => {
    console.log('🛍️📱 handlePlaceOrder CALLED');

    // ✅ Block placing an order while the store is closed and doesn't
    // support pre-order. A closed-but-pre-order-capable store is let
    // through — CartContent's own scheduling flow (Gap B) already handles
    // that case correctly (forces a future time, disables ASAP).
    if (!guardStoreOpenAction({ allowIfPreOrderSupported: true })) {
      return;
    }

    if (analyzingCart) {
      console.warn('⏳ Cannot place order - Cart analysis in progress');
      alert('We are calculating the final totals and delivery fee. Please wait a moment.');
      return;
    }

    if (cart.length === 0) {
      console.warn('❌ Cart is empty');
      return;
    }

    // ✅ ADD THIS: Check inventory before proceeding
    const isInventoryOk = await checkCartInventory(cart);
    if (!isInventoryOk) {
      console.warn('❌ Inventory check failed');
      return;
    }

    const orderType = getOrderType();
    console.log('📦 Order Type:', orderType, `(${orderType === 10 ? 'COLLECTION' : 'DELIVERY'})`);
    console.log('⏰ selectedCollectionTime:', selectedCollectionTime);
    console.log('📍 selectedAddress:', selectedAddress);

    if (orderType === 10 && !selectedCollectionTime) {
      console.warn('⏰ Collection order - NO TIME SELECTED');
      alert('⏰ Please select a pickup time before placing your order');
      return;
    }

    if (orderType === 5 && !selectedAddress) {
      console.warn('📍 Delivery order - NO ADDRESS');
      alert('📍 Please select a delivery address before placing your order');
      return;
    }

    // ✅ FIX 3: Independent safety check — never rely solely on the Order Now
    // button's disabled state (CartContent.js). Re-verify the freshest
    // delivery/shipping data right before submitting, for both correctness
    // (bundle-only carts previously had shippingInfo permanently null, which
    // the button's disabled check couldn't detect as "not serviceable") and
    // the race condition fixed above (a stale cartAnalysis briefly rendering
    // as usable while a fresher analysis is still in flight).
    if (orderType === 5) {
      const shipping = cartAnalysis?.shipping || null;
      if (shipping?.serviceable === false) {
        console.warn('🚫 Delivery not serviceable to this address:', shipping);
        alert(shipping?.message || 'Delivery is not available to this address. Please select a different address.');
        return;
      }
      if (cartSignature !== lastAnalyzedCartSignatureRef.current) {
        console.warn('⏳ Delivery analysis does not match current cart/address yet');
        alert('We are still calculating the delivery fee for your latest changes. Please wait a moment and try again.');
        return;
      }
    }

    // ✅ REAL-TIME email check — always fetch fresh from server, no cache
    if (isUserLoggedIn) {
      const token = getValidAuthSession()?.token;
      if (token) {
        try {
          console.log('📧 Checking email from server (real-time)...');
          const profileRes = await axios.get(`${API_BASE_URL}/web/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Api-Key': X_API_KEY,
              'X-Tenant': getTenantId(),
              'Accept': 'application/json'
            }
          });
          const serverEmail = profileRes.data?.data?.email;
          const serverName = profileRes.data?.data?.name;
          const serverPhone = profileRes.data?.data?.phone;

          // Sync local state with server truth
          if (serverEmail) {
            setUserEmail(serverEmail);
          }
          if (serverName) setUserName(serverName);
          if (serverPhone) setUserPhone(serverPhone);

          // Update cache with fresh data
          if (profileRes.data?.data) {
            apiCache.setUserProfile(profileRes.data.data);
          }

          if (!serverEmail) {
            console.log('📧 No email on server - showing email prompt');
            setEmailInput('');
            setEmailError('');
            setShowEmailModal(true);
            return;
          }

          console.log('📧 Email confirmed:', serverEmail);
        } catch (err) {
          console.error('⚠️ Could not verify email, proceeding anyway:', err.message);
          // If API fails, don't block the order — let backend handle it
        }
      }
    }

    console.log('✅ All validations passed - initiating checkout flow');

    if (CHECKOUT_FLOW_CONFIG.USE_COD_ONLY_FLOW) {
      // NEW: Show custom modal for COD-only flow
      setShowOrderConfirmationModal(true);
    } else {
      // OLD: Show standard payment selection modal
      setShowPaymentModal(true);
    }
  };

  const handleConfirmOrder = () => {
    setShowOrderConfirmationModal(false);
    console.log('🚀 Order confirmed from custom modal - triggering placement');
    handlePaymentMethod('cash');
  };

  // ✅ Handle email submission from prompt modal
  const handleEmailSubmit = async () => {
    const trimmedEmail = emailInput.trim();

    // Validate email
    if (!trimmedEmail) {
      setEmailError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailSaving(true);
    setEmailError('');

    try {
      const token = getValidAuthSession()?.token;
      if (!token) {
        setEmailError('Session expired. Please login again.');
        setEmailSaving(false);
        return;
      }

      const response = await axios.put(
        `${API_BASE_URL}/web/me`,
        {
          email: trimmedEmail
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Api-Key': X_API_KEY,
            'X-Tenant': getTenantId(),
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (response.data) {
        console.log('✅ Email updated successfully:', trimmedEmail);
        setUserEmail(trimmedEmail);

        // Invalidate cache so it forces a fresh fetch
        apiCache.invalidateUserProfile();

        // Notify Header to instantly fetch new profile
        window.dispatchEvent(new Event('profile_updated'));

        setShowEmailModal(false);

        // Final transition directly to online payment
        setShowPaymentModal(true);
        handlePaymentMethod('card');
      }
    } catch (err) {
      console.error('❌ Error updating email:', err);
      if (err.response?.data?.errors?.email) {
        setEmailError(err.response.data.errors.email[0] || 'Invalid email');
      } else {
        setEmailError(err.response?.data?.message || 'Failed to save email. Please try again.');
      }
    } finally {
      setEmailSaving(false);
    }
  };

  const handleLocationChangeClick = () => {
    if (onLocationChange) {
      onLocationChange();
    }
    onClose();
  };

  const handleShowProductDetails = (product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
  };

  const handleCloseProductDetails = () => {
    setShowProductDetails(false);
    setSelectedProduct(null);
  };

  const handleOpenCustomizationFromDetails = (product) => {
    setProductToCustomize(product);
    setIsEditingCartItem(false);
    setEditingCartItem(null);
    setShowCustomizeModal(true);
  };

  const config = getRestaurantConfigSync();

  // Admin-configured filter visibility (Tenant::getMenuFilterSettings()) — replaces
  // the old single "is this apparel?" branch with per-filter-type toggles.
  // getRestaurantConfigSync() returns a static skeleton mock that's never
  // updated with live API data (see its doc comment in restaurantConfig.js),
  // so config.menuFilterSettings is always the skeleton default. App.js holds
  // the actually-fetched live config in React state and must pass it down as
  // a prop — same pattern already used for outletType/propOutletType above.
  // Drives which copy/icon the empty-search-results state shows.
  const outletType = propOutletType || 'restaurant';

  // Same rationale as menuFilterSettings: never read templateType from
  // the static getRestaurantConfigSync() skeleton — it must come from the
  // live config held in App.js and threaded down as a prop.
  const templateType = propTemplateType || 'template1';

  const rawFilterSettings = propMenuFilterSettings || config.menuFilterSettings || {};
  // Meat stores carry non-veg/meat products by definition — suppress filters when outletType === 'meat_store'
  const menuFilterSettings = outletType === 'meat_store'
    ? { ...rawFilterSettings, veg_non_veg: false, gender: false, item_nature: false, age_group: false, brand: false, color: false }
    : rawFilterSettings;

  const emptyStateContent = EMPTY_STATE_BY_OUTLET_TYPE[outletType] || DEFAULT_EMPTY_STATE;

  // Distinct values present in the catalog, same "build chips from real data"
  // approach the POS/Kiosk apps use — not a fixed admin-managed list.
  const availableBrands = useMemo(
    () => Array.from(new Set(products.map(p => p.brand).filter(Boolean))).sort(),
    [products]
  );
  const availableColors = useMemo(
    () => Array.from(new Set(products.map(p => p.color).filter(Boolean))).sort(),
    [products]
  );
  const availableAgeGroups = useMemo(
    () => Array.from(new Set(
      products.map(p => (p.ageGroup || '').toLowerCase()).filter(ag => ag && ag !== 'all')
    )).sort(),
    [products]
  );
  // A single-value Gender filter can't narrow anything down — only show it
  // when there's an actual choice (2+ distinct values present), same rule
  // applied to the POS/Kiosk apps' Gender and Item Nature filters.
  const availableGenders = useMemo(
    () => Array.from(new Set(
      products.map(p => (p.genderCompatibility || '').toLowerCase()).filter(Boolean)
    )),
    [products]
  );
  const showGenderFilter = menuFilterSettings.gender && availableGenders.length >= 2;

  // Admin-configured filter visibility (Tenant::getMenuFilterSettings()) — replaces
  // the old single "is this apparel?" branch with per-filter-type toggles.
  // NOTE: `item_nature` is deliberately NOT included — nothing renders or
  // filters on it (no ApparelFilters section, no predicate), so letting it
  // flip on the whole retail Filters sidebar just produced an empty
  // "Filters / Clear All" shell (e.g. on dry_fruit_shop tenants that had the
  // flag set). Only flags that actually drive a visible filter count here.
  const showRetailFilters = !!(
    showGenderFilter ||
    menuFilterSettings.age_group ||
    menuFilterSettings.brand ||
    menuFilterSettings.color
  );

  // 4️⃣ FILTERED PRODUCTS LOGIC
  const sourceProducts = showRetailFilters ? (allProducts.length > 0 ? allProducts : products) : products;

  const filteredProducts = sourceProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    if (menuFilterSettings.veg_non_veg) {
      if (vegFilter && !nonVegFilter && product.isVeg !== true) return false;
      if (nonVegFilter && !vegFilter && product.isVeg !== false) return false;
    }

    if (showRetailFilters) {
      // Category Filter (apparelFilters.categories)
      if (apparelFilters.categories && apparelFilters.categories.length > 0) {
        const selectedCatIds = apparelFilters.categories.map(String);
        if (!selectedCatIds.includes('all')) {
          const productCatId = String(product.category ?? product.item_category_id ?? product.category_id ?? '');
          const productCatName = String(product.category_name ?? '').toLowerCase();
          const matchesCategory = selectedCatIds.some(id =>
            id === productCatId || (id.length > 2 && id.toLowerCase() === productCatName)
          );
          if (!matchesCategory) return false;
        }
      }

      // Price Range Filter
      if (apparelFilters.priceRange) {
        const price = Number(product.flatPrice || product.price || 0);
        if (apparelFilters.priceRange === '0-500' && price > 500) return false;
        if (apparelFilters.priceRange === '500-1000' && (price < 500 || price > 1000)) return false;
        if (apparelFilters.priceRange === '1000-2500' && (price < 1000 || price > 2500)) return false;
        if (apparelFilters.priceRange === '2500-5000' && (price < 2500 || price > 5000)) return false;
        if (apparelFilters.priceRange === '5000+' && price <= 5000) return false;
      }

      if (menuFilterSettings.gender && apparelFilters.gender?.length) {
        const productGender = (product.genderCompatibility || '').toLowerCase();
        if (productGender !== 'unisex' && !apparelFilters.gender.includes(productGender)) return false;
      }

      if (menuFilterSettings.age_group && apparelFilters.ageGroups?.length) {
        const productAgeGroup = (product.ageGroup || '').toLowerCase();
        if (productAgeGroup !== 'all' && !apparelFilters.ageGroups.includes(productAgeGroup)) return false;
      }

      if (menuFilterSettings.brand && apparelFilters.brands?.length) {
        if (!apparelFilters.brands.includes(product.brand || '')) return false;
      }

      if (menuFilterSettings.color && apparelFilters.colors?.length) {
        if (!apparelFilters.colors.includes(product.color || '')) return false;
      }
    }

    return true;
  });

  // Client-side re-ordering of the already-filtered list. Pure presentation —
  // does not change which products are shown, only the display order.
  const sortedProducts = useMemo(() => {
    if (sortBy === 'price_asc') {
      return [...filteredProducts].sort((a, b) => (Number(a.flatPrice || a.price || 0)) - (Number(b.flatPrice || b.price || 0)));
    }
    if (sortBy === 'price_desc') {
      return [...filteredProducts].sort((a, b) => (Number(b.flatPrice || b.price || 0)) - (Number(a.flatPrice || a.price || 0)));
    }
    return filteredProducts;
  }, [filteredProducts, sortBy]);

  if (!isOpen) return null;

  // Loader removed to show RestaurantMenuApp and its skeleton immediately

  const hasOffers = cartAnalysis !== null;

  // 🔍 DEBUG: Log offers visibility
  console.log('🎯 Offers button debug:', {
    cartLength: cart.length,
    cartAnalysis: cartAnalysis ? 'EXISTS' : 'NULL',
    hasOffers: hasOffers,
    willShowButton: cart.length > 0 && hasOffers
  });

  return (
    <>
      <div className="menu-app-overlay">        <div className="menu-app-container">
        {HeaderComponent && (
          <HeaderComponent
            {...headerProps}
            onLocationChangeFromMenu={handleLocationChangeClick}
            onShowAuthModal={handleShowAuthModalFromHeader}
            onLogout={handleLogout}  // ✅ ADD THIS
          />
        )}

        <div className="menu-app-layout">
          {showRetailFilters ? (
            <ApparelFilters
              filters={apparelFilters}
              setFilters={setApparelFilters}
              menuFilterSettings={menuFilterSettings}
              showGenderFilter={showGenderFilter}
              availableBrands={availableBrands}
              availableColors={availableColors}
              availableAgeGroups={availableAgeGroups}
              availableSizes={[]}
              availableCategories={categories}
              isLoading={categoryLoading}
            />
          ) : (
            <div className="menu-app-sidebar">
              <div className="menu-app-sidebar-scroll" ref={sidebarScrollRef}>
                <h2 className="menu-app-sidebar-title">{t('menu.categories')}</h2>
                <div className="menu-app-categories-vertical">
                  {categories.length === 0 ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="menu-app-category-skeleton" />
                    ))
                  ) : (
                    categories.map(category => (
                      <button
                        key={category.id}
                        onClick={async () => { // ✅ Add async
                          console.log('🔘 Category button clicked:', category.id, category.name);
                          setSelectedCategory(category.id);
                          updateCategoryInUrl(category);
                          await handleCategoryChange(category.id); // ✅ Add await
                        }}
                        className={`menu-app-category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                      >
                        {category.image && (
                          <span className="menu-app-category-thumb">
                            <FallbackImg src={category.image} alt={category.name} />
                          </span>
                        )}
                        <span className="menu-app-category-name">{category.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="menu-app-sidebar-scrollbar-track" ref={sidebarTrackRef} aria-hidden="true">
                <div
                  className="menu-app-sidebar-scrollbar-thumb"
                  ref={sidebarThumbRef}
                  onPointerDown={handleSidebarThumbPointerDown}
                />
              </div>
            </div>
          )}

          <div className="menu-app-main">
            <div className="menu-app-main-scroll" ref={mainScrollRef}>
              <div className="menu-app-search-container">
                <div className="menu-app-search-wrapper">
                  <div className="menu-app-search-box">
                    <div className="search-input-wrapper">
                      <Search className="menu-app-search-icon" size={22} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={outletType === 'meat_store' ? 'Search meat & seafood' : templateType === 'template16' ? 'Search sweets & treats' : 'Search for Items'}
                        className="menu-app-search-input"
                      />
                    </div>
                  </div>

                  {branchesReady && totalBranchCount > 1 && (
                    <button
                      className="menu-app-change-store-btn"
                      onClick={handleLocationChangeClick}
                    >
                      <Store size={18} />
                      <span>{t('menu.changeStore')}</span>
                    </button>
                  )}

                  {/* Desktop "Sort by" — lives in the search row so it stays
                    visible regardless of whether the category strip below
                    is shown (that strip is hidden at desktop widths, since
                    the ApparelFilters sidebar already has Categories; this
                    used to live inside that strip and was going dark along
                    with it). Hidden below 901px via CSS — mobile keeps its
                    own separate Sort entry in the fixed Filter/Sort bar. */}
                  {showRetailFilters && (
                    <div className="menu-app-sort-desktop" ref={sortDropdownRef}>
                      <button
                        type="button"
                        ref={sortTriggerRef}
                        className="menu-app-sort-desktop-trigger"
                        onClick={() => setIsSortDropdownOpen(prev => !prev)}
                        aria-haspopup="listbox"
                        aria-expanded={isSortDropdownOpen}
                      >
                        <ArrowUpDown size={15} />
                        <span>Sort by</span>
                        <ChevronDown size={14} className={`menu-app-sort-desktop-chevron ${isSortDropdownOpen ? 'is-open' : ''}`} />
                      </button>
                      {isSortDropdownOpen && sortTriggerRef.current && createPortal(
                        <ul
                          ref={sortMenuRef}
                          className="menu-app-sort-desktop-menu"
                          role="listbox"
                          style={{
                            position: 'fixed',
                            top: sortTriggerRef.current.getBoundingClientRect().bottom + 6,
                            right: window.innerWidth - sortTriggerRef.current.getBoundingClientRect().right
                          }}
                        >
                          {SORT_OPTIONS.map(opt => (
                            <li
                              key={opt.value}
                              role="option"
                              aria-selected={sortBy === opt.value}
                              className={`menu-app-sort-desktop-option ${sortBy === opt.value ? 'is-selected' : ''}`}
                              onClick={() => { setSortBy(opt.value); setIsSortDropdownOpen(false); }}
                            >
                              {opt.label}
                            </li>
                          ))}
                        </ul>,
                        document.body
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick category strip — retail/apparel stores only. Writes to the
                same apparelFilters.categories state the ApparelFilters checkbox
                list already reads/writes, so it's just a second UI for the
                same existing filter, not a new filtering mechanism. */}
              {showRetailFilters && categories.length > 0 && (
                <div className="menu-app-categories-retail">
                  <div
                    className="menu-app-categories-retail-scroll"
                    ref={categoriesScrollRef}
                    style={{
                      WebkitMaskImage: `linear-gradient(to right, ${categoriesCanScrollLeft ? 'transparent 0, black 20px' : 'black 0'}, ${categoriesCanScrollRight ? 'black calc(100% - 20px), transparent 100%' : 'black 100%'})`,
                      maskImage: `linear-gradient(to right, ${categoriesCanScrollLeft ? 'transparent 0, black 20px' : 'black 0'}, ${categoriesCanScrollRight ? 'black calc(100% - 20px), transparent 100%' : 'black 100%'})`
                    }}
                  >
                    <button
                      type="button"
                      className={`menu-app-category-chip ${(!apparelFilters.categories || apparelFilters.categories.length === 0) ? 'active' : ''}`}
                      onClick={() => setApparelFilters(prev => ({ ...prev, categories: [] }))}
                    >
                      <span className="menu-app-category-chip-name">All</span>
                    </button>
                    {categories.map(category => {
                      const isActive = (apparelFilters.categories || []).includes(category.id);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          className={`menu-app-category-chip ${isActive ? 'active' : ''}`}
                          onClick={() => setApparelFilters(prev => ({
                            ...prev,
                            categories: isActive ? [] : [category.id]
                          }))}
                        >
                          {category.image && (
                            <span className="menu-app-category-chip-thumb">
                              <FallbackImg src={category.image} alt={category.name} />
                            </span>
                          )}
                          <span className="menu-app-category-chip-name">{category.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Combined Filters and Store Info Row */}
              {(() => {
                if (showRetailFilters) {
                  // Sort/count now render inline in the categories row above
                  // (desktop). Mobile still gets its own fixed Filter/Sort bar
                  // rendered further below, unaffected by this change.
                  return null;
                }

                return !!menuFilterSettings.veg_non_veg && (
                  <div className="menu-app-filters-and-store-row">

                    {/* Left side: Filters */}
                    <div className="menu-app-filters-left">
                      <span className="menu-app-filters-label">{t('menu.filterItems')}</span>

                      {/* VEG FILTER BUTTON */}
                      <button
                        onClick={handleVegFilterClick}
                        className={`menu-app-filter-tag ${vegFilter ? 'active-veg' : ''}`}
                      >
                        {vegFilter && <span>✓</span>}
                        <span className="filter-text-veg">🟢 {t('menu.veg')}</span>
                      </button>

                      {/* NON-VEG FILTER BUTTON */}
                      <button
                        onClick={handleNonVegFilterClick}
                        className={`menu-app-filter-tag ${nonVegFilter ? 'active-non-veg' : ''}`}
                      >
                        {nonVegFilter && <span>✓</span>}
                        <span className="filter-text-non-veg">🔴 {t('menu.nonVeg')}</span>
                      </button>
                    </div>

                    {/* Removed Order Type Badge from here, moved to Cart Headers */}
                    <div className="menu-app-store-info-compact">
                      {orderDetails?.isPreOrder && orderDetails?.scheduleInfo && (
                        <span className="menu-app-preorder-label">
                          Pre-Order: {orderDetails.scheduleInfo.date} at {orderDetails.scheduleInfo.time}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
              {!showRetailFilters && (
                <div className="menu-app-categories-mobile">
                  <div className="menu-app-categories-scroll">
                    {categories.length === 0 ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="menu-app-category-mobile-skeleton" />
                      ))
                    ) : (
                      categories.map(category => (
                        <button
                          key={category.id}
                          onClick={async () => { // ✅ Add async
                            console.log('📱 Mobile category clicked:', category.id, category.name);
                            setSelectedCategory(category.id);
                            updateCategoryInUrl(category);
                            await handleCategoryChange(category.id); // ✅ Add await
                          }}
                          className={`menu-app-category-mobile ${selectedCategory === category.id ? 'active' : ''}`}
                        >
                          {category.image && (
                            <span className="menu-app-category-mobile-thumb">
                              <FallbackImg src={category.image} alt={category.name} />
                            </span>
                          )}
                          <span className="menu-app-category-mobile-name">{category.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className={`menu-app-products ${showRetailFilters ? 'has-mobile-toolbar' : ''} ${cart.length > 0 ? 'has-cart-fab' : ''}`}>
                {categoryLoading ? (
                  <div className="menu-app-products-grid">
                    {Array.from({ length: 20 }).map((_, i) => (
                      <div key={i} className="menu-app-skeleton-card">
                        <div className="skeleton-image" />
                        <div className="skeleton-body">
                          <div className="skeleton-line skeleton-title" />
                          <div className="skeleton-footer">
                            <div className="skeleton-line skeleton-price" />
                            <div className="skeleton-btn" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="menu-app-empty-state">
                    {selectedCategory === 'combo-offers' ? (
                      <>
                        <div className="menu-app-empty-icon-wrapper">
                          <Tag size={52} strokeWidth={1.5} />
                        </div>
                        <h3 className="menu-app-empty-title">No Combo Deals Today</h3>
                        <p className="menu-app-empty-text">
                          We don't have any combo offers at this store right now.<br />
                          Please check back later for tasty deals!
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="menu-app-empty-icon-wrapper">
                          <emptyStateContent.icon size={56} strokeWidth={1.5} />
                        </div>
                        <h3 className="menu-app-empty-title">{emptyStateContent.title}</h3>
                        <p className="menu-app-empty-text">
                          {emptyStateContent.description}
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="menu-app-products-grid">
                    {sortedProducts.map(product => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onAddToCart={addToCart}
                        onShowDetails={handleShowProductDetails}
                        isUserLoggedIn={isUserLoggedIn}
                        onShowAuthModal={handleShowAuthModalFromHeader}
                        cart={cart}
                        onUpdateQuantity={updateCartQuantity}
                        onRemove={removeFromCart}
                        generateCartItemKey={generateCartItemKey}
                        outletType={outletType}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="menu-app-main-scrollbar-track" ref={mainTrackRef} aria-hidden="true">
              <div
                className="menu-app-main-scrollbar-thumb"
                ref={mainThumbRef}
                onPointerDown={handleMainThumbPointerDown}
              />
            </div>
          </div>

          <div className="menu-app-cart-sidebar">
            <div className="menu-app-cart-sidebar-content">
              {/* HEADER - Sticky at Top */}
              <div className="menu-app-cart-sidebar-header">
                <div className="menu-app-cart-header-main-row">
                  <h2 className="menu-app-cart-sidebar-title">{t('cart.title')}</h2>
                  <div className="menu-app-cart-sidebar-actions">
                    {cart.length > 0 && (
                      <button
                        className="menu-app-clear-btn-minimal"
                        onClick={() => setShowClearCartDialog(true)}
                        title="Clear Cart"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="menu-app-cart-header-sub-row">
                  <div className="menu-app-cart-header-badges">
                    <div className="menu-app-order-type-chip-desktop">
                      <Truck size={13} />
                      <span>{orderDetails?.type === 'collection' ? 'Collection' : 'Delivery'}</span>
                    </div>
                    <span className="menu-app-cart-sidebar-count-badge">
                      {cart.length} {cart.length === 1 ? t('cart.item') : t('cart.items')}
                    </span>
                  </div>

                  {cart.length > 0 && hasOffers && (
                    <button className="menu-app-offers-btn-compact" onClick={() => setShowOffersModal(true)}>
                      <Tag size={13} />
                      <span>{t('cart.offers')}</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="menu-app-cart-sidebar-body">
                <CartContent
                  key={`${selectedStore?.id}-${orderDetails?.type}-${orderDetails?.isPreOrder}`} // ✅ Force re-render
                  cart={cart}
                  categories={categories}
                  cartAnalysis={cartAnalysis}
                  onUpdateQuantity={updateCartQuantity}
                  onRemove={removeFromCart}
                  onPlaceOrder={handlePlaceOrder}
                  loading={loading}
                  orderType={orderDetails?.type}
                  getTotalAmount={getTotalAmount}
                  getTotalSavings={getTotalSavings}
                  getFinalTotal={getFinalTotal}
                  getFreeItemsValue={getFreeItemsValue}
                  selectedTime={selectedCollectionTime}
                  onTimeChange={setSelectedCollectionTime}
                  onScheduleModeChange={setScheduleMode}
                  loyaltyPoints={loyaltyPoints}
                  redeemPoints={redeemPoints}
                  onRedeemPointsChange={setRedeemPoints}
                  selectedAddress={selectedAddress}
                  onAddressChange={setSelectedAddress}
                  addresses={propAddresses}
                  addressesLoading={addressesLoading}
                  onAddressSelect={onAddressSelect}
                  onCreateAddress={onCreateAddress}
                  onUpdateAddress={onUpdateAddress}
                  onDeleteAddress={onDeleteAddress}
                  onSetDefaultAddress={onSetDefaultAddress}
                  offersManager={offersManager}
                  selectedStore={selectedStore}
                  isPreOrder={orderDetails?.isPreOrder || false}
                  scheduleInfo={orderDetails?.scheduleInfo || null}
                  showScheduleUI={true}
                  storeIsOpen={storeIsOpen}
                  onEditCustomization={handleEditCustomization} // ✅ NEW
                  userId={userId}
                  cookingInstruction={cookingInstruction}
                  setCookingInstruction={setCookingInstruction}
                  shippingInfo={cartAnalysis?.shipping || null} onDeliverySpeedChange={setDeliverySpeed}
                  isAnalyzing={analyzingCart}
                  outletType={outletType}
                />
              </div>
            </div>
          </div>
        </div>

        {showRetailFilters && (
          <div className="menu-app-mobile-toolbar">
            <button
              type="button"
              className={`menu-app-mobile-toolbar-btn ${(apparelFilters.categories?.length || apparelFilters.priceRange || apparelFilters.gender?.length || apparelFilters.ageGroups?.length || apparelFilters.sizes?.length || apparelFilters.colors?.length || apparelFilters.brands?.length || apparelFilters.minRating) ? 'has-active-badge' : ''}`}
              onClick={() => setShowFilterDrawer(true)}
            >
              <Tag size={18} />
              <span>Filter</span>
            </button>
            <div className="menu-app-mobile-toolbar-divider" />
            <button
              type="button"
              className={`menu-app-mobile-toolbar-btn ${sortBy !== 'relevance' ? 'has-active-badge' : ''}`}
              onClick={() => setShowSortSheet(true)}
            >
              <ArrowUpDown size={18} />
              <span>Sort</span>
            </button>
          </div>
        )}

        {cart.length > 0 && (
          <div className={`menu-app-cart-fab-container ${showRetailFilters ? 'has-mobile-toolbar' : ''}`}>
            <button onClick={() => { if (guardStoreOpenAction()) setShowCart(true); }} className="menu-app-cart-fab-centered">
              <div className="cart-fab-centered-left">
                <ShoppingCart size={22} strokeWidth={2.5} />
              </div>
              <div className="cart-fab-centered-middle">
                <span className="cart-fab-centered-title">View cart</span>
                <span className="cart-fab-centered-subtitle">
                  {cart.reduce((total, item) => total + item.quantity, 0)} {cart.reduce((total, item) => total + item.quantity, 0) === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div className="cart-fab-centered-right">
                <div className="cart-fab-circle-arrow">
                  <ChevronRight size={18} strokeWidth={3} />
                </div>
              </div>
            </button>
          </div>
        )}

        {showCart && (
          <div className="menu-app-cart-overlay" onClick={() => setShowCart(false)}>
            <div className="menu-app-cart-modal" onClick={(e) => e.stopPropagation()}>
              <div className="menu-app-cart-header">
                <div className="menu-app-cart-header-left">
                  <button onClick={() => setShowCart(false)} className="menu-app-cart-back">
                    <X size={24} />
                  </button>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h2 className="menu-app-cart-title">{t('cart.title')}</h2>
                      <div className="menu-app-order-type-chip" style={{ padding: '2px 8px', margin: 0, border: '1px solid rgba(249, 115, 22, 0.2)', borderRadius: '12px' }}>
                        <Truck size={12} />
                        <span style={{ fontSize: '11px' }}>{orderDetails?.type === 'collection' ? 'Collection' : 'Delivery'}</span>
                      </div>
                    </div>
                    <span className="menu-app-cart-count-badge">
                      {cart.length} {cart.length === 1 ? t('cart.item') : t('cart.items')}
                    </span>
                  </div>
                </div>
                <div className="menu-app-cart-header-right">
                  {hasOffers && (
                    <button className="menu-app-offers-btn" onClick={() => setShowOffersModal(true)}>
                      <Tag size={18} />
                      <span>Offers</span>
                    </button>
                  )}
                  {cart.length > 0 && (
                    <button className="menu-app-clear-btn" onClick={() => setShowClearCartDialog(true)}>
                      <Trash2 size={14} />
                      <span>Clear</span>
                    </button>
                  )}
                </div>
              </div>
              <CartContent
                key={`${selectedStore?.id}-${orderDetails?.type}-${orderDetails?.isPreOrder}`} // ✅ Force re-render
                cart={cart}
                categories={categories}
                cartAnalysis={cartAnalysis}
                onUpdateQuantity={updateCartQuantity}
                onRemove={removeFromCart}
                onPlaceOrder={handlePlaceOrder}
                loading={loading}
                getTotalAmount={getTotalAmount}
                getTotalSavings={getTotalSavings}
                getFinalTotal={getFinalTotal}
                getFreeItemsValue={getFreeItemsValue}
                orderType={orderDetails?.type}
                selectedTime={selectedCollectionTime}
                onTimeChange={setSelectedCollectionTime}
                onScheduleModeChange={setScheduleMode}
                loyaltyPoints={loyaltyPoints}
                redeemPoints={redeemPoints}
                onRedeemPointsChange={setRedeemPoints}
                selectedAddress={selectedAddress}
                onAddressChange={setSelectedAddress}
                addresses={propAddresses}
                addressesLoading={addressesLoading}
                onAddressSelect={onAddressSelect}
                onCreateAddress={onCreateAddress}
                onUpdateAddress={onUpdateAddress}
                onDeleteAddress={onDeleteAddress}
                onSetDefaultAddress={onSetDefaultAddress}
                offersManager={offersManager}
                selectedStore={selectedStore}
                isPreOrder={orderDetails?.isPreOrder || false}
                scheduleInfo={orderDetails?.scheduleInfo || null}
                showScheduleUI={true}
                storeIsOpen={storeIsOpen}
                onEditCustomization={handleEditCustomization} // ✅ NEW
                userId={userId}
                cookingInstruction={cookingInstruction}
                setCookingInstruction={setCookingInstruction}
                shippingInfo={cartAnalysis?.shipping || null} onDeliverySpeedChange={setDeliverySpeed}
                isAnalyzing={analyzingCart}
                outletType={outletType}
              />
            </div>
          </div>
        )}

        <ProductDetailsModal
          isOpen={showProductDetails}
          onClose={handleCloseProductDetails}
          product={selectedProduct}
          onAddToCart={addToCart}
          onOpenCustomization={handleOpenCustomizationFromDetails}
          isUserLoggedIn={isUserLoggedIn}
          cart={cart}
          onUpdateQuantity={updateCartQuantity}
          onRemove={removeFromCart}
          generateCartItemKey={generateCartItemKey}
        />

        <CustomizationModal
          isOpen={showCustomizeModal}
          onClose={() => {
            setShowCustomizeModal(false);
            setProductToCustomize(null);
            setIsEditingCartItem(false);
            setEditingCartItem(null);
          }}
          product={productToCustomize}
          onAddToCart={handleCustomizedProductAdd}
          isEditing={isEditingCartItem}
          existingCartItem={editingCartItem}
          isUserLoggedIn={isUserLoggedIn}
        />

        <ComboDetailsModal
          isOpen={showComboModal}
          onClose={() => {
            setShowComboModal(false);
            setComboToDisplay(null);
          }}
          combo={comboToDisplay}
          onAddToCart={(combo) => {
            // Re-call addToCart with a flag to bypass the modal check
            addToCart({ ...combo, isCustomized: true });
          }}
          isUserLoggedIn={isUserLoggedIn}
          onShowAuthModal={handleShowAuthModalFromHeader}
        />
        <OffersModal
          show={showOffersModal}
          onClose={() => setShowOffersModal(false)}
          offersManager={offersManager}
          cart={cart}
          getBranchId={getBranchId}
          getCustomerId={getCustomerId}
          couponCode={couponCode}
          onCouponChange={setCouponCode}
          onApplyCoupon={handleApplyCoupon}
          activeOffers={activeOffers}
          onSelectOffer={(code) => {
            setCouponCode(code);
            setTimeout(() => handleApplyCoupon(), 100);
          }}
        />
        {/* ✅ Order Processing Overlay (for COD) */}
        {processingPayment && (
          <div className="menu-app-processing-overlay">
            <div className="menu-app-processing-card">
              <div className="menu-app-processing-animation">
                <div className="menu-app-spinner large"></div>
                <div className="processing-pulse"></div>
              </div>
              <h2 className="menu-app-processing-title">Placing Your Order</h2>
            </div>
          </div>
        )}

        <OrderConfirmationModal
          isOpen={showOrderConfirmationModal}
          onClose={() => setShowOrderConfirmationModal(false)}
          onConfirm={handleConfirmOrder}
          restaurantName={selectedStore?.name}
          loading={processingPayment}
          orderType={orderDetails?.type}
        />

        <PaymentModal
          show={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          total={getFinalTotal}
          onPaymentSelect={handlePaymentMethod}
          processing={processingPayment}
        />

        {/* ✅ NEW: Store Closed Modal Handler */}
        <StoreClosedModal
          isOpen={showStoreClosedModal}
          onClose={() => {
            console.log('🏪 User clicked "Choose Another Store" from closure modal');
            setShowStoreClosedModal(false);
            // ✅ CRITICAL: Call parent's onClose to go back to store selector
            if (onClose) {
              setTimeout(onClose, 300); // Small delay for smooth transition
            }
          }}
          store={selectedStore}
          storeStatus={storeClosureInfo}
          onPreOrder={handlePreOrderFromModal}
          supportsPreOrder={selectedStore?.allow_pre_order === true || selectedStore?.pre_order_enabled === true}
          hasOtherStores={!branchesReady || totalBranchCount !== 1}
        />

        {/* ✅ Clear Cart Confirmation Dialog */}
        {showClearCartDialog && (
          <div className="clear-cart-dialog-overlay" onClick={() => setShowClearCartDialog(false)}>
            <div className="clear-cart-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="clear-cart-dialog-icon">
                <div className="clear-cart-warning-triangle">
                  <AlertTriangle size={36} strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="clear-cart-dialog-title">Clear Cart</h3>
              <p className="clear-cart-dialog-text">Are you sure you want to clear all items from your cart?</p>
              <div className="clear-cart-dialog-actions">
                <button
                  className="clear-cart-dialog-btn cancel"
                  onClick={() => setShowClearCartDialog(false)}
                >
                  Cancel
                </button>
                <button
                  className="clear-cart-dialog-btn confirm"
                  onClick={() => {
                    clearCart();
                    setShowClearCartDialog(false);
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Weight Selection Modal */}
        <WeightSelectionModal
          isOpen={showWeightModal}
          onClose={() => {
            setShowWeightModal(false);
            setWeightProduct(null);
          }}
          product={weightProduct}
          onConfirm={handleWeightConfirm}
        />

        {/* ✅ Email Prompt Modal */}
        {showEmailModal && (
          <div className="email-prompt-overlay" onClick={() => setShowEmailModal(false)}>
            <div className="email-prompt-modal" onClick={(e) => e.stopPropagation()}>
              <button className="email-prompt-close" onClick={() => setShowEmailModal(false)}>
                <X size={20} />
              </button>
              <div className="email-prompt-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #B91C1C)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M22 4L12 13L2 4" />
                </svg>
              </div>
              <h3 className="email-prompt-title">Email Required</h3>
              <p className="email-prompt-subtitle">Please provide your email address to receive order updates and invoices.</p>
              <div className="email-prompt-form">
                <input
                  type="email"
                  className={`email-prompt-input ${emailError ? 'email-prompt-input-error' : ''}`}
                  placeholder="Enter your email address"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !emailSaving) handleEmailSubmit();
                  }}
                  disabled={emailSaving}
                  autoFocus={window.innerWidth > 768}
                />
                {emailError && (
                  <p className="email-prompt-error">
                    <AlertCircle size={14} />
                    <span>{emailError}</span>
                  </p>
                )}
              </div>
              <div className="email-prompt-actions">
                <button
                  className="email-prompt-btn-cancel"
                  onClick={() => setShowEmailModal(false)}
                  disabled={emailSaving}
                >
                  Cancel
                </button>
                <button
                  className="email-prompt-btn-submit"
                  onClick={handleEmailSubmit}
                  disabled={emailSaving}
                >
                  {emailSaving ? 'Saving...' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          // AuthModal calls onClose() right after onAuthSuccess() on every
          // successful login too, not just on cancel — skip clearing when
          // that's why we're here, or the pending action would be wiped a
          // moment after handleAuthSuccess saved it, before the post-login
          // remount below gets a chance to resume it.
          if (!authSucceededRef.current) {
            setPendingProduct(null);
            clearPendingCartAction();
          }
          authSucceededRef.current = false;
        }}
        onAuthSuccess={handleAuthSuccess}
        branchId={selectedStore?.id}
        authMethodProp={authMethod}
      />

      {/* Menu App Toast Portal */}
      {
        menuToast && createPortal(
          <div
            style={{
              position: 'fixed',
              top: '80px',
              right: '24px',
              zIndex: 99999,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 24px',
              borderRadius: '16px',
              fontSize: '15px',
              fontWeight: '600',
              boxShadow: menuToast.type === 'success'
                ? '0 10px 40px -10px rgba(76, 175, 80, 0.4)'
                : '0 10px 40px -10px rgba(220, 53, 69, 0.4)',
              animation: 'userProfileToastSlide 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
              minWidth: '260px',
              maxWidth: '380px',
              background: menuToast.type === 'success'
                ? 'linear-gradient(135deg, rgba(46, 125, 50, 0.95), rgba(76, 175, 80, 0.9))'
                : 'linear-gradient(135deg, rgba(198, 40, 40, 0.95), rgba(220, 53, 69, 0.9))',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(12px)',
              transformOrigin: 'top right'
            }}
          >
            <span style={{
              fontFamily: 'var(--font-primary, "Inter", Arial, sans-serif)',
              letterSpacing: '0.3px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              {menuToast.message}
            </span>
          </div>,
          document.body
        )
      }

      <ApparelFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        filters={apparelFilters}
        setFilters={setApparelFilters}
        menuFilterSettings={menuFilterSettings}
        showGenderFilter={showGenderFilter}
        availableBrands={availableBrands}
        availableColors={availableColors}
        availableAgeGroups={availableAgeGroups}
        availableSizes={[]}
        availableCategories={categories}
        totalProducts={filteredProducts.length}
        isLoading={categoryLoading}
      />

      <SortDrawer
        isOpen={showSortSheet}
        onClose={() => setShowSortSheet(false)}
        sortBy={sortBy}
        onChangeSortBy={setSortBy}
      />
    </>
  );
};

export default RestaurantMenuApp;